import 'buffer-shim';
import once from 'lodash/once';
import require$$0$1 from 'buffer-xor';
import require$$0$2 from 'safe-buffer';
import require$$1$2, { Transform } from 'readable-stream';
import require$$2$1 from 'string_decoder';
import require$$3 from 'inherits';
import { md5 as md5$1, sha1 } from '@noble/hashes/legacy.js';
import require$$0$3 from 'asn1.js';
import require$$0$4 from 'buffer';
import require$$2$2 from 'sha.js';
import { randomBytes } from '@noble/post-quantum/utils';
export { randomBytes } from '@noble/post-quantum/utils';
import require$$0$5 from 'bn.js';
import pino from 'pino/browser';
import path from 'path-browserify';
import without from 'lodash/without';
import padEnd from 'lodash/padEnd';
import { EventEmitter } from 'eventemitter3';
import set from 'lodash/set';
import isArray from 'lodash/isArray';
import mergeWith from 'lodash/mergeWith';
import secp256k1 from 'secp256k1';
import { Keccak } from 'sha3';
import { ml_dsa87 } from '@noble/post-quantum/ml-dsa';
import { p256 } from '@noble/curves/p256';

class Cache {
    value;
    valueTimestamp;
    valueFactory;
    maxAgeInMilliseconds;
    constructor(valueFactory, maxAgeInMilliseconds) {
        this.valueFactory = valueFactory;
        this.maxAgeInMilliseconds = maxAgeInMilliseconds;
    }
    async get() {
        const now = Date.now();
        if ((this.valueTimestamp === undefined) || (now > (this.valueTimestamp + this.maxAgeInMilliseconds))) {
            this.value = await this.valueFactory();
            this.valueTimestamp = now;
        }
        return this.value;
    }
    invalidate() {
        this.value = undefined;
        this.valueTimestamp = undefined;
    }
}

/**
 * Given an iterable of promises, settles them one at a time.
 * If all of them resolve, returns the list of values.
 * If one of them rejects, provides a callback for reverting the
 * preceding (already resolved) values.
 */
async function pTransaction(promises, rollback) {
    const results = [];
    for (const promise of promises) {
        try {
            results.push(await promise);
        }
        catch (err) {
            // eslint-disable-next-line @typescript-eslint/await-thenable
            await Promise.allSettled(results.map((r) => rollback(r)));
            throw err;
        }
    }
    return results;
}

class AbortError extends Error {
    code = 'AbortError';
    constructor(customErrorContext) {
        super(customErrorContext === undefined
            ? `aborted`
            : `${customErrorContext} aborted`);
    }
}
/**
 * Wraps a Promise into one that can be aborted with `AbortSignal`.
 * Aborting causes the returned Promise to reject with `AbortError` unless
 * the underlying promise itself has already resolved or rejected.
 *
 * Notice that it is the user's responsibility to implement any custom cleanup
 * logic in a `finally` or `catch` block in case of resources that need to be
 * freed up.
 */
function asAbortable(promise, abortSignal, customErrorContext) {
    if (abortSignal === undefined) {
        return promise;
    }
    if (abortSignal.aborted) {
        return Promise.reject(new AbortError(customErrorContext));
    }
    let abortListener;
    return new Promise((resolve, reject) => {
        abortListener = () => {
            reject(new AbortError(customErrorContext));
        };
        abortSignal.addEventListener('abort', abortListener);
        promise.then(resolve, reject);
    }).finally(() => {
        abortSignal.removeEventListener('abort', abortListener);
    });
}

/**
 * setTimeout with AbortSignal support. Aborting will simply clear
 * the timeout silently.
 */
const setAbortableTimeout = (cb, ms, abortSignal) => {
    if (abortSignal.aborted) {
        return;
    }
    const abortListener = () => {
        clearTimeout(timeoutRef);
    };
    abortSignal.addEventListener('abort', abortListener, { once: true });
    const timeoutRef = setTimeout(() => {
        abortSignal.removeEventListener('abort', abortListener);
        cb();
    }, ms);
};
/**
 * setInterval with AbortSignal support. Aborting will simply clear
 * the interval silently.
 */
const setAbortableInterval = (cb, ms, abortSignal) => {
    if (abortSignal.aborted) {
        return;
    }
    const abortListener = () => {
        clearInterval(timeoutRef);
    };
    abortSignal.addEventListener('abort', abortListener, { once: true });
    const timeoutRef = setInterval(() => {
        cb();
    }, ms);
};

const noopExecutor = () => { };
/**
 * Deferred promise allowing external control of resolve / reject.
 */
class Defer extends Promise {
    resolveFn;
    rejectFn;
    ensureNoopCatchAttached;
    settled = false;
    constructor(executor = noopExecutor) {
        let localResolve;
        let localReject;
        super((resolve, reject) => {
            localResolve = resolve;
            localReject = reject;
            executor(resolve, reject);
        });
        if (localResolve === undefined) {
            throw new Error('invariant violation: resolveFn was undefined');
        }
        if (localReject === undefined) {
            throw new Error('invariant violation: rejectFn was undefined');
        }
        this.resolveFn = localResolve;
        this.rejectFn = localReject;
        this.ensureNoopCatchAttached = once(() => {
            super.catch(() => { });
        });
    }
    resolve(value) {
        this.ensureNoopCatchAttached();
        if (!this.settled) {
            this.settled = true;
            this.resolveFn(value);
        }
    }
    reject(error) {
        this.ensureNoopCatchAttached();
        if (!this.settled) {
            this.settled = true;
            this.rejectFn(error);
        }
    }
    wrap(fn) {
        this.ensureNoopCatchAttached();
        return async (...args) => {
            try {
                const value = await fn(...args);
                this.resolve(value);
                return value;
            }
            catch (err) {
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                this.reject(err);
                throw err;
            }
        };
    }
}

function isENSNameFormatIgnoreCase(str) {
    return str.indexOf('.') > 0;
}
function toENSName(str) {
    if (isENSNameFormatIgnoreCase(str)) {
        return str.toLowerCase();
    }
    throw new Error(`not a valid ENS name: "${str}"`);
}

const REGEX$1 = /^0x[a-fA-F0-9]{40}$/;
const PREFIXED_STRING_LENGTH = 42;
function toEthereumAddress(str) {
    if (REGEX$1.test(str)) {
        return str.toLowerCase();
    }
    throw new Error(`not a valid Ethereum address: "${str}"`);
}

function isENSName(domain) {
    return isENSNameFormatIgnoreCase(domain);
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function getAugmentedNamespace(n) {
  if (Object.prototype.hasOwnProperty.call(n, '__esModule')) return n;
  var f = n.default;
	if (typeof f == "function") {
		var a = function a () {
			var isInstance = false;
      try {
        isInstance = this instanceof a;
      } catch {}
			if (isInstance) {
        return Reflect.construct(f, arguments, this.constructor);
			}
			return f.apply(this, arguments);
		};
		a.prototype = f.prototype;
  } else a = {};
  Object.defineProperty(a, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

var browser$3 = {};

var encrypter = {};

var ecb = {};

var hasRequiredEcb;

function requireEcb () {
	if (hasRequiredEcb) return ecb;
	hasRequiredEcb = 1;
	ecb.encrypt = function (self, block) {
	  return self._cipher.encryptBlock(block)
	};

	ecb.decrypt = function (self, block) {
	  return self._cipher.decryptBlock(block)
	};
	return ecb;
}

var cbc = {};

var hasRequiredCbc;

function requireCbc () {
	if (hasRequiredCbc) return cbc;
	hasRequiredCbc = 1;
	var xor = require$$0$1;

	cbc.encrypt = function (self, block) {
	  var data = xor(block, self._prev);

	  self._prev = self._cipher.encryptBlock(data);
	  return self._prev
	};

	cbc.decrypt = function (self, block) {
	  var pad = self._prev;

	  self._prev = block;
	  var out = self._cipher.decryptBlock(block);

	  return xor(out, pad)
	};
	return cbc;
}

var cfb = {};

var hasRequiredCfb;

function requireCfb () {
	if (hasRequiredCfb) return cfb;
	hasRequiredCfb = 1;
	var Buffer = require$$0$2.Buffer;
	var xor = require$$0$1;

	function encryptStart (self, data, decrypt) {
	  var len = data.length;
	  var out = xor(data, self._cache);
	  self._cache = self._cache.slice(len);
	  self._prev = Buffer.concat([self._prev, decrypt ? data : out]);
	  return out
	}

	cfb.encrypt = function (self, data, decrypt) {
	  var out = Buffer.allocUnsafe(0);
	  var len;

	  while (data.length) {
	    if (self._cache.length === 0) {
	      self._cache = self._cipher.encryptBlock(self._prev);
	      self._prev = Buffer.allocUnsafe(0);
	    }

	    if (self._cache.length <= data.length) {
	      len = self._cache.length;
	      out = Buffer.concat([out, encryptStart(self, data.slice(0, len), decrypt)]);
	      data = data.slice(len);
	    } else {
	      out = Buffer.concat([out, encryptStart(self, data, decrypt)]);
	      break
	    }
	  }

	  return out
	};
	return cfb;
}

var cfb8 = {};

var hasRequiredCfb8;

function requireCfb8 () {
	if (hasRequiredCfb8) return cfb8;
	hasRequiredCfb8 = 1;
	var Buffer = require$$0$2.Buffer;

	function encryptByte (self, byteParam, decrypt) {
	  var pad = self._cipher.encryptBlock(self._prev);
	  var out = pad[0] ^ byteParam;

	  self._prev = Buffer.concat([
	    self._prev.slice(1),
	    Buffer.from([decrypt ? byteParam : out])
	  ]);

	  return out
	}

	cfb8.encrypt = function (self, chunk, decrypt) {
	  var len = chunk.length;
	  var out = Buffer.allocUnsafe(len);
	  var i = -1;

	  while (++i < len) {
	    out[i] = encryptByte(self, chunk[i], decrypt);
	  }

	  return out
	};
	return cfb8;
}

var cfb1 = {};

var hasRequiredCfb1;

function requireCfb1 () {
	if (hasRequiredCfb1) return cfb1;
	hasRequiredCfb1 = 1;
	var Buffer = require$$0$2.Buffer;

	function encryptByte (self, byteParam, decrypt) {
	  var pad;
	  var i = -1;
	  var len = 8;
	  var out = 0;
	  var bit, value;
	  while (++i < len) {
	    pad = self._cipher.encryptBlock(self._prev);
	    bit = (byteParam & (1 << (7 - i))) ? 0x80 : 0;
	    value = pad[0] ^ bit;
	    out += ((value & 0x80) >> (i % 8));
	    self._prev = shiftIn(self._prev, decrypt ? bit : value);
	  }
	  return out
	}

	function shiftIn (buffer, value) {
	  var len = buffer.length;
	  var i = -1;
	  var out = Buffer.allocUnsafe(buffer.length);
	  buffer = Buffer.concat([buffer, Buffer.from([value])]);

	  while (++i < len) {
	    out[i] = buffer[i] << 1 | buffer[i + 1] >> (7);
	  }

	  return out
	}

	cfb1.encrypt = function (self, chunk, decrypt) {
	  var len = chunk.length;
	  var out = Buffer.allocUnsafe(len);
	  var i = -1;

	  while (++i < len) {
	    out[i] = encryptByte(self, chunk[i], decrypt);
	  }

	  return out
	};
	return cfb1;
}

var ofb = {};

var hasRequiredOfb;

function requireOfb () {
	if (hasRequiredOfb) return ofb;
	hasRequiredOfb = 1;
	var xor = require$$0$1;

	function getBlock (self) {
	  self._prev = self._cipher.encryptBlock(self._prev);
	  return self._prev
	}

	ofb.encrypt = function (self, chunk) {
	  while (self._cache.length < chunk.length) {
	    self._cache = Buffer.concat([self._cache, getBlock(self)]);
	  }

	  var pad = self._cache.slice(0, chunk.length);
	  self._cache = self._cache.slice(chunk.length);
	  return xor(chunk, pad)
	};
	return ofb;
}

var ctr = {};

var incr32_1;
var hasRequiredIncr32;

function requireIncr32 () {
	if (hasRequiredIncr32) return incr32_1;
	hasRequiredIncr32 = 1;
	function incr32 (iv) {
	  var len = iv.length;
	  var item;
	  while (len--) {
	    item = iv.readUInt8(len);
	    if (item === 255) {
	      iv.writeUInt8(0, len);
	    } else {
	      item++;
	      iv.writeUInt8(item, len);
	      break
	    }
	  }
	}
	incr32_1 = incr32;
	return incr32_1;
}

var hasRequiredCtr;

function requireCtr () {
	if (hasRequiredCtr) return ctr;
	hasRequiredCtr = 1;
	var xor = require$$0$1;
	var Buffer = require$$0$2.Buffer;
	var incr32 = requireIncr32();

	function getBlock (self) {
	  var out = self._cipher.encryptBlockRaw(self._prev);
	  incr32(self._prev);
	  return out
	}

	var blockSize = 16;
	ctr.encrypt = function (self, chunk) {
	  var chunkNum = Math.ceil(chunk.length / blockSize);
	  var start = self._cache.length;
	  self._cache = Buffer.concat([
	    self._cache,
	    Buffer.allocUnsafe(chunkNum * blockSize)
	  ]);
	  for (var i = 0; i < chunkNum; i++) {
	    var out = getBlock(self);
	    var offset = start + i * blockSize;
	    self._cache.writeUInt32BE(out[0], offset + 0);
	    self._cache.writeUInt32BE(out[1], offset + 4);
	    self._cache.writeUInt32BE(out[2], offset + 8);
	    self._cache.writeUInt32BE(out[3], offset + 12);
	  }
	  var pad = self._cache.slice(0, chunk.length);
	  self._cache = self._cache.slice(chunk.length);
	  return xor(chunk, pad)
	};
	return ctr;
}

var aes128 = {
	cipher: "AES",
	key: 128,
	iv: 16,
	mode: "CBC",
	type: "block"
};
var aes192 = {
	cipher: "AES",
	key: 192,
	iv: 16,
	mode: "CBC",
	type: "block"
};
var aes256 = {
	cipher: "AES",
	key: 256,
	iv: 16,
	mode: "CBC",
	type: "block"
};
var require$$2 = {
	"aes-128-ecb": {
	cipher: "AES",
	key: 128,
	iv: 0,
	mode: "ECB",
	type: "block"
},
	"aes-192-ecb": {
	cipher: "AES",
	key: 192,
	iv: 0,
	mode: "ECB",
	type: "block"
},
	"aes-256-ecb": {
	cipher: "AES",
	key: 256,
	iv: 0,
	mode: "ECB",
	type: "block"
},
	"aes-128-cbc": {
	cipher: "AES",
	key: 128,
	iv: 16,
	mode: "CBC",
	type: "block"
},
	"aes-192-cbc": {
	cipher: "AES",
	key: 192,
	iv: 16,
	mode: "CBC",
	type: "block"
},
	"aes-256-cbc": {
	cipher: "AES",
	key: 256,
	iv: 16,
	mode: "CBC",
	type: "block"
},
	aes128: aes128,
	aes192: aes192,
	aes256: aes256,
	"aes-128-cfb": {
	cipher: "AES",
	key: 128,
	iv: 16,
	mode: "CFB",
	type: "stream"
},
	"aes-192-cfb": {
	cipher: "AES",
	key: 192,
	iv: 16,
	mode: "CFB",
	type: "stream"
},
	"aes-256-cfb": {
	cipher: "AES",
	key: 256,
	iv: 16,
	mode: "CFB",
	type: "stream"
},
	"aes-128-cfb8": {
	cipher: "AES",
	key: 128,
	iv: 16,
	mode: "CFB8",
	type: "stream"
},
	"aes-192-cfb8": {
	cipher: "AES",
	key: 192,
	iv: 16,
	mode: "CFB8",
	type: "stream"
},
	"aes-256-cfb8": {
	cipher: "AES",
	key: 256,
	iv: 16,
	mode: "CFB8",
	type: "stream"
},
	"aes-128-cfb1": {
	cipher: "AES",
	key: 128,
	iv: 16,
	mode: "CFB1",
	type: "stream"
},
	"aes-192-cfb1": {
	cipher: "AES",
	key: 192,
	iv: 16,
	mode: "CFB1",
	type: "stream"
},
	"aes-256-cfb1": {
	cipher: "AES",
	key: 256,
	iv: 16,
	mode: "CFB1",
	type: "stream"
},
	"aes-128-ofb": {
	cipher: "AES",
	key: 128,
	iv: 16,
	mode: "OFB",
	type: "stream"
},
	"aes-192-ofb": {
	cipher: "AES",
	key: 192,
	iv: 16,
	mode: "OFB",
	type: "stream"
},
	"aes-256-ofb": {
	cipher: "AES",
	key: 256,
	iv: 16,
	mode: "OFB",
	type: "stream"
},
	"aes-128-ctr": {
	cipher: "AES",
	key: 128,
	iv: 16,
	mode: "CTR",
	type: "stream"
},
	"aes-192-ctr": {
	cipher: "AES",
	key: 192,
	iv: 16,
	mode: "CTR",
	type: "stream"
},
	"aes-256-ctr": {
	cipher: "AES",
	key: 256,
	iv: 16,
	mode: "CTR",
	type: "stream"
},
	"aes-128-gcm": {
	cipher: "AES",
	key: 128,
	iv: 12,
	mode: "GCM",
	type: "auth"
},
	"aes-192-gcm": {
	cipher: "AES",
	key: 192,
	iv: 12,
	mode: "GCM",
	type: "auth"
},
	"aes-256-gcm": {
	cipher: "AES",
	key: 256,
	iv: 12,
	mode: "GCM",
	type: "auth"
}
};

var modes_1;
var hasRequiredModes;

function requireModes () {
	if (hasRequiredModes) return modes_1;
	hasRequiredModes = 1;
	var modeModules = {
	  ECB: requireEcb(),
	  CBC: requireCbc(),
	  CFB: requireCfb(),
	  CFB8: requireCfb8(),
	  CFB1: requireCfb1(),
	  OFB: requireOfb(),
	  CTR: requireCtr(),
	  GCM: requireCtr()
	};

	var modes = require$$2;

	for (var key in modes) {
	  modes[key].module = modeModules[modes[key].mode];
	}

	modes_1 = modes;
	return modes_1;
}

var aes = {};

var hasRequiredAes;

function requireAes () {
	if (hasRequiredAes) return aes;
	hasRequiredAes = 1;
	// based on the aes implimentation in triple sec
	// https://github.com/keybase/triplesec
	// which is in turn based on the one from crypto-js
	// https://code.google.com/p/crypto-js/

	var Buffer = require$$0$2.Buffer;

	function asUInt32Array (buf) {
	  if (!Buffer.isBuffer(buf)) buf = Buffer.from(buf);

	  var len = (buf.length / 4) | 0;
	  var out = new Array(len);

	  for (var i = 0; i < len; i++) {
	    out[i] = buf.readUInt32BE(i * 4);
	  }

	  return out
	}

	function scrubVec (v) {
	  for (var i = 0; i < v.length; v++) {
	    v[i] = 0;
	  }
	}

	function cryptBlock (M, keySchedule, SUB_MIX, SBOX, nRounds) {
	  var SUB_MIX0 = SUB_MIX[0];
	  var SUB_MIX1 = SUB_MIX[1];
	  var SUB_MIX2 = SUB_MIX[2];
	  var SUB_MIX3 = SUB_MIX[3];

	  var s0 = M[0] ^ keySchedule[0];
	  var s1 = M[1] ^ keySchedule[1];
	  var s2 = M[2] ^ keySchedule[2];
	  var s3 = M[3] ^ keySchedule[3];
	  var t0, t1, t2, t3;
	  var ksRow = 4;

	  for (var round = 1; round < nRounds; round++) {
	    t0 = SUB_MIX0[s0 >>> 24] ^ SUB_MIX1[(s1 >>> 16) & 0xff] ^ SUB_MIX2[(s2 >>> 8) & 0xff] ^ SUB_MIX3[s3 & 0xff] ^ keySchedule[ksRow++];
	    t1 = SUB_MIX0[s1 >>> 24] ^ SUB_MIX1[(s2 >>> 16) & 0xff] ^ SUB_MIX2[(s3 >>> 8) & 0xff] ^ SUB_MIX3[s0 & 0xff] ^ keySchedule[ksRow++];
	    t2 = SUB_MIX0[s2 >>> 24] ^ SUB_MIX1[(s3 >>> 16) & 0xff] ^ SUB_MIX2[(s0 >>> 8) & 0xff] ^ SUB_MIX3[s1 & 0xff] ^ keySchedule[ksRow++];
	    t3 = SUB_MIX0[s3 >>> 24] ^ SUB_MIX1[(s0 >>> 16) & 0xff] ^ SUB_MIX2[(s1 >>> 8) & 0xff] ^ SUB_MIX3[s2 & 0xff] ^ keySchedule[ksRow++];
	    s0 = t0;
	    s1 = t1;
	    s2 = t2;
	    s3 = t3;
	  }

	  t0 = ((SBOX[s0 >>> 24] << 24) | (SBOX[(s1 >>> 16) & 0xff] << 16) | (SBOX[(s2 >>> 8) & 0xff] << 8) | SBOX[s3 & 0xff]) ^ keySchedule[ksRow++];
	  t1 = ((SBOX[s1 >>> 24] << 24) | (SBOX[(s2 >>> 16) & 0xff] << 16) | (SBOX[(s3 >>> 8) & 0xff] << 8) | SBOX[s0 & 0xff]) ^ keySchedule[ksRow++];
	  t2 = ((SBOX[s2 >>> 24] << 24) | (SBOX[(s3 >>> 16) & 0xff] << 16) | (SBOX[(s0 >>> 8) & 0xff] << 8) | SBOX[s1 & 0xff]) ^ keySchedule[ksRow++];
	  t3 = ((SBOX[s3 >>> 24] << 24) | (SBOX[(s0 >>> 16) & 0xff] << 16) | (SBOX[(s1 >>> 8) & 0xff] << 8) | SBOX[s2 & 0xff]) ^ keySchedule[ksRow++];
	  t0 = t0 >>> 0;
	  t1 = t1 >>> 0;
	  t2 = t2 >>> 0;
	  t3 = t3 >>> 0;

	  return [t0, t1, t2, t3]
	}

	// AES constants
	var RCON = [0x00, 0x01, 0x02, 0x04, 0x08, 0x10, 0x20, 0x40, 0x80, 0x1b, 0x36];
	var G = (function () {
	  // Compute double table
	  var d = new Array(256);
	  for (var j = 0; j < 256; j++) {
	    if (j < 128) {
	      d[j] = j << 1;
	    } else {
	      d[j] = (j << 1) ^ 0x11b;
	    }
	  }

	  var SBOX = [];
	  var INV_SBOX = [];
	  var SUB_MIX = [[], [], [], []];
	  var INV_SUB_MIX = [[], [], [], []];

	  // Walk GF(2^8)
	  var x = 0;
	  var xi = 0;
	  for (var i = 0; i < 256; ++i) {
	    // Compute sbox
	    var sx = xi ^ (xi << 1) ^ (xi << 2) ^ (xi << 3) ^ (xi << 4);
	    sx = (sx >>> 8) ^ (sx & 0xff) ^ 0x63;
	    SBOX[x] = sx;
	    INV_SBOX[sx] = x;

	    // Compute multiplication
	    var x2 = d[x];
	    var x4 = d[x2];
	    var x8 = d[x4];

	    // Compute sub bytes, mix columns tables
	    var t = (d[sx] * 0x101) ^ (sx * 0x1010100);
	    SUB_MIX[0][x] = (t << 24) | (t >>> 8);
	    SUB_MIX[1][x] = (t << 16) | (t >>> 16);
	    SUB_MIX[2][x] = (t << 8) | (t >>> 24);
	    SUB_MIX[3][x] = t;

	    // Compute inv sub bytes, inv mix columns tables
	    t = (x8 * 0x1010101) ^ (x4 * 0x10001) ^ (x2 * 0x101) ^ (x * 0x1010100);
	    INV_SUB_MIX[0][sx] = (t << 24) | (t >>> 8);
	    INV_SUB_MIX[1][sx] = (t << 16) | (t >>> 16);
	    INV_SUB_MIX[2][sx] = (t << 8) | (t >>> 24);
	    INV_SUB_MIX[3][sx] = t;

	    if (x === 0) {
	      x = xi = 1;
	    } else {
	      x = x2 ^ d[d[d[x8 ^ x2]]];
	      xi ^= d[d[xi]];
	    }
	  }

	  return {
	    SBOX: SBOX,
	    INV_SBOX: INV_SBOX,
	    SUB_MIX: SUB_MIX,
	    INV_SUB_MIX: INV_SUB_MIX
	  }
	})();

	function AES (key) {
	  this._key = asUInt32Array(key);
	  this._reset();
	}

	AES.blockSize = 4 * 4;
	AES.keySize = 256 / 8;
	AES.prototype.blockSize = AES.blockSize;
	AES.prototype.keySize = AES.keySize;
	AES.prototype._reset = function () {
	  var keyWords = this._key;
	  var keySize = keyWords.length;
	  var nRounds = keySize + 6;
	  var ksRows = (nRounds + 1) * 4;

	  var keySchedule = [];
	  for (var k = 0; k < keySize; k++) {
	    keySchedule[k] = keyWords[k];
	  }

	  for (k = keySize; k < ksRows; k++) {
	    var t = keySchedule[k - 1];

	    if (k % keySize === 0) {
	      t = (t << 8) | (t >>> 24);
	      t =
	        (G.SBOX[t >>> 24] << 24) |
	        (G.SBOX[(t >>> 16) & 0xff] << 16) |
	        (G.SBOX[(t >>> 8) & 0xff] << 8) |
	        (G.SBOX[t & 0xff]);

	      t ^= RCON[(k / keySize) | 0] << 24;
	    } else if (keySize > 6 && k % keySize === 4) {
	      t =
	        (G.SBOX[t >>> 24] << 24) |
	        (G.SBOX[(t >>> 16) & 0xff] << 16) |
	        (G.SBOX[(t >>> 8) & 0xff] << 8) |
	        (G.SBOX[t & 0xff]);
	    }

	    keySchedule[k] = keySchedule[k - keySize] ^ t;
	  }

	  var invKeySchedule = [];
	  for (var ik = 0; ik < ksRows; ik++) {
	    var ksR = ksRows - ik;
	    var tt = keySchedule[ksR - (ik % 4 ? 0 : 4)];

	    if (ik < 4 || ksR <= 4) {
	      invKeySchedule[ik] = tt;
	    } else {
	      invKeySchedule[ik] =
	        G.INV_SUB_MIX[0][G.SBOX[tt >>> 24]] ^
	        G.INV_SUB_MIX[1][G.SBOX[(tt >>> 16) & 0xff]] ^
	        G.INV_SUB_MIX[2][G.SBOX[(tt >>> 8) & 0xff]] ^
	        G.INV_SUB_MIX[3][G.SBOX[tt & 0xff]];
	    }
	  }

	  this._nRounds = nRounds;
	  this._keySchedule = keySchedule;
	  this._invKeySchedule = invKeySchedule;
	};

	AES.prototype.encryptBlockRaw = function (M) {
	  M = asUInt32Array(M);
	  return cryptBlock(M, this._keySchedule, G.SUB_MIX, G.SBOX, this._nRounds)
	};

	AES.prototype.encryptBlock = function (M) {
	  var out = this.encryptBlockRaw(M);
	  var buf = Buffer.allocUnsafe(16);
	  buf.writeUInt32BE(out[0], 0);
	  buf.writeUInt32BE(out[1], 4);
	  buf.writeUInt32BE(out[2], 8);
	  buf.writeUInt32BE(out[3], 12);
	  return buf
	};

	AES.prototype.decryptBlock = function (M) {
	  M = asUInt32Array(M);

	  // swap
	  var m1 = M[1];
	  M[1] = M[3];
	  M[3] = m1;

	  var out = cryptBlock(M, this._invKeySchedule, G.INV_SUB_MIX, G.INV_SBOX, this._nRounds);
	  var buf = Buffer.allocUnsafe(16);
	  buf.writeUInt32BE(out[0], 0);
	  buf.writeUInt32BE(out[3], 4);
	  buf.writeUInt32BE(out[2], 8);
	  buf.writeUInt32BE(out[1], 12);
	  return buf
	};

	AES.prototype.scrub = function () {
	  scrubVec(this._keySchedule);
	  scrubVec(this._invKeySchedule);
	  scrubVec(this._key);
	};

	aes.AES = AES;
	return aes;
}

var cipherBase;
var hasRequiredCipherBase;

function requireCipherBase () {
	if (hasRequiredCipherBase) return cipherBase;
	hasRequiredCipherBase = 1;

	var Buffer = require$$0$2.Buffer;
	var Transform = require$$1$2.Transform;
	var StringDecoder = require$$2$1.StringDecoder;
	var inherits = require$$3;

	function CipherBase(hashMode) {
		Transform.call(this);
		this.hashMode = typeof hashMode === 'string';
		if (this.hashMode) {
			this[hashMode] = this._finalOrDigest;
		} else {
			this['final'] = this._finalOrDigest;
		}
		if (this._final) {
			this.__final = this._final;
			this._final = null;
		}
		this._decoder = null;
		this._encoding = null;
	}
	inherits(CipherBase, Transform);

	var useUint8Array = typeof Uint8Array !== 'undefined';
	var useArrayBuffer = typeof ArrayBuffer !== 'undefined'
		&& typeof Uint8Array !== 'undefined'
		&& ArrayBuffer.isView
		&& (Buffer.prototype instanceof Uint8Array || Buffer.TYPED_ARRAY_SUPPORT);

	function toBuffer(data, encoding) {
		/*
		 * No need to do anything for exact instance
		 * This is only valid when safe-buffer.Buffer === buffer.Buffer, i.e. when Buffer.from/Buffer.alloc existed
		 */
		if (data instanceof Buffer) {
			return data;
		}

		// Convert strings to Buffer
		if (typeof data === 'string') {
			return Buffer.from(data, encoding);
		}

		/*
		 * Wrap any TypedArray instances and DataViews
		 * Makes sense only on engines with full TypedArray support -- let Buffer detect that
		 */
		if (useArrayBuffer && ArrayBuffer.isView(data)) {
			// Bug in Node.js <6.3.1, which treats this as out-of-bounds
			if (data.byteLength === 0) {
				return Buffer.alloc(0);
			}

			var res = Buffer.from(data.buffer, data.byteOffset, data.byteLength);
			/*
			 * Recheck result size, as offset/length doesn't work on Node.js <5.10
			 * We just go to Uint8Array case if this fails
			 */
			if (res.byteLength === data.byteLength) {
				return res;
			}
		}

		/*
		 * Uint8Array in engines where Buffer.from might not work with ArrayBuffer, just copy over
		 * Doesn't make sense with other TypedArray instances
		 */
		if (useUint8Array && data instanceof Uint8Array) {
			return Buffer.from(data);
		}

		/*
		 * Old Buffer polyfill on an engine that doesn't have TypedArray support
		 * Also, this is from a different Buffer polyfill implementation then we have, as instanceof check failed
		 * Convert to our current Buffer implementation
		 */
		if (
			Buffer.isBuffer(data)
				&& data.constructor
				&& typeof data.constructor.isBuffer === 'function'
				&& data.constructor.isBuffer(data)
		) {
			return Buffer.from(data);
		}

		throw new TypeError('The "data" argument must be of type string or an instance of Buffer, TypedArray, or DataView.');
	}

	CipherBase.prototype.update = function (data, inputEnc, outputEnc) {
		var bufferData = toBuffer(data, inputEnc); // asserts correct input type
		var outData = this._update(bufferData);
		if (this.hashMode) {
			return this;
		}

		if (outputEnc) {
			outData = this._toString(outData, outputEnc);
		}

		return outData;
	};

	CipherBase.prototype.setAutoPadding = function () {};
	CipherBase.prototype.getAuthTag = function () {
		throw new Error('trying to get auth tag in unsupported state');
	};

	CipherBase.prototype.setAuthTag = function () {
		throw new Error('trying to set auth tag in unsupported state');
	};

	CipherBase.prototype.setAAD = function () {
		throw new Error('trying to set aad in unsupported state');
	};

	CipherBase.prototype._transform = function (data, _, next) {
		var err;
		try {
			if (this.hashMode) {
				this._update(data);
			} else {
				this.push(this._update(data));
			}
		} catch (e) {
			err = e;
		} finally {
			next(err);
		}
	};
	CipherBase.prototype._flush = function (done) {
		var err;
		try {
			this.push(this.__final());
		} catch (e) {
			err = e;
		}

		done(err);
	};
	CipherBase.prototype._finalOrDigest = function (outputEnc) {
		var outData = this.__final() || Buffer.alloc(0);
		if (outputEnc) {
			outData = this._toString(outData, outputEnc, true);
		}
		return outData;
	};

	CipherBase.prototype._toString = function (value, enc, fin) {
		if (!this._decoder) {
			this._decoder = new StringDecoder(enc);
			this._encoding = enc;
		}

		if (this._encoding !== enc) {
			throw new Error('can’t switch encodings');
		}

		var out = this._decoder.write(value);
		if (fin) {
			out += this._decoder.end();
		}

		return out;
	};

	cipherBase = CipherBase;
	return cipherBase;
}

var ghash;
var hasRequiredGhash;

function requireGhash () {
	if (hasRequiredGhash) return ghash;
	hasRequiredGhash = 1;
	var Buffer = require$$0$2.Buffer;
	var ZEROES = Buffer.alloc(16, 0);

	function toArray (buf) {
	  return [
	    buf.readUInt32BE(0),
	    buf.readUInt32BE(4),
	    buf.readUInt32BE(8),
	    buf.readUInt32BE(12)
	  ]
	}

	function fromArray (out) {
	  var buf = Buffer.allocUnsafe(16);
	  buf.writeUInt32BE(out[0] >>> 0, 0);
	  buf.writeUInt32BE(out[1] >>> 0, 4);
	  buf.writeUInt32BE(out[2] >>> 0, 8);
	  buf.writeUInt32BE(out[3] >>> 0, 12);
	  return buf
	}

	function GHASH (key) {
	  this.h = key;
	  this.state = Buffer.alloc(16, 0);
	  this.cache = Buffer.allocUnsafe(0);
	}

	// from http://bitwiseshiftleft.github.io/sjcl/doc/symbols/src/core_gcm.js.html
	// by Juho Vähä-Herttua
	GHASH.prototype.ghash = function (block) {
	  var i = -1;
	  while (++i < block.length) {
	    this.state[i] ^= block[i];
	  }
	  this._multiply();
	};

	GHASH.prototype._multiply = function () {
	  var Vi = toArray(this.h);
	  var Zi = [0, 0, 0, 0];
	  var j, xi, lsbVi;
	  var i = -1;
	  while (++i < 128) {
	    xi = (this.state[~~(i / 8)] & (1 << (7 - (i % 8)))) !== 0;
	    if (xi) {
	      // Z_i+1 = Z_i ^ V_i
	      Zi[0] ^= Vi[0];
	      Zi[1] ^= Vi[1];
	      Zi[2] ^= Vi[2];
	      Zi[3] ^= Vi[3];
	    }

	    // Store the value of LSB(V_i)
	    lsbVi = (Vi[3] & 1) !== 0;

	    // V_i+1 = V_i >> 1
	    for (j = 3; j > 0; j--) {
	      Vi[j] = (Vi[j] >>> 1) | ((Vi[j - 1] & 1) << 31);
	    }
	    Vi[0] = Vi[0] >>> 1;

	    // If LSB(V_i) is 1, V_i+1 = (V_i >> 1) ^ R
	    if (lsbVi) {
	      Vi[0] = Vi[0] ^ (0xe1 << 24);
	    }
	  }
	  this.state = fromArray(Zi);
	};

	GHASH.prototype.update = function (buf) {
	  this.cache = Buffer.concat([this.cache, buf]);
	  var chunk;
	  while (this.cache.length >= 16) {
	    chunk = this.cache.slice(0, 16);
	    this.cache = this.cache.slice(16);
	    this.ghash(chunk);
	  }
	};

	GHASH.prototype.final = function (abl, bl) {
	  if (this.cache.length) {
	    this.ghash(Buffer.concat([this.cache, ZEROES], 16));
	  }

	  this.ghash(fromArray([0, abl, 0, bl]));
	  return this.state
	};

	ghash = GHASH;
	return ghash;
}

var authCipher;
var hasRequiredAuthCipher;

function requireAuthCipher () {
	if (hasRequiredAuthCipher) return authCipher;
	hasRequiredAuthCipher = 1;
	var aes = requireAes();
	var Buffer = require$$0$2.Buffer;
	var Transform = requireCipherBase();
	var inherits = require$$3;
	var GHASH = requireGhash();
	var xor = require$$0$1;
	var incr32 = requireIncr32();

	function xorTest (a, b) {
	  var out = 0;
	  if (a.length !== b.length) out++;

	  var len = Math.min(a.length, b.length);
	  for (var i = 0; i < len; ++i) {
	    out += (a[i] ^ b[i]);
	  }

	  return out
	}

	function calcIv (self, iv, ck) {
	  if (iv.length === 12) {
	    self._finID = Buffer.concat([iv, Buffer.from([0, 0, 0, 1])]);
	    return Buffer.concat([iv, Buffer.from([0, 0, 0, 2])])
	  }
	  var ghash = new GHASH(ck);
	  var len = iv.length;
	  var toPad = len % 16;
	  ghash.update(iv);
	  if (toPad) {
	    toPad = 16 - toPad;
	    ghash.update(Buffer.alloc(toPad, 0));
	  }
	  ghash.update(Buffer.alloc(8, 0));
	  var ivBits = len * 8;
	  var tail = Buffer.alloc(8);
	  tail.writeUIntBE(ivBits, 0, 8);
	  ghash.update(tail);
	  self._finID = ghash.state;
	  var out = Buffer.from(self._finID);
	  incr32(out);
	  return out
	}
	function StreamCipher (mode, key, iv, decrypt) {
	  Transform.call(this);

	  var h = Buffer.alloc(4, 0);

	  this._cipher = new aes.AES(key);
	  var ck = this._cipher.encryptBlock(h);
	  this._ghash = new GHASH(ck);
	  iv = calcIv(this, iv, ck);

	  this._prev = Buffer.from(iv);
	  this._cache = Buffer.allocUnsafe(0);
	  this._secCache = Buffer.allocUnsafe(0);
	  this._decrypt = decrypt;
	  this._alen = 0;
	  this._len = 0;
	  this._mode = mode;

	  this._authTag = null;
	  this._called = false;
	}

	inherits(StreamCipher, Transform);

	StreamCipher.prototype._update = function (chunk) {
	  if (!this._called && this._alen) {
	    var rump = 16 - (this._alen % 16);
	    if (rump < 16) {
	      rump = Buffer.alloc(rump, 0);
	      this._ghash.update(rump);
	    }
	  }

	  this._called = true;
	  var out = this._mode.encrypt(this, chunk);
	  if (this._decrypt) {
	    this._ghash.update(chunk);
	  } else {
	    this._ghash.update(out);
	  }
	  this._len += chunk.length;
	  return out
	};

	StreamCipher.prototype._final = function () {
	  if (this._decrypt && !this._authTag) throw new Error('Unsupported state or unable to authenticate data')

	  var tag = xor(this._ghash.final(this._alen * 8, this._len * 8), this._cipher.encryptBlock(this._finID));
	  if (this._decrypt && xorTest(tag, this._authTag)) throw new Error('Unsupported state or unable to authenticate data')

	  this._authTag = tag;
	  this._cipher.scrub();
	};

	StreamCipher.prototype.getAuthTag = function getAuthTag () {
	  if (this._decrypt || !Buffer.isBuffer(this._authTag)) throw new Error('Attempting to get auth tag in unsupported state')

	  return this._authTag
	};

	StreamCipher.prototype.setAuthTag = function setAuthTag (tag) {
	  if (!this._decrypt) throw new Error('Attempting to set auth tag in unsupported state')

	  this._authTag = tag;
	};

	StreamCipher.prototype.setAAD = function setAAD (buf) {
	  if (this._called) throw new Error('Attempting to set AAD in unsupported state')

	  this._ghash.update(buf);
	  this._alen += buf.length;
	};

	authCipher = StreamCipher;
	return authCipher;
}

var streamCipher;
var hasRequiredStreamCipher;

function requireStreamCipher () {
	if (hasRequiredStreamCipher) return streamCipher;
	hasRequiredStreamCipher = 1;
	var aes = requireAes();
	var Buffer = require$$0$2.Buffer;
	var Transform = requireCipherBase();
	var inherits = require$$3;

	function StreamCipher (mode, key, iv, decrypt) {
	  Transform.call(this);

	  this._cipher = new aes.AES(key);
	  this._prev = Buffer.from(iv);
	  this._cache = Buffer.allocUnsafe(0);
	  this._secCache = Buffer.allocUnsafe(0);
	  this._decrypt = decrypt;
	  this._mode = mode;
	}

	inherits(StreamCipher, Transform);

	StreamCipher.prototype._update = function (chunk) {
	  return this._mode.encrypt(this, chunk, this._decrypt)
	};

	StreamCipher.prototype._final = function () {
	  this._cipher.scrub();
	};

	streamCipher = StreamCipher;
	return streamCipher;
}

var hashBase;
var hasRequiredHashBase;

function requireHashBase () {
	if (hasRequiredHashBase) return hashBase;
	hasRequiredHashBase = 1;
	var Buffer = require$$0$2.Buffer;
	var Transform = require$$1$2.Transform;
	var inherits = require$$3;

	function HashBase (blockSize) {
	  Transform.call(this);

	  this._block = Buffer.allocUnsafe(blockSize);
	  this._blockSize = blockSize;
	  this._blockOffset = 0;
	  this._length = [0, 0, 0, 0];

	  this._finalized = false;
	}

	inherits(HashBase, Transform);

	HashBase.prototype._transform = function (chunk, encoding, callback) {
	  var error = null;
	  try {
	    this.update(chunk, encoding);
	  } catch (err) {
	    error = err;
	  }

	  callback(error);
	};

	HashBase.prototype._flush = function (callback) {
	  var error = null;
	  try {
	    this.push(this.digest());
	  } catch (err) {
	    error = err;
	  }

	  callback(error);
	};

	var useUint8Array = typeof Uint8Array !== 'undefined';
	var useArrayBuffer = typeof ArrayBuffer !== 'undefined' &&
	  typeof Uint8Array !== 'undefined' &&
	  ArrayBuffer.isView &&
	  (Buffer.prototype instanceof Uint8Array || Buffer.TYPED_ARRAY_SUPPORT);

	function toBuffer (data, encoding) {
	  // No need to do anything for exact instance
	  // This is only valid when safe-buffer.Buffer === buffer.Buffer, i.e. when Buffer.from/Buffer.alloc existed
	  if (data instanceof Buffer) return data

	  // Convert strings to Buffer
	  if (typeof data === 'string') return Buffer.from(data, encoding)

	  /*
	   * Wrap any TypedArray instances and DataViews
	   * Makes sense only on engines with full TypedArray support -- let Buffer detect that
	   */
	  if (useArrayBuffer && ArrayBuffer.isView(data)) {
	    if (data.byteLength === 0) return Buffer.alloc(0) // Bug in Node.js <6.3.1, which treats this as out-of-bounds
	    var res = Buffer.from(data.buffer, data.byteOffset, data.byteLength);
	    // Recheck result size, as offset/length doesn't work on Node.js <5.10
	    // We just go to Uint8Array case if this fails
	    if (res.byteLength === data.byteLength) return res
	  }

	  /*
	   * Uint8Array in engines where Buffer.from might not work with ArrayBuffer, just copy over
	   * Doesn't make sense with other TypedArray instances
	   */
	  if (useUint8Array && data instanceof Uint8Array) return Buffer.from(data)

	  /*
	   * Old Buffer polyfill on an engine that doesn't have TypedArray support
	   * Also, this is from a different Buffer polyfill implementation then we have, as instanceof check failed
	   * Convert to our current Buffer implementation
	   */
	  if (
	    Buffer.isBuffer(data) &&
	    data.constructor &&
	    typeof data.constructor.isBuffer === 'function' &&
	    data.constructor.isBuffer(data)
	  ) {
	    return Buffer.from(data)
	  }

	  throw new TypeError('The "data" argument must be of type string or an instance of Buffer, TypedArray, or DataView.')
	}

	HashBase.prototype.update = function (data, encoding) {
	  if (this._finalized) throw new Error('Digest already called')

	  data = toBuffer(data, encoding); // asserts correct input type

	  // consume data
	  var block = this._block;
	  var offset = 0;
	  while (this._blockOffset + data.length - offset >= this._blockSize) {
	    for (var i = this._blockOffset; i < this._blockSize;) block[i++] = data[offset++];
	    this._update();
	    this._blockOffset = 0;
	  }
	  while (offset < data.length) block[this._blockOffset++] = data[offset++];

	  // update length
	  for (var j = 0, carry = data.length * 8; carry > 0; ++j) {
	    this._length[j] += carry;
	    carry = (this._length[j] / 0x0100000000) | 0;
	    if (carry > 0) this._length[j] -= 0x0100000000 * carry;
	  }

	  return this
	};

	HashBase.prototype._update = function () {
	  throw new Error('_update is not implemented')
	};

	HashBase.prototype.digest = function (encoding) {
	  if (this._finalized) throw new Error('Digest already called')
	  this._finalized = true;

	  var digest = this._digest();
	  if (encoding !== undefined) digest = digest.toString(encoding);

	  // reset state
	  this._block.fill(0);
	  this._blockOffset = 0;
	  for (var i = 0; i < 4; ++i) this._length[i] = 0;

	  return digest
	};

	HashBase.prototype._digest = function () {
	  throw new Error('_digest is not implemented')
	};

	hashBase = HashBase;
	return hashBase;
}

var md5_js;
var hasRequiredMd5_js;

function requireMd5_js () {
	if (hasRequiredMd5_js) return md5_js;
	hasRequiredMd5_js = 1;
	var inherits = require$$3;
	var HashBase = requireHashBase();
	var Buffer = require$$0$2.Buffer;

	var ARRAY16 = new Array(16);

	function MD5 () {
	  HashBase.call(this, 64);

	  // state
	  this._a = 0x67452301;
	  this._b = 0xefcdab89;
	  this._c = 0x98badcfe;
	  this._d = 0x10325476;
	}

	inherits(MD5, HashBase);

	MD5.prototype._update = function () {
	  var M = ARRAY16;
	  for (var i = 0; i < 16; ++i) M[i] = this._block.readInt32LE(i * 4);

	  var a = this._a;
	  var b = this._b;
	  var c = this._c;
	  var d = this._d;

	  a = fnF(a, b, c, d, M[0], 0xd76aa478, 7);
	  d = fnF(d, a, b, c, M[1], 0xe8c7b756, 12);
	  c = fnF(c, d, a, b, M[2], 0x242070db, 17);
	  b = fnF(b, c, d, a, M[3], 0xc1bdceee, 22);
	  a = fnF(a, b, c, d, M[4], 0xf57c0faf, 7);
	  d = fnF(d, a, b, c, M[5], 0x4787c62a, 12);
	  c = fnF(c, d, a, b, M[6], 0xa8304613, 17);
	  b = fnF(b, c, d, a, M[7], 0xfd469501, 22);
	  a = fnF(a, b, c, d, M[8], 0x698098d8, 7);
	  d = fnF(d, a, b, c, M[9], 0x8b44f7af, 12);
	  c = fnF(c, d, a, b, M[10], 0xffff5bb1, 17);
	  b = fnF(b, c, d, a, M[11], 0x895cd7be, 22);
	  a = fnF(a, b, c, d, M[12], 0x6b901122, 7);
	  d = fnF(d, a, b, c, M[13], 0xfd987193, 12);
	  c = fnF(c, d, a, b, M[14], 0xa679438e, 17);
	  b = fnF(b, c, d, a, M[15], 0x49b40821, 22);

	  a = fnG(a, b, c, d, M[1], 0xf61e2562, 5);
	  d = fnG(d, a, b, c, M[6], 0xc040b340, 9);
	  c = fnG(c, d, a, b, M[11], 0x265e5a51, 14);
	  b = fnG(b, c, d, a, M[0], 0xe9b6c7aa, 20);
	  a = fnG(a, b, c, d, M[5], 0xd62f105d, 5);
	  d = fnG(d, a, b, c, M[10], 0x02441453, 9);
	  c = fnG(c, d, a, b, M[15], 0xd8a1e681, 14);
	  b = fnG(b, c, d, a, M[4], 0xe7d3fbc8, 20);
	  a = fnG(a, b, c, d, M[9], 0x21e1cde6, 5);
	  d = fnG(d, a, b, c, M[14], 0xc33707d6, 9);
	  c = fnG(c, d, a, b, M[3], 0xf4d50d87, 14);
	  b = fnG(b, c, d, a, M[8], 0x455a14ed, 20);
	  a = fnG(a, b, c, d, M[13], 0xa9e3e905, 5);
	  d = fnG(d, a, b, c, M[2], 0xfcefa3f8, 9);
	  c = fnG(c, d, a, b, M[7], 0x676f02d9, 14);
	  b = fnG(b, c, d, a, M[12], 0x8d2a4c8a, 20);

	  a = fnH(a, b, c, d, M[5], 0xfffa3942, 4);
	  d = fnH(d, a, b, c, M[8], 0x8771f681, 11);
	  c = fnH(c, d, a, b, M[11], 0x6d9d6122, 16);
	  b = fnH(b, c, d, a, M[14], 0xfde5380c, 23);
	  a = fnH(a, b, c, d, M[1], 0xa4beea44, 4);
	  d = fnH(d, a, b, c, M[4], 0x4bdecfa9, 11);
	  c = fnH(c, d, a, b, M[7], 0xf6bb4b60, 16);
	  b = fnH(b, c, d, a, M[10], 0xbebfbc70, 23);
	  a = fnH(a, b, c, d, M[13], 0x289b7ec6, 4);
	  d = fnH(d, a, b, c, M[0], 0xeaa127fa, 11);
	  c = fnH(c, d, a, b, M[3], 0xd4ef3085, 16);
	  b = fnH(b, c, d, a, M[6], 0x04881d05, 23);
	  a = fnH(a, b, c, d, M[9], 0xd9d4d039, 4);
	  d = fnH(d, a, b, c, M[12], 0xe6db99e5, 11);
	  c = fnH(c, d, a, b, M[15], 0x1fa27cf8, 16);
	  b = fnH(b, c, d, a, M[2], 0xc4ac5665, 23);

	  a = fnI(a, b, c, d, M[0], 0xf4292244, 6);
	  d = fnI(d, a, b, c, M[7], 0x432aff97, 10);
	  c = fnI(c, d, a, b, M[14], 0xab9423a7, 15);
	  b = fnI(b, c, d, a, M[5], 0xfc93a039, 21);
	  a = fnI(a, b, c, d, M[12], 0x655b59c3, 6);
	  d = fnI(d, a, b, c, M[3], 0x8f0ccc92, 10);
	  c = fnI(c, d, a, b, M[10], 0xffeff47d, 15);
	  b = fnI(b, c, d, a, M[1], 0x85845dd1, 21);
	  a = fnI(a, b, c, d, M[8], 0x6fa87e4f, 6);
	  d = fnI(d, a, b, c, M[15], 0xfe2ce6e0, 10);
	  c = fnI(c, d, a, b, M[6], 0xa3014314, 15);
	  b = fnI(b, c, d, a, M[13], 0x4e0811a1, 21);
	  a = fnI(a, b, c, d, M[4], 0xf7537e82, 6);
	  d = fnI(d, a, b, c, M[11], 0xbd3af235, 10);
	  c = fnI(c, d, a, b, M[2], 0x2ad7d2bb, 15);
	  b = fnI(b, c, d, a, M[9], 0xeb86d391, 21);

	  this._a = (this._a + a) | 0;
	  this._b = (this._b + b) | 0;
	  this._c = (this._c + c) | 0;
	  this._d = (this._d + d) | 0;
	};

	MD5.prototype._digest = function () {
	  // create padding and handle blocks
	  this._block[this._blockOffset++] = 0x80;
	  if (this._blockOffset > 56) {
	    this._block.fill(0, this._blockOffset, 64);
	    this._update();
	    this._blockOffset = 0;
	  }

	  this._block.fill(0, this._blockOffset, 56);
	  this._block.writeUInt32LE(this._length[0], 56);
	  this._block.writeUInt32LE(this._length[1], 60);
	  this._update();

	  // produce result
	  var buffer = Buffer.allocUnsafe(16);
	  buffer.writeInt32LE(this._a, 0);
	  buffer.writeInt32LE(this._b, 4);
	  buffer.writeInt32LE(this._c, 8);
	  buffer.writeInt32LE(this._d, 12);
	  return buffer
	};

	function rotl (x, n) {
	  return (x << n) | (x >>> (32 - n))
	}

	function fnF (a, b, c, d, m, k, s) {
	  return (rotl((a + ((b & c) | ((~b) & d)) + m + k) | 0, s) + b) | 0
	}

	function fnG (a, b, c, d, m, k, s) {
	  return (rotl((a + ((b & d) | (c & (~d))) + m + k) | 0, s) + b) | 0
	}

	function fnH (a, b, c, d, m, k, s) {
	  return (rotl((a + (b ^ c ^ d) + m + k) | 0, s) + b) | 0
	}

	function fnI (a, b, c, d, m, k, s) {
	  return (rotl((a + ((c ^ (b | (~d)))) + m + k) | 0, s) + b) | 0
	}

	md5_js = MD5;
	return md5_js;
}

var evp_bytestokey;
var hasRequiredEvp_bytestokey;

function requireEvp_bytestokey () {
	if (hasRequiredEvp_bytestokey) return evp_bytestokey;
	hasRequiredEvp_bytestokey = 1;
	var Buffer = require$$0$2.Buffer;
	var MD5 = requireMd5_js();

	/* eslint-disable camelcase */
	function EVP_BytesToKey (password, salt, keyBits, ivLen) {
	  if (!Buffer.isBuffer(password)) password = Buffer.from(password, 'binary');
	  if (salt) {
	    if (!Buffer.isBuffer(salt)) salt = Buffer.from(salt, 'binary');
	    if (salt.length !== 8) throw new RangeError('salt should be Buffer with 8 byte length')
	  }

	  var keyLen = keyBits / 8;
	  var key = Buffer.alloc(keyLen);
	  var iv = Buffer.alloc(ivLen || 0);
	  var tmp = Buffer.alloc(0);

	  while (keyLen > 0 || ivLen > 0) {
	    var hash = new MD5();
	    hash.update(tmp);
	    hash.update(password);
	    if (salt) hash.update(salt);
	    tmp = hash.digest();

	    var used = 0;

	    if (keyLen > 0) {
	      var keyStart = key.length - keyLen;
	      used = Math.min(keyLen, tmp.length);
	      tmp.copy(key, keyStart, 0, used);
	      keyLen -= used;
	    }

	    if (used < tmp.length && ivLen > 0) {
	      var ivStart = iv.length - ivLen;
	      var length = Math.min(ivLen, tmp.length - used);
	      tmp.copy(iv, ivStart, used, used + length);
	      ivLen -= length;
	    }
	  }

	  tmp.fill(0);
	  return { key: key, iv: iv }
	}

	evp_bytestokey = EVP_BytesToKey;
	return evp_bytestokey;
}

var hasRequiredEncrypter;

function requireEncrypter () {
	if (hasRequiredEncrypter) return encrypter;
	hasRequiredEncrypter = 1;
	var MODES = requireModes();
	var AuthCipher = requireAuthCipher();
	var Buffer = require$$0$2.Buffer;
	var StreamCipher = requireStreamCipher();
	var Transform = requireCipherBase();
	var aes = requireAes();
	var ebtk = requireEvp_bytestokey();
	var inherits = require$$3;

	function Cipher (mode, key, iv) {
	  Transform.call(this);

	  this._cache = new Splitter();
	  this._cipher = new aes.AES(key);
	  this._prev = Buffer.from(iv);
	  this._mode = mode;
	  this._autopadding = true;
	}

	inherits(Cipher, Transform);

	Cipher.prototype._update = function (data) {
	  this._cache.add(data);
	  var chunk;
	  var thing;
	  var out = [];

	  while ((chunk = this._cache.get())) {
	    thing = this._mode.encrypt(this, chunk);
	    out.push(thing);
	  }

	  return Buffer.concat(out)
	};

	var PADDING = Buffer.alloc(16, 0x10);

	Cipher.prototype._final = function () {
	  var chunk = this._cache.flush();
	  if (this._autopadding) {
	    chunk = this._mode.encrypt(this, chunk);
	    this._cipher.scrub();
	    return chunk
	  }

	  if (!chunk.equals(PADDING)) {
	    this._cipher.scrub();
	    throw new Error('data not multiple of block length')
	  }
	};

	Cipher.prototype.setAutoPadding = function (setTo) {
	  this._autopadding = !!setTo;
	  return this
	};

	function Splitter () {
	  this.cache = Buffer.allocUnsafe(0);
	}

	Splitter.prototype.add = function (data) {
	  this.cache = Buffer.concat([this.cache, data]);
	};

	Splitter.prototype.get = function () {
	  if (this.cache.length > 15) {
	    var out = this.cache.slice(0, 16);
	    this.cache = this.cache.slice(16);
	    return out
	  }
	  return null
	};

	Splitter.prototype.flush = function () {
	  var len = 16 - this.cache.length;
	  var padBuff = Buffer.allocUnsafe(len);

	  var i = -1;
	  while (++i < len) {
	    padBuff.writeUInt8(len, i);
	  }

	  return Buffer.concat([this.cache, padBuff])
	};

	function createCipheriv (suite, password, iv) {
	  var config = MODES[suite.toLowerCase()];
	  if (!config) throw new TypeError('invalid suite type')

	  if (typeof password === 'string') password = Buffer.from(password);
	  if (password.length !== config.key / 8) throw new TypeError('invalid key length ' + password.length)

	  if (typeof iv === 'string') iv = Buffer.from(iv);
	  if (config.mode !== 'GCM' && iv.length !== config.iv) throw new TypeError('invalid iv length ' + iv.length)

	  if (config.type === 'stream') {
	    return new StreamCipher(config.module, password, iv)
	  } else if (config.type === 'auth') {
	    return new AuthCipher(config.module, password, iv)
	  }

	  return new Cipher(config.module, password, iv)
	}

	function createCipher (suite, password) {
	  var config = MODES[suite.toLowerCase()];
	  if (!config) throw new TypeError('invalid suite type')

	  var keys = ebtk(password, false, config.key, config.iv);
	  return createCipheriv(suite, keys.key, keys.iv)
	}

	encrypter.createCipheriv = createCipheriv;
	encrypter.createCipher = createCipher;
	return encrypter;
}

var decrypter = {};

var hasRequiredDecrypter;

function requireDecrypter () {
	if (hasRequiredDecrypter) return decrypter;
	hasRequiredDecrypter = 1;
	var AuthCipher = requireAuthCipher();
	var Buffer = require$$0$2.Buffer;
	var MODES = requireModes();
	var StreamCipher = requireStreamCipher();
	var Transform = requireCipherBase();
	var aes = requireAes();
	var ebtk = requireEvp_bytestokey();
	var inherits = require$$3;

	function Decipher (mode, key, iv) {
	  Transform.call(this);

	  this._cache = new Splitter();
	  this._last = void 0;
	  this._cipher = new aes.AES(key);
	  this._prev = Buffer.from(iv);
	  this._mode = mode;
	  this._autopadding = true;
	}

	inherits(Decipher, Transform);

	Decipher.prototype._update = function (data) {
	  this._cache.add(data);
	  var chunk;
	  var thing;
	  var out = [];
	  while ((chunk = this._cache.get(this._autopadding))) {
	    thing = this._mode.decrypt(this, chunk);
	    out.push(thing);
	  }
	  return Buffer.concat(out)
	};

	Decipher.prototype._final = function () {
	  var chunk = this._cache.flush();
	  if (this._autopadding) {
	    return unpad(this._mode.decrypt(this, chunk))
	  } else if (chunk) {
	    throw new Error('data not multiple of block length')
	  }
	};

	Decipher.prototype.setAutoPadding = function (setTo) {
	  this._autopadding = !!setTo;
	  return this
	};

	function Splitter () {
	  this.cache = Buffer.allocUnsafe(0);
	}

	Splitter.prototype.add = function (data) {
	  this.cache = Buffer.concat([this.cache, data]);
	};

	Splitter.prototype.get = function (autoPadding) {
	  var out;
	  if (autoPadding) {
	    if (this.cache.length > 16) {
	      out = this.cache.slice(0, 16);
	      this.cache = this.cache.slice(16);
	      return out
	    }
	  } else {
	    if (this.cache.length >= 16) {
	      out = this.cache.slice(0, 16);
	      this.cache = this.cache.slice(16);
	      return out
	    }
	  }

	  return null
	};

	Splitter.prototype.flush = function () {
	  if (this.cache.length) return this.cache
	};

	function unpad (last) {
	  var padded = last[15];
	  if (padded < 1 || padded > 16) {
	    throw new Error('unable to decrypt data')
	  }
	  var i = -1;
	  while (++i < padded) {
	    if (last[(i + (16 - padded))] !== padded) {
	      throw new Error('unable to decrypt data')
	    }
	  }
	  if (padded === 16) return

	  return last.slice(0, 16 - padded)
	}

	function createDecipheriv (suite, password, iv) {
	  var config = MODES[suite.toLowerCase()];
	  if (!config) throw new TypeError('invalid suite type')

	  if (typeof iv === 'string') iv = Buffer.from(iv);
	  if (config.mode !== 'GCM' && iv.length !== config.iv) throw new TypeError('invalid iv length ' + iv.length)

	  if (typeof password === 'string') password = Buffer.from(password);
	  if (password.length !== config.key / 8) throw new TypeError('invalid key length ' + password.length)

	  if (config.type === 'stream') {
	    return new StreamCipher(config.module, password, iv, true)
	  } else if (config.type === 'auth') {
	    return new AuthCipher(config.module, password, iv, true)
	  }

	  return new Decipher(config.module, password, iv)
	}

	function createDecipher (suite, password) {
	  var config = MODES[suite.toLowerCase()];
	  if (!config) throw new TypeError('invalid suite type')

	  var keys = ebtk(password, false, config.key, config.iv);
	  return createDecipheriv(suite, keys.key, keys.iv)
	}

	decrypter.createDecipher = createDecipher;
	decrypter.createDecipheriv = createDecipheriv;
	return decrypter;
}

var hasRequiredBrowser$3;

function requireBrowser$3 () {
	if (hasRequiredBrowser$3) return browser$3;
	hasRequiredBrowser$3 = 1;
	var ciphers = requireEncrypter();
	var deciphers = requireDecrypter();
	var modes = require$$2;

	function getCiphers () {
	  return Object.keys(modes)
	}

	browser$3.createCipher = browser$3.Cipher = ciphers.createCipher;
	browser$3.createCipheriv = browser$3.Cipheriv = ciphers.createCipheriv;
	browser$3.createDecipher = browser$3.Decipher = deciphers.createDecipher;
	browser$3.createDecipheriv = browser$3.Decipheriv = deciphers.createDecipheriv;
	browser$3.listCiphers = browser$3.getCiphers = getCiphers;
	return browser$3;
}

var browserExports$1 = requireBrowser$3();

var modesExports = requireModes();
var aesModes = /*@__PURE__*/getDefaultExportFromCjs(modesExports);

const binaryToUtf8 = (bytes) => {
    return new TextDecoder().decode(bytes);
};
const utf8ToBinary = (utf8) => {
    return new TextEncoder().encode(utf8);
};
const binaryToHex = (bytes, addPrefix = false) => {
    if (addPrefix) {
        return `0x${Buffer.from(bytes).toString('hex')}`;
    }
    return Buffer.from(bytes).toString('hex');
};
const hexToBinary = (hex) => {
    if (hex.startsWith('0x')) {
        hex = hex.slice(2);
    }
    if (hex.length % 2 !== 0) {
        throw new Error(`Hex string length must be even, received: 0x${hex}`);
    }
    const result = Buffer.from(hex, 'hex');
    if (hex.length !== result.length * 2) {
        throw new Error(`Hex string input is likely malformed, received: 0x${hex}`);
    }
    return result;
};
const areEqualBinaries = (arr1, arr2) => {
    return Buffer.compare(arr1, arr2) === 0;
};

var browser$2 = {};

var asn1 = {};

var certificate;
var hasRequiredCertificate;

function requireCertificate () {
	if (hasRequiredCertificate) return certificate;
	hasRequiredCertificate = 1;

	var asn = require$$0$3;

	var Time = asn.define('Time', function () {
		this.choice({
			utcTime: this.utctime(),
			generalTime: this.gentime()
		});
	});

	var AttributeTypeValue = asn.define('AttributeTypeValue', function () {
		this.seq().obj(
			this.key('type').objid(),
			this.key('value').any()
		);
	});

	var AlgorithmIdentifier = asn.define('AlgorithmIdentifier', function () {
		this.seq().obj(
			this.key('algorithm').objid(),
			this.key('parameters').optional(),
			this.key('curve').objid().optional()
		);
	});

	var SubjectPublicKeyInfo = asn.define('SubjectPublicKeyInfo', function () {
		this.seq().obj(
			this.key('algorithm').use(AlgorithmIdentifier),
			this.key('subjectPublicKey').bitstr()
		);
	});

	var RelativeDistinguishedName = asn.define('RelativeDistinguishedName', function () {
		this.setof(AttributeTypeValue);
	});

	var RDNSequence = asn.define('RDNSequence', function () {
		this.seqof(RelativeDistinguishedName);
	});

	var Name = asn.define('Name', function () {
		this.choice({
			rdnSequence: this.use(RDNSequence)
		});
	});

	var Validity = asn.define('Validity', function () {
		this.seq().obj(
			this.key('notBefore').use(Time),
			this.key('notAfter').use(Time)
		);
	});

	var Extension = asn.define('Extension', function () {
		this.seq().obj(
			this.key('extnID').objid(),
			this.key('critical').bool().def(false),
			this.key('extnValue').octstr()
		);
	});

	var TBSCertificate = asn.define('TBSCertificate', function () {
		this.seq().obj(
			this.key('version').explicit(0)['int']().optional(),
			this.key('serialNumber')['int'](),
			this.key('signature').use(AlgorithmIdentifier),
			this.key('issuer').use(Name),
			this.key('validity').use(Validity),
			this.key('subject').use(Name),
			this.key('subjectPublicKeyInfo').use(SubjectPublicKeyInfo),
			this.key('issuerUniqueID').implicit(1).bitstr().optional(),
			this.key('subjectUniqueID').implicit(2).bitstr().optional(),
			this.key('extensions').explicit(3).seqof(Extension).optional()
		);
	});

	var X509Certificate = asn.define('X509Certificate', function () {
		this.seq().obj(
			this.key('tbsCertificate').use(TBSCertificate),
			this.key('signatureAlgorithm').use(AlgorithmIdentifier),
			this.key('signatureValue').bitstr()
		);
	});

	certificate = X509Certificate;
	return certificate;
}

var hasRequiredAsn1;

function requireAsn1 () {
	if (hasRequiredAsn1) return asn1;
	hasRequiredAsn1 = 1;

	var asn1$1 = require$$0$3;

	asn1.certificate = requireCertificate();

	var RSAPrivateKey = asn1$1.define('RSAPrivateKey', function () {
		this.seq().obj(
			this.key('version')['int'](),
			this.key('modulus')['int'](),
			this.key('publicExponent')['int'](),
			this.key('privateExponent')['int'](),
			this.key('prime1')['int'](),
			this.key('prime2')['int'](),
			this.key('exponent1')['int'](),
			this.key('exponent2')['int'](),
			this.key('coefficient')['int']()
		);
	});
	asn1.RSAPrivateKey = RSAPrivateKey;

	var RSAPublicKey = asn1$1.define('RSAPublicKey', function () {
		this.seq().obj(
			this.key('modulus')['int'](),
			this.key('publicExponent')['int']()
		);
	});
	asn1.RSAPublicKey = RSAPublicKey;

	var AlgorithmIdentifier = asn1$1.define('AlgorithmIdentifier', function () {
		this.seq().obj(
			this.key('algorithm').objid(),
			this.key('none').null_().optional(),
			this.key('curve').objid().optional(),
			this.key('params').seq().obj(
				this.key('p')['int'](),
				this.key('q')['int'](),
				this.key('g')['int']()
			).optional()
		);
	});

	var PublicKey = asn1$1.define('SubjectPublicKeyInfo', function () {
		this.seq().obj(
			this.key('algorithm').use(AlgorithmIdentifier),
			this.key('subjectPublicKey').bitstr()
		);
	});
	asn1.PublicKey = PublicKey;

	var PrivateKeyInfo = asn1$1.define('PrivateKeyInfo', function () {
		this.seq().obj(
			this.key('version')['int'](),
			this.key('algorithm').use(AlgorithmIdentifier),
			this.key('subjectPrivateKey').octstr()
		);
	});
	asn1.PrivateKey = PrivateKeyInfo;
	var EncryptedPrivateKeyInfo = asn1$1.define('EncryptedPrivateKeyInfo', function () {
		this.seq().obj(
			this.key('algorithm').seq().obj(
				this.key('id').objid(),
				this.key('decrypt').seq().obj(
					this.key('kde').seq().obj(
						this.key('id').objid(),
						this.key('kdeparams').seq().obj(
							this.key('salt').octstr(),
							this.key('iters')['int']()
						)
					),
					this.key('cipher').seq().obj(
						this.key('algo').objid(),
						this.key('iv').octstr()
					)
				)
			),
			this.key('subjectPrivateKey').octstr()
		);
	});

	asn1.EncryptedPrivateKey = EncryptedPrivateKeyInfo;

	var DSAPrivateKey = asn1$1.define('DSAPrivateKey', function () {
		this.seq().obj(
			this.key('version')['int'](),
			this.key('p')['int'](),
			this.key('q')['int'](),
			this.key('g')['int'](),
			this.key('pub_key')['int'](),
			this.key('priv_key')['int']()
		);
	});
	asn1.DSAPrivateKey = DSAPrivateKey;

	asn1.DSAparam = asn1$1.define('DSAparam', function () {
		this['int']();
	});

	var ECParameters = asn1$1.define('ECParameters', function () {
		this.choice({
			namedCurve: this.objid()
		});
	});

	var ECPrivateKey = asn1$1.define('ECPrivateKey', function () {
		this.seq().obj(
			this.key('version')['int'](),
			this.key('privateKey').octstr(),
			this.key('parameters').optional().explicit(0).use(ECParameters),
			this.key('publicKey').optional().explicit(1).bitstr()
		);
	});
	asn1.ECPrivateKey = ECPrivateKey;

	asn1.signature = asn1$1.define('signature', function () {
		this.seq().obj(
			this.key('r')['int'](),
			this.key('s')['int']()
		);
	});
	return asn1;
}

var require$$1$1 = {
	"2.16.840.1.101.3.4.1.1": "aes-128-ecb",
	"2.16.840.1.101.3.4.1.2": "aes-128-cbc",
	"2.16.840.1.101.3.4.1.3": "aes-128-ofb",
	"2.16.840.1.101.3.4.1.4": "aes-128-cfb",
	"2.16.840.1.101.3.4.1.21": "aes-192-ecb",
	"2.16.840.1.101.3.4.1.22": "aes-192-cbc",
	"2.16.840.1.101.3.4.1.23": "aes-192-ofb",
	"2.16.840.1.101.3.4.1.24": "aes-192-cfb",
	"2.16.840.1.101.3.4.1.41": "aes-256-ecb",
	"2.16.840.1.101.3.4.1.42": "aes-256-cbc",
	"2.16.840.1.101.3.4.1.43": "aes-256-ofb",
	"2.16.840.1.101.3.4.1.44": "aes-256-cfb"
};

var fixProc;
var hasRequiredFixProc;

function requireFixProc () {
	if (hasRequiredFixProc) return fixProc;
	hasRequiredFixProc = 1;

	// adapted from https://github.com/apatil/pemstrip
	var findProc = /Proc-Type: 4,ENCRYPTED[\n\r]+DEK-Info: AES-((?:128)|(?:192)|(?:256))-CBC,([0-9A-H]+)[\n\r]+([0-9A-z\n\r+/=]+)[\n\r]+/m;
	var startRegex = /^-----BEGIN ((?:.*? KEY)|CERTIFICATE)-----/m;
	var fullRegex = /^-----BEGIN ((?:.*? KEY)|CERTIFICATE)-----([0-9A-z\n\r+/=]+)-----END \1-----$/m;
	var evp = requireEvp_bytestokey();
	var ciphers = requireBrowser$3();
	var Buffer = require$$0$2.Buffer;
	fixProc = function (okey, password) {
		var key = okey.toString();
		var match = key.match(findProc);
		var decrypted;
		if (!match) {
			var match2 = key.match(fullRegex);
			decrypted = Buffer.from(match2[2].replace(/[\r\n]/g, ''), 'base64');
		} else {
			var suite = 'aes' + match[1];
			var iv = Buffer.from(match[2], 'hex');
			var cipherText = Buffer.from(match[3].replace(/[\r\n]/g, ''), 'base64');
			var cipherKey = evp(password, iv.slice(0, 8), parseInt(match[1], 10)).key;
			var out = [];
			var cipher = ciphers.createDecipheriv(suite, cipherKey, iv);
			out.push(cipher.update(cipherText));
			out.push(cipher['final']());
			decrypted = Buffer.concat(out);
		}
		var tag = key.match(startRegex)[1];
		return {
			tag: tag,
			data: decrypted
		};
	};
	return fixProc;
}

var browser$1 = {};

var precondition;
var hasRequiredPrecondition;

function requirePrecondition () {
	if (hasRequiredPrecondition) return precondition;
	hasRequiredPrecondition = 1;
	var MAX_ALLOC = Math.pow(2, 30) - 1; // default in iojs

	precondition = function (iterations, keylen) {
	  if (typeof iterations !== 'number') {
	    throw new TypeError('Iterations not a number')
	  }

	  if (iterations < 0) {
	    throw new TypeError('Bad iterations')
	  }

	  if (typeof keylen !== 'number') {
	    throw new TypeError('Key length not a number')
	  }

	  if (keylen < 0 || keylen > MAX_ALLOC || keylen !== keylen) { /* eslint no-self-compare: 0 */
	    throw new TypeError('Bad key length')
	  }
	};
	return precondition;
}

var defaultEncoding_1;
var hasRequiredDefaultEncoding;

function requireDefaultEncoding () {
	if (hasRequiredDefaultEncoding) return defaultEncoding_1;
	hasRequiredDefaultEncoding = 1;
	var defaultEncoding;
	/* istanbul ignore next */
	if (commonjsGlobal.process && commonjsGlobal.process.browser) {
	  defaultEncoding = 'utf-8';
	} else if (commonjsGlobal.process && commonjsGlobal.process.version) {
	  var pVersionMajor = parseInt(process.version.split('.')[0].slice(1), 10);

	  defaultEncoding = pVersionMajor >= 6 ? 'utf-8' : 'binary';
	} else {
	  defaultEncoding = 'utf-8';
	}
	defaultEncoding_1 = defaultEncoding;
	return defaultEncoding_1;
}

var md5;
var hasRequiredMd5;

function requireMd5 () {
	if (hasRequiredMd5) return md5;
	hasRequiredMd5 = 1;
	var MD5 = requireMd5_js();

	md5 = function (buffer) {
	  return new MD5().update(buffer).digest()
	};
	return md5;
}

var ripemd160;
var hasRequiredRipemd160;

function requireRipemd160 () {
	if (hasRequiredRipemd160) return ripemd160;
	hasRequiredRipemd160 = 1;
	var Buffer = require$$0$4.Buffer;
	var inherits = require$$3;
	var HashBase = requireHashBase();

	var ARRAY16 = new Array(16);

	var zl = [
	  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
	  7, 4, 13, 1, 10, 6, 15, 3, 12, 0, 9, 5, 2, 14, 11, 8,
	  3, 10, 14, 4, 9, 15, 8, 1, 2, 7, 0, 6, 13, 11, 5, 12,
	  1, 9, 11, 10, 0, 8, 12, 4, 13, 3, 7, 15, 14, 5, 6, 2,
	  4, 0, 5, 9, 7, 12, 2, 10, 14, 1, 3, 8, 11, 6, 15, 13
	];

	var zr = [
	  5, 14, 7, 0, 9, 2, 11, 4, 13, 6, 15, 8, 1, 10, 3, 12,
	  6, 11, 3, 7, 0, 13, 5, 10, 14, 15, 8, 12, 4, 9, 1, 2,
	  15, 5, 1, 3, 7, 14, 6, 9, 11, 8, 12, 2, 10, 0, 4, 13,
	  8, 6, 4, 1, 3, 11, 15, 0, 5, 12, 2, 13, 9, 7, 10, 14,
	  12, 15, 10, 4, 1, 5, 8, 7, 6, 2, 13, 14, 0, 3, 9, 11
	];

	var sl = [
	  11, 14, 15, 12, 5, 8, 7, 9, 11, 13, 14, 15, 6, 7, 9, 8,
	  7, 6, 8, 13, 11, 9, 7, 15, 7, 12, 15, 9, 11, 7, 13, 12,
	  11, 13, 6, 7, 14, 9, 13, 15, 14, 8, 13, 6, 5, 12, 7, 5,
	  11, 12, 14, 15, 14, 15, 9, 8, 9, 14, 5, 6, 8, 6, 5, 12,
	  9, 15, 5, 11, 6, 8, 13, 12, 5, 12, 13, 14, 11, 8, 5, 6
	];

	var sr = [
	  8, 9, 9, 11, 13, 15, 15, 5, 7, 7, 8, 11, 14, 14, 12, 6,
	  9, 13, 15, 7, 12, 8, 9, 11, 7, 7, 12, 7, 6, 15, 13, 11,
	  9, 7, 15, 11, 8, 6, 6, 14, 12, 13, 5, 14, 13, 13, 7, 5,
	  15, 5, 8, 11, 14, 14, 6, 14, 6, 9, 12, 9, 12, 5, 15, 8,
	  8, 5, 12, 9, 12, 5, 14, 6, 8, 13, 6, 5, 15, 13, 11, 11
	];

	var hl = [0x00000000, 0x5a827999, 0x6ed9eba1, 0x8f1bbcdc, 0xa953fd4e];
	var hr = [0x50a28be6, 0x5c4dd124, 0x6d703ef3, 0x7a6d76e9, 0x00000000];

	function RIPEMD160 () {
	  HashBase.call(this, 64);

	  // state
	  this._a = 0x67452301;
	  this._b = 0xefcdab89;
	  this._c = 0x98badcfe;
	  this._d = 0x10325476;
	  this._e = 0xc3d2e1f0;
	}

	inherits(RIPEMD160, HashBase);

	RIPEMD160.prototype._update = function () {
	  var words = ARRAY16;
	  for (var j = 0; j < 16; ++j) words[j] = this._block.readInt32LE(j * 4);

	  var al = this._a | 0;
	  var bl = this._b | 0;
	  var cl = this._c | 0;
	  var dl = this._d | 0;
	  var el = this._e | 0;

	  var ar = this._a | 0;
	  var br = this._b | 0;
	  var cr = this._c | 0;
	  var dr = this._d | 0;
	  var er = this._e | 0;

	  // computation
	  for (var i = 0; i < 80; i += 1) {
	    var tl;
	    var tr;
	    if (i < 16) {
	      tl = fn1(al, bl, cl, dl, el, words[zl[i]], hl[0], sl[i]);
	      tr = fn5(ar, br, cr, dr, er, words[zr[i]], hr[0], sr[i]);
	    } else if (i < 32) {
	      tl = fn2(al, bl, cl, dl, el, words[zl[i]], hl[1], sl[i]);
	      tr = fn4(ar, br, cr, dr, er, words[zr[i]], hr[1], sr[i]);
	    } else if (i < 48) {
	      tl = fn3(al, bl, cl, dl, el, words[zl[i]], hl[2], sl[i]);
	      tr = fn3(ar, br, cr, dr, er, words[zr[i]], hr[2], sr[i]);
	    } else if (i < 64) {
	      tl = fn4(al, bl, cl, dl, el, words[zl[i]], hl[3], sl[i]);
	      tr = fn2(ar, br, cr, dr, er, words[zr[i]], hr[3], sr[i]);
	    } else { // if (i<80) {
	      tl = fn5(al, bl, cl, dl, el, words[zl[i]], hl[4], sl[i]);
	      tr = fn1(ar, br, cr, dr, er, words[zr[i]], hr[4], sr[i]);
	    }

	    al = el;
	    el = dl;
	    dl = rotl(cl, 10);
	    cl = bl;
	    bl = tl;

	    ar = er;
	    er = dr;
	    dr = rotl(cr, 10);
	    cr = br;
	    br = tr;
	  }

	  // update state
	  var t = (this._b + cl + dr) | 0;
	  this._b = (this._c + dl + er) | 0;
	  this._c = (this._d + el + ar) | 0;
	  this._d = (this._e + al + br) | 0;
	  this._e = (this._a + bl + cr) | 0;
	  this._a = t;
	};

	RIPEMD160.prototype._digest = function () {
	  // create padding and handle blocks
	  this._block[this._blockOffset++] = 0x80;
	  if (this._blockOffset > 56) {
	    this._block.fill(0, this._blockOffset, 64);
	    this._update();
	    this._blockOffset = 0;
	  }

	  this._block.fill(0, this._blockOffset, 56);
	  this._block.writeUInt32LE(this._length[0], 56);
	  this._block.writeUInt32LE(this._length[1], 60);
	  this._update();

	  // produce result
	  var buffer = Buffer.alloc ? Buffer.alloc(20) : new Buffer(20);
	  buffer.writeInt32LE(this._a, 0);
	  buffer.writeInt32LE(this._b, 4);
	  buffer.writeInt32LE(this._c, 8);
	  buffer.writeInt32LE(this._d, 12);
	  buffer.writeInt32LE(this._e, 16);
	  return buffer
	};

	function rotl (x, n) {
	  return (x << n) | (x >>> (32 - n))
	}

	function fn1 (a, b, c, d, e, m, k, s) {
	  return (rotl((a + (b ^ c ^ d) + m + k) | 0, s) + e) | 0
	}

	function fn2 (a, b, c, d, e, m, k, s) {
	  return (rotl((a + ((b & c) | ((~b) & d)) + m + k) | 0, s) + e) | 0
	}

	function fn3 (a, b, c, d, e, m, k, s) {
	  return (rotl((a + ((b | (~c)) ^ d) + m + k) | 0, s) + e) | 0
	}

	function fn4 (a, b, c, d, e, m, k, s) {
	  return (rotl((a + ((b & d) | (c & (~d))) + m + k) | 0, s) + e) | 0
	}

	function fn5 (a, b, c, d, e, m, k, s) {
	  return (rotl((a + (b ^ (c | (~d))) + m + k) | 0, s) + e) | 0
	}

	ripemd160 = RIPEMD160;
	return ripemd160;
}

var toBuffer;
var hasRequiredToBuffer;

function requireToBuffer () {
	if (hasRequiredToBuffer) return toBuffer;
	hasRequiredToBuffer = 1;
	var Buffer = require$$0$2.Buffer;

	toBuffer = function (thing, encoding, name) {
	  if (Buffer.isBuffer(thing)) {
	    return thing
	  } else if (typeof thing === 'string') {
	    return Buffer.from(thing, encoding)
	  } else if (ArrayBuffer.isView(thing)) {
	    return Buffer.from(thing.buffer)
	  } else {
	    throw new TypeError(name + ' must be a string, a Buffer, a typed array or a DataView')
	  }
	};
	return toBuffer;
}

var syncBrowser;
var hasRequiredSyncBrowser;

function requireSyncBrowser () {
	if (hasRequiredSyncBrowser) return syncBrowser;
	hasRequiredSyncBrowser = 1;
	var md5 = requireMd5();
	var RIPEMD160 = requireRipemd160();
	var sha = require$$2$2;
	var Buffer = require$$0$2.Buffer;

	var checkParameters = requirePrecondition();
	var defaultEncoding = requireDefaultEncoding();
	var toBuffer = requireToBuffer();

	var ZEROS = Buffer.alloc(128);
	var sizes = {
	  md5: 16,
	  sha1: 20,
	  sha224: 28,
	  sha256: 32,
	  sha384: 48,
	  sha512: 64,
	  rmd160: 20,
	  ripemd160: 20
	};

	function Hmac (alg, key, saltLen) {
	  var hash = getDigest(alg);
	  var blocksize = (alg === 'sha512' || alg === 'sha384') ? 128 : 64;

	  if (key.length > blocksize) {
	    key = hash(key);
	  } else if (key.length < blocksize) {
	    key = Buffer.concat([key, ZEROS], blocksize);
	  }

	  var ipad = Buffer.allocUnsafe(blocksize + sizes[alg]);
	  var opad = Buffer.allocUnsafe(blocksize + sizes[alg]);
	  for (var i = 0; i < blocksize; i++) {
	    ipad[i] = key[i] ^ 0x36;
	    opad[i] = key[i] ^ 0x5C;
	  }

	  var ipad1 = Buffer.allocUnsafe(blocksize + saltLen + 4);
	  ipad.copy(ipad1, 0, 0, blocksize);
	  this.ipad1 = ipad1;
	  this.ipad2 = ipad;
	  this.opad = opad;
	  this.alg = alg;
	  this.blocksize = blocksize;
	  this.hash = hash;
	  this.size = sizes[alg];
	}

	Hmac.prototype.run = function (data, ipad) {
	  data.copy(ipad, this.blocksize);
	  var h = this.hash(ipad);
	  h.copy(this.opad, this.blocksize);
	  return this.hash(this.opad)
	};

	function getDigest (alg) {
	  function shaFunc (data) {
	    return sha(alg).update(data).digest()
	  }
	  function rmd160Func (data) {
	    return new RIPEMD160().update(data).digest()
	  }

	  if (alg === 'rmd160' || alg === 'ripemd160') return rmd160Func
	  if (alg === 'md5') return md5
	  return shaFunc
	}

	function pbkdf2 (password, salt, iterations, keylen, digest) {
	  checkParameters(iterations, keylen);
	  password = toBuffer(password, defaultEncoding, 'Password');
	  salt = toBuffer(salt, defaultEncoding, 'Salt');

	  digest = digest || 'sha1';

	  var hmac = new Hmac(digest, password, salt.length);

	  var DK = Buffer.allocUnsafe(keylen);
	  var block1 = Buffer.allocUnsafe(salt.length + 4);
	  salt.copy(block1, 0, 0, salt.length);

	  var destPos = 0;
	  var hLen = sizes[digest];
	  var l = Math.ceil(keylen / hLen);

	  for (var i = 1; i <= l; i++) {
	    block1.writeUInt32BE(i, salt.length);

	    var T = hmac.run(block1, hmac.ipad1);
	    var U = T;

	    for (var j = 1; j < iterations; j++) {
	      U = hmac.run(U, hmac.ipad2);
	      for (var k = 0; k < hLen; k++) T[k] ^= U[k];
	    }

	    T.copy(DK, destPos);
	    destPos += hLen;
	  }

	  return DK
	}

	syncBrowser = pbkdf2;
	return syncBrowser;
}

var async;
var hasRequiredAsync;

function requireAsync () {
	if (hasRequiredAsync) return async;
	hasRequiredAsync = 1;
	var Buffer = require$$0$2.Buffer;

	var checkParameters = requirePrecondition();
	var defaultEncoding = requireDefaultEncoding();
	var sync = requireSyncBrowser();
	var toBuffer = requireToBuffer();

	var ZERO_BUF;
	var subtle = commonjsGlobal.crypto && commonjsGlobal.crypto.subtle;
	var toBrowser = {
	  sha: 'SHA-1',
	  'sha-1': 'SHA-1',
	  sha1: 'SHA-1',
	  sha256: 'SHA-256',
	  'sha-256': 'SHA-256',
	  sha384: 'SHA-384',
	  'sha-384': 'SHA-384',
	  'sha-512': 'SHA-512',
	  sha512: 'SHA-512'
	};
	var checks = [];
	function checkNative (algo) {
	  if (commonjsGlobal.process && !commonjsGlobal.process.browser) {
	    return Promise.resolve(false)
	  }
	  if (!subtle || !subtle.importKey || !subtle.deriveBits) {
	    return Promise.resolve(false)
	  }
	  if (checks[algo] !== undefined) {
	    return checks[algo]
	  }
	  ZERO_BUF = ZERO_BUF || Buffer.alloc(8);
	  var prom = browserPbkdf2(ZERO_BUF, ZERO_BUF, 10, 128, algo)
	    .then(function () {
	      return true
	    }).catch(function () {
	      return false
	    });
	  checks[algo] = prom;
	  return prom
	}
	var nextTick;
	function getNextTick () {
	  if (nextTick) {
	    return nextTick
	  }
	  if (commonjsGlobal.process && commonjsGlobal.process.nextTick) {
	    nextTick = commonjsGlobal.process.nextTick;
	  } else if (commonjsGlobal.queueMicrotask) {
	    nextTick = commonjsGlobal.queueMicrotask;
	  } else if (commonjsGlobal.setImmediate) {
	    nextTick = commonjsGlobal.setImmediate;
	  } else {
	    nextTick = commonjsGlobal.setTimeout;
	  }
	  return nextTick
	}
	function browserPbkdf2 (password, salt, iterations, length, algo) {
	  return subtle.importKey(
	    'raw', password, { name: 'PBKDF2' }, false, ['deriveBits']
	  ).then(function (key) {
	    return subtle.deriveBits({
	      name: 'PBKDF2',
	      salt: salt,
	      iterations: iterations,
	      hash: {
	        name: algo
	      }
	    }, key, length << 3)
	  }).then(function (res) {
	    return Buffer.from(res)
	  })
	}

	function resolvePromise (promise, callback) {
	  promise.then(function (out) {
	    getNextTick()(function () {
	      callback(null, out);
	    });
	  }, function (e) {
	    getNextTick()(function () {
	      callback(e);
	    });
	  });
	}
	async = function (password, salt, iterations, keylen, digest, callback) {
	  if (typeof digest === 'function') {
	    callback = digest;
	    digest = undefined;
	  }

	  digest = digest || 'sha1';
	  var algo = toBrowser[digest.toLowerCase()];

	  if (!algo || typeof commonjsGlobal.Promise !== 'function') {
	    getNextTick()(function () {
	      var out;
	      try {
	        out = sync(password, salt, iterations, keylen, digest);
	      } catch (e) {
	        return callback(e)
	      }
	      callback(null, out);
	    });
	    return
	  }

	  checkParameters(iterations, keylen);
	  password = toBuffer(password, defaultEncoding, 'Password');
	  salt = toBuffer(salt, defaultEncoding, 'Salt');
	  if (typeof callback !== 'function') throw new Error('No callback provided to pbkdf2')

	  resolvePromise(checkNative(algo).then(function (resp) {
	    if (resp) return browserPbkdf2(password, salt, iterations, keylen, algo)

	    return sync(password, salt, iterations, keylen, digest)
	  }), callback);
	};
	return async;
}

var hasRequiredBrowser$2;

function requireBrowser$2 () {
	if (hasRequiredBrowser$2) return browser$1;
	hasRequiredBrowser$2 = 1;
	browser$1.pbkdf2 = requireAsync();
	browser$1.pbkdf2Sync = requireSyncBrowser();
	return browser$1;
}

var parseAsn1;
var hasRequiredParseAsn1;

function requireParseAsn1 () {
	if (hasRequiredParseAsn1) return parseAsn1;
	hasRequiredParseAsn1 = 1;

	var asn1 = requireAsn1();
	var aesid = require$$1$1;
	var fixProc = requireFixProc();
	var ciphers = requireBrowser$3();
	var compat = requireBrowser$2();
	var Buffer = require$$0$2.Buffer;

	function decrypt(data, password) {
		var salt = data.algorithm.decrypt.kde.kdeparams.salt;
		var iters = parseInt(data.algorithm.decrypt.kde.kdeparams.iters.toString(), 10);
		var algo = aesid[data.algorithm.decrypt.cipher.algo.join('.')];
		var iv = data.algorithm.decrypt.cipher.iv;
		var cipherText = data.subjectPrivateKey;
		var keylen = parseInt(algo.split('-')[1], 10) / 8;
		var key = compat.pbkdf2Sync(password, salt, iters, keylen, 'sha1');
		var cipher = ciphers.createDecipheriv(algo, key, iv);
		var out = [];
		out.push(cipher.update(cipherText));
		out.push(cipher['final']());
		return Buffer.concat(out);
	}

	function parseKeys(buffer) {
		var password;
		if (typeof buffer === 'object' && !Buffer.isBuffer(buffer)) {
			password = buffer.passphrase;
			buffer = buffer.key;
		}
		if (typeof buffer === 'string') {
			buffer = Buffer.from(buffer);
		}

		var stripped = fixProc(buffer, password);

		var type = stripped.tag;
		var data = stripped.data;
		var subtype, ndata;
		switch (type) {
			case 'CERTIFICATE':
				ndata = asn1.certificate.decode(data, 'der').tbsCertificate.subjectPublicKeyInfo;
				// falls through
			case 'PUBLIC KEY':
				if (!ndata) {
					ndata = asn1.PublicKey.decode(data, 'der');
				}
				subtype = ndata.algorithm.algorithm.join('.');
				switch (subtype) {
					case '1.2.840.113549.1.1.1':
						return asn1.RSAPublicKey.decode(ndata.subjectPublicKey.data, 'der');
					case '1.2.840.10045.2.1':
						ndata.subjectPrivateKey = ndata.subjectPublicKey;
						return {
							type: 'ec',
							data: ndata
						};
					case '1.2.840.10040.4.1':
						ndata.algorithm.params.pub_key = asn1.DSAparam.decode(ndata.subjectPublicKey.data, 'der');
						return {
							type: 'dsa',
							data: ndata.algorithm.params
						};
					default: throw new Error('unknown key id ' + subtype);
				}
				// throw new Error('unknown key type ' + type)
			case 'ENCRYPTED PRIVATE KEY':
				data = asn1.EncryptedPrivateKey.decode(data, 'der');
				data = decrypt(data, password);
				// falls through
			case 'PRIVATE KEY':
				ndata = asn1.PrivateKey.decode(data, 'der');
				subtype = ndata.algorithm.algorithm.join('.');
				switch (subtype) {
					case '1.2.840.113549.1.1.1':
						return asn1.RSAPrivateKey.decode(ndata.subjectPrivateKey, 'der');
					case '1.2.840.10045.2.1':
						return {
							curve: ndata.algorithm.curve,
							privateKey: asn1.ECPrivateKey.decode(ndata.subjectPrivateKey, 'der').privateKey
						};
					case '1.2.840.10040.4.1':
						ndata.algorithm.params.priv_key = asn1.DSAparam.decode(ndata.subjectPrivateKey, 'der');
						return {
							type: 'dsa',
							params: ndata.algorithm.params
						};
					default: throw new Error('unknown key id ' + subtype);
				}
				// throw new Error('unknown key type ' + type)
			case 'RSA PUBLIC KEY':
				return asn1.RSAPublicKey.decode(data, 'der');
			case 'RSA PRIVATE KEY':
				return asn1.RSAPrivateKey.decode(data, 'der');
			case 'DSA PRIVATE KEY':
				return {
					type: 'dsa',
					params: asn1.DSAPrivateKey.decode(data, 'der')
				};
			case 'EC PRIVATE KEY':
				data = asn1.ECPrivateKey.decode(data, 'der');
				return {
					curve: data.parameters.value,
					privateKey: data.privateKey
				};
			default: throw new Error('unknown key type ' + type);
		}
	}
	parseKeys.signature = asn1.signature;

	parseAsn1 = parseKeys;
	return parseAsn1;
}

var randombytes = /*#__PURE__*/Object.freeze({
    __proto__: null,
    default: randomBytes
});

var require$$1 = /*@__PURE__*/getAugmentedNamespace(randombytes);

var browser;
var hasRequiredBrowser$1;

function requireBrowser$1 () {
	if (hasRequiredBrowser$1) return browser;
	hasRequiredBrowser$1 = 1;
	var inherits = require$$3;
	var MD5 = requireMd5_js();
	var RIPEMD160 = requireRipemd160();
	var sha = require$$2$2;
	var Base = requireCipherBase();

	function Hash (hash) {
	  Base.call(this, 'digest');

	  this._hash = hash;
	}

	inherits(Hash, Base);

	Hash.prototype._update = function (data) {
	  this._hash.update(data);
	};

	Hash.prototype._final = function () {
	  return this._hash.digest()
	};

	browser = function createHash (alg) {
	  alg = alg.toLowerCase();
	  if (alg === 'md5') return new MD5()
	  if (alg === 'rmd160' || alg === 'ripemd160') return new RIPEMD160()

	  return new Hash(sha(alg))
	};
	return browser;
}

var mgf;
var hasRequiredMgf;

function requireMgf () {
	if (hasRequiredMgf) return mgf;
	hasRequiredMgf = 1;
	var createHash = requireBrowser$1();
	var Buffer = require$$0$2.Buffer;

	mgf = function (seed, len) {
	  var t = Buffer.alloc(0);
	  var i = 0;
	  var c;
	  while (t.length < len) {
	    c = i2ops(i++);
	    t = Buffer.concat([t, createHash('sha1').update(seed).update(c).digest()]);
	  }
	  return t.slice(0, len)
	};

	function i2ops (c) {
	  var out = Buffer.allocUnsafe(4);
	  out.writeUInt32BE(c, 0);
	  return out
	}
	return mgf;
}

var xor;
var hasRequiredXor;

function requireXor () {
	if (hasRequiredXor) return xor;
	hasRequiredXor = 1;
	xor = function xor (a, b) {
	  var len = a.length;
	  var i = -1;
	  while (++i < len) {
	    a[i] ^= b[i];
	  }
	  return a
	};
	return xor;
}

var bn$1 = {exports: {}};

var _nodeResolve_empty = {};

var _nodeResolve_empty$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    default: _nodeResolve_empty
});

var require$$0 = /*@__PURE__*/getAugmentedNamespace(_nodeResolve_empty$1);

var bn = bn$1.exports;

var hasRequiredBn;

function requireBn () {
	if (hasRequiredBn) return bn$1.exports;
	hasRequiredBn = 1;
	(function (module) {
		(function (module, exports$1) {

		  // Utils
		  function assert (val, msg) {
		    if (!val) throw new Error(msg || 'Assertion failed');
		  }

		  // Could use `inherits` module, but don't want to move from single file
		  // architecture yet.
		  function inherits (ctor, superCtor) {
		    ctor.super_ = superCtor;
		    var TempCtor = function () {};
		    TempCtor.prototype = superCtor.prototype;
		    ctor.prototype = new TempCtor();
		    ctor.prototype.constructor = ctor;
		  }

		  // BN

		  function BN (number, base, endian) {
		    if (BN.isBN(number)) {
		      return number;
		    }

		    this.negative = 0;
		    this.words = null;
		    this.length = 0;

		    // Reduction context
		    this.red = null;

		    if (number !== null) {
		      if (base === 'le' || base === 'be') {
		        endian = base;
		        base = 10;
		      }

		      this._init(number || 0, base || 10, endian || 'be');
		    }
		  }
		  if (typeof module === 'object') {
		    module.exports = BN;
		  } else {
		    exports$1.BN = BN;
		  }

		  BN.BN = BN;
		  BN.wordSize = 26;

		  var Buffer;
		  try {
		    if (typeof window !== 'undefined' && typeof window.Buffer !== 'undefined') {
		      Buffer = window.Buffer;
		    } else {
		      Buffer = require$$0.Buffer;
		    }
		  } catch (e) {
		  }

		  BN.isBN = function isBN (num) {
		    if (num instanceof BN) {
		      return true;
		    }

		    return num !== null && typeof num === 'object' &&
		      num.constructor.wordSize === BN.wordSize && Array.isArray(num.words);
		  };

		  BN.max = function max (left, right) {
		    if (left.cmp(right) > 0) return left;
		    return right;
		  };

		  BN.min = function min (left, right) {
		    if (left.cmp(right) < 0) return left;
		    return right;
		  };

		  BN.prototype._init = function init (number, base, endian) {
		    if (typeof number === 'number') {
		      return this._initNumber(number, base, endian);
		    }

		    if (typeof number === 'object') {
		      return this._initArray(number, base, endian);
		    }

		    if (base === 'hex') {
		      base = 16;
		    }
		    assert(base === (base | 0) && base >= 2 && base <= 36);

		    number = number.toString().replace(/\s+/g, '');
		    var start = 0;
		    if (number[0] === '-') {
		      start++;
		      this.negative = 1;
		    }

		    if (start < number.length) {
		      if (base === 16) {
		        this._parseHex(number, start, endian);
		      } else {
		        this._parseBase(number, base, start);
		        if (endian === 'le') {
		          this._initArray(this.toArray(), base, endian);
		        }
		      }
		    }
		  };

		  BN.prototype._initNumber = function _initNumber (number, base, endian) {
		    if (number < 0) {
		      this.negative = 1;
		      number = -number;
		    }
		    if (number < 0x4000000) {
		      this.words = [ number & 0x3ffffff ];
		      this.length = 1;
		    } else if (number < 0x10000000000000) {
		      this.words = [
		        number & 0x3ffffff,
		        (number / 0x4000000) & 0x3ffffff
		      ];
		      this.length = 2;
		    } else {
		      assert(number < 0x20000000000000); // 2 ^ 53 (unsafe)
		      this.words = [
		        number & 0x3ffffff,
		        (number / 0x4000000) & 0x3ffffff,
		        1
		      ];
		      this.length = 3;
		    }

		    if (endian !== 'le') return;

		    // Reverse the bytes
		    this._initArray(this.toArray(), base, endian);
		  };

		  BN.prototype._initArray = function _initArray (number, base, endian) {
		    // Perhaps a Uint8Array
		    assert(typeof number.length === 'number');
		    if (number.length <= 0) {
		      this.words = [ 0 ];
		      this.length = 1;
		      return this;
		    }

		    this.length = Math.ceil(number.length / 3);
		    this.words = new Array(this.length);
		    for (var i = 0; i < this.length; i++) {
		      this.words[i] = 0;
		    }

		    var j, w;
		    var off = 0;
		    if (endian === 'be') {
		      for (i = number.length - 1, j = 0; i >= 0; i -= 3) {
		        w = number[i] | (number[i - 1] << 8) | (number[i - 2] << 16);
		        this.words[j] |= (w << off) & 0x3ffffff;
		        this.words[j + 1] = (w >>> (26 - off)) & 0x3ffffff;
		        off += 24;
		        if (off >= 26) {
		          off -= 26;
		          j++;
		        }
		      }
		    } else if (endian === 'le') {
		      for (i = 0, j = 0; i < number.length; i += 3) {
		        w = number[i] | (number[i + 1] << 8) | (number[i + 2] << 16);
		        this.words[j] |= (w << off) & 0x3ffffff;
		        this.words[j + 1] = (w >>> (26 - off)) & 0x3ffffff;
		        off += 24;
		        if (off >= 26) {
		          off -= 26;
		          j++;
		        }
		      }
		    }
		    return this.strip();
		  };

		  function parseHex4Bits (string, index) {
		    var c = string.charCodeAt(index);
		    // 'A' - 'F'
		    if (c >= 65 && c <= 70) {
		      return c - 55;
		    // 'a' - 'f'
		    } else if (c >= 97 && c <= 102) {
		      return c - 87;
		    // '0' - '9'
		    } else {
		      return (c - 48) & 0xf;
		    }
		  }

		  function parseHexByte (string, lowerBound, index) {
		    var r = parseHex4Bits(string, index);
		    if (index - 1 >= lowerBound) {
		      r |= parseHex4Bits(string, index - 1) << 4;
		    }
		    return r;
		  }

		  BN.prototype._parseHex = function _parseHex (number, start, endian) {
		    // Create possibly bigger array to ensure that it fits the number
		    this.length = Math.ceil((number.length - start) / 6);
		    this.words = new Array(this.length);
		    for (var i = 0; i < this.length; i++) {
		      this.words[i] = 0;
		    }

		    // 24-bits chunks
		    var off = 0;
		    var j = 0;

		    var w;
		    if (endian === 'be') {
		      for (i = number.length - 1; i >= start; i -= 2) {
		        w = parseHexByte(number, start, i) << off;
		        this.words[j] |= w & 0x3ffffff;
		        if (off >= 18) {
		          off -= 18;
		          j += 1;
		          this.words[j] |= w >>> 26;
		        } else {
		          off += 8;
		        }
		      }
		    } else {
		      var parseLength = number.length - start;
		      for (i = parseLength % 2 === 0 ? start + 1 : start; i < number.length; i += 2) {
		        w = parseHexByte(number, start, i) << off;
		        this.words[j] |= w & 0x3ffffff;
		        if (off >= 18) {
		          off -= 18;
		          j += 1;
		          this.words[j] |= w >>> 26;
		        } else {
		          off += 8;
		        }
		      }
		    }

		    this.strip();
		  };

		  function parseBase (str, start, end, mul) {
		    var r = 0;
		    var len = Math.min(str.length, end);
		    for (var i = start; i < len; i++) {
		      var c = str.charCodeAt(i) - 48;

		      r *= mul;

		      // 'a'
		      if (c >= 49) {
		        r += c - 49 + 0xa;

		      // 'A'
		      } else if (c >= 17) {
		        r += c - 17 + 0xa;

		      // '0' - '9'
		      } else {
		        r += c;
		      }
		    }
		    return r;
		  }

		  BN.prototype._parseBase = function _parseBase (number, base, start) {
		    // Initialize as zero
		    this.words = [ 0 ];
		    this.length = 1;

		    // Find length of limb in base
		    for (var limbLen = 0, limbPow = 1; limbPow <= 0x3ffffff; limbPow *= base) {
		      limbLen++;
		    }
		    limbLen--;
		    limbPow = (limbPow / base) | 0;

		    var total = number.length - start;
		    var mod = total % limbLen;
		    var end = Math.min(total, total - mod) + start;

		    var word = 0;
		    for (var i = start; i < end; i += limbLen) {
		      word = parseBase(number, i, i + limbLen, base);

		      this.imuln(limbPow);
		      if (this.words[0] + word < 0x4000000) {
		        this.words[0] += word;
		      } else {
		        this._iaddn(word);
		      }
		    }

		    if (mod !== 0) {
		      var pow = 1;
		      word = parseBase(number, i, number.length, base);

		      for (i = 0; i < mod; i++) {
		        pow *= base;
		      }

		      this.imuln(pow);
		      if (this.words[0] + word < 0x4000000) {
		        this.words[0] += word;
		      } else {
		        this._iaddn(word);
		      }
		    }

		    this.strip();
		  };

		  BN.prototype.copy = function copy (dest) {
		    dest.words = new Array(this.length);
		    for (var i = 0; i < this.length; i++) {
		      dest.words[i] = this.words[i];
		    }
		    dest.length = this.length;
		    dest.negative = this.negative;
		    dest.red = this.red;
		  };

		  BN.prototype.clone = function clone () {
		    var r = new BN(null);
		    this.copy(r);
		    return r;
		  };

		  BN.prototype._expand = function _expand (size) {
		    while (this.length < size) {
		      this.words[this.length++] = 0;
		    }
		    return this;
		  };

		  // Remove leading `0` from `this`
		  BN.prototype.strip = function strip () {
		    while (this.length > 1 && this.words[this.length - 1] === 0) {
		      this.length--;
		    }
		    return this._normSign();
		  };

		  BN.prototype._normSign = function _normSign () {
		    // -0 = 0
		    if (this.length === 1 && this.words[0] === 0) {
		      this.negative = 0;
		    }
		    return this;
		  };

		  BN.prototype.inspect = function inspect () {
		    return (this.red ? '<BN-R: ' : '<BN: ') + this.toString(16) + '>';
		  };

		  /*

		  var zeros = [];
		  var groupSizes = [];
		  var groupBases = [];

		  var s = '';
		  var i = -1;
		  while (++i < BN.wordSize) {
		    zeros[i] = s;
		    s += '0';
		  }
		  groupSizes[0] = 0;
		  groupSizes[1] = 0;
		  groupBases[0] = 0;
		  groupBases[1] = 0;
		  var base = 2 - 1;
		  while (++base < 36 + 1) {
		    var groupSize = 0;
		    var groupBase = 1;
		    while (groupBase < (1 << BN.wordSize) / base) {
		      groupBase *= base;
		      groupSize += 1;
		    }
		    groupSizes[base] = groupSize;
		    groupBases[base] = groupBase;
		  }

		  */

		  var zeros = [
		    '',
		    '0',
		    '00',
		    '000',
		    '0000',
		    '00000',
		    '000000',
		    '0000000',
		    '00000000',
		    '000000000',
		    '0000000000',
		    '00000000000',
		    '000000000000',
		    '0000000000000',
		    '00000000000000',
		    '000000000000000',
		    '0000000000000000',
		    '00000000000000000',
		    '000000000000000000',
		    '0000000000000000000',
		    '00000000000000000000',
		    '000000000000000000000',
		    '0000000000000000000000',
		    '00000000000000000000000',
		    '000000000000000000000000',
		    '0000000000000000000000000'
		  ];

		  var groupSizes = [
		    0, 0,
		    25, 16, 12, 11, 10, 9, 8,
		    8, 7, 7, 7, 7, 6, 6,
		    6, 6, 6, 6, 6, 5, 5,
		    5, 5, 5, 5, 5, 5, 5,
		    5, 5, 5, 5, 5, 5, 5
		  ];

		  var groupBases = [
		    0, 0,
		    33554432, 43046721, 16777216, 48828125, 60466176, 40353607, 16777216,
		    43046721, 10000000, 19487171, 35831808, 62748517, 7529536, 11390625,
		    16777216, 24137569, 34012224, 47045881, 64000000, 4084101, 5153632,
		    6436343, 7962624, 9765625, 11881376, 14348907, 17210368, 20511149,
		    24300000, 28629151, 33554432, 39135393, 45435424, 52521875, 60466176
		  ];

		  BN.prototype.toString = function toString (base, padding) {
		    base = base || 10;
		    padding = padding | 0 || 1;

		    var out;
		    if (base === 16 || base === 'hex') {
		      out = '';
		      var off = 0;
		      var carry = 0;
		      for (var i = 0; i < this.length; i++) {
		        var w = this.words[i];
		        var word = (((w << off) | carry) & 0xffffff).toString(16);
		        carry = (w >>> (24 - off)) & 0xffffff;
		        off += 2;
		        if (off >= 26) {
		          off -= 26;
		          i--;
		        }
		        if (carry !== 0 || i !== this.length - 1) {
		          out = zeros[6 - word.length] + word + out;
		        } else {
		          out = word + out;
		        }
		      }
		      if (carry !== 0) {
		        out = carry.toString(16) + out;
		      }
		      while (out.length % padding !== 0) {
		        out = '0' + out;
		      }
		      if (this.negative !== 0) {
		        out = '-' + out;
		      }
		      return out;
		    }

		    if (base === (base | 0) && base >= 2 && base <= 36) {
		      // var groupSize = Math.floor(BN.wordSize * Math.LN2 / Math.log(base));
		      var groupSize = groupSizes[base];
		      // var groupBase = Math.pow(base, groupSize);
		      var groupBase = groupBases[base];
		      out = '';
		      var c = this.clone();
		      c.negative = 0;
		      while (!c.isZero()) {
		        var r = c.modn(groupBase).toString(base);
		        c = c.idivn(groupBase);

		        if (!c.isZero()) {
		          out = zeros[groupSize - r.length] + r + out;
		        } else {
		          out = r + out;
		        }
		      }
		      if (this.isZero()) {
		        out = '0' + out;
		      }
		      while (out.length % padding !== 0) {
		        out = '0' + out;
		      }
		      if (this.negative !== 0) {
		        out = '-' + out;
		      }
		      return out;
		    }

		    assert(false, 'Base should be between 2 and 36');
		  };

		  BN.prototype.toNumber = function toNumber () {
		    var ret = this.words[0];
		    if (this.length === 2) {
		      ret += this.words[1] * 0x4000000;
		    } else if (this.length === 3 && this.words[2] === 0x01) {
		      // NOTE: at this stage it is known that the top bit is set
		      ret += 0x10000000000000 + (this.words[1] * 0x4000000);
		    } else if (this.length > 2) {
		      assert(false, 'Number can only safely store up to 53 bits');
		    }
		    return (this.negative !== 0) ? -ret : ret;
		  };

		  BN.prototype.toJSON = function toJSON () {
		    return this.toString(16);
		  };

		  BN.prototype.toBuffer = function toBuffer (endian, length) {
		    assert(typeof Buffer !== 'undefined');
		    return this.toArrayLike(Buffer, endian, length);
		  };

		  BN.prototype.toArray = function toArray (endian, length) {
		    return this.toArrayLike(Array, endian, length);
		  };

		  BN.prototype.toArrayLike = function toArrayLike (ArrayType, endian, length) {
		    var byteLength = this.byteLength();
		    var reqLength = length || Math.max(1, byteLength);
		    assert(byteLength <= reqLength, 'byte array longer than desired length');
		    assert(reqLength > 0, 'Requested array length <= 0');

		    this.strip();
		    var littleEndian = endian === 'le';
		    var res = new ArrayType(reqLength);

		    var b, i;
		    var q = this.clone();
		    if (!littleEndian) {
		      // Assume big-endian
		      for (i = 0; i < reqLength - byteLength; i++) {
		        res[i] = 0;
		      }

		      for (i = 0; !q.isZero(); i++) {
		        b = q.andln(0xff);
		        q.iushrn(8);

		        res[reqLength - i - 1] = b;
		      }
		    } else {
		      for (i = 0; !q.isZero(); i++) {
		        b = q.andln(0xff);
		        q.iushrn(8);

		        res[i] = b;
		      }

		      for (; i < reqLength; i++) {
		        res[i] = 0;
		      }
		    }

		    return res;
		  };

		  if (Math.clz32) {
		    BN.prototype._countBits = function _countBits (w) {
		      return 32 - Math.clz32(w);
		    };
		  } else {
		    BN.prototype._countBits = function _countBits (w) {
		      var t = w;
		      var r = 0;
		      if (t >= 0x1000) {
		        r += 13;
		        t >>>= 13;
		      }
		      if (t >= 0x40) {
		        r += 7;
		        t >>>= 7;
		      }
		      if (t >= 0x8) {
		        r += 4;
		        t >>>= 4;
		      }
		      if (t >= 0x02) {
		        r += 2;
		        t >>>= 2;
		      }
		      return r + t;
		    };
		  }

		  BN.prototype._zeroBits = function _zeroBits (w) {
		    // Short-cut
		    if (w === 0) return 26;

		    var t = w;
		    var r = 0;
		    if ((t & 0x1fff) === 0) {
		      r += 13;
		      t >>>= 13;
		    }
		    if ((t & 0x7f) === 0) {
		      r += 7;
		      t >>>= 7;
		    }
		    if ((t & 0xf) === 0) {
		      r += 4;
		      t >>>= 4;
		    }
		    if ((t & 0x3) === 0) {
		      r += 2;
		      t >>>= 2;
		    }
		    if ((t & 0x1) === 0) {
		      r++;
		    }
		    return r;
		  };

		  // Return number of used bits in a BN
		  BN.prototype.bitLength = function bitLength () {
		    var w = this.words[this.length - 1];
		    var hi = this._countBits(w);
		    return (this.length - 1) * 26 + hi;
		  };

		  function toBitArray (num) {
		    var w = new Array(num.bitLength());

		    for (var bit = 0; bit < w.length; bit++) {
		      var off = (bit / 26) | 0;
		      var wbit = bit % 26;

		      w[bit] = (num.words[off] & (1 << wbit)) >>> wbit;
		    }

		    return w;
		  }

		  // Number of trailing zero bits
		  BN.prototype.zeroBits = function zeroBits () {
		    if (this.isZero()) return 0;

		    var r = 0;
		    for (var i = 0; i < this.length; i++) {
		      var b = this._zeroBits(this.words[i]);
		      r += b;
		      if (b !== 26) break;
		    }
		    return r;
		  };

		  BN.prototype.byteLength = function byteLength () {
		    return Math.ceil(this.bitLength() / 8);
		  };

		  BN.prototype.toTwos = function toTwos (width) {
		    if (this.negative !== 0) {
		      return this.abs().inotn(width).iaddn(1);
		    }
		    return this.clone();
		  };

		  BN.prototype.fromTwos = function fromTwos (width) {
		    if (this.testn(width - 1)) {
		      return this.notn(width).iaddn(1).ineg();
		    }
		    return this.clone();
		  };

		  BN.prototype.isNeg = function isNeg () {
		    return this.negative !== 0;
		  };

		  // Return negative clone of `this`
		  BN.prototype.neg = function neg () {
		    return this.clone().ineg();
		  };

		  BN.prototype.ineg = function ineg () {
		    if (!this.isZero()) {
		      this.negative ^= 1;
		    }

		    return this;
		  };

		  // Or `num` with `this` in-place
		  BN.prototype.iuor = function iuor (num) {
		    while (this.length < num.length) {
		      this.words[this.length++] = 0;
		    }

		    for (var i = 0; i < num.length; i++) {
		      this.words[i] = this.words[i] | num.words[i];
		    }

		    return this.strip();
		  };

		  BN.prototype.ior = function ior (num) {
		    assert((this.negative | num.negative) === 0);
		    return this.iuor(num);
		  };

		  // Or `num` with `this`
		  BN.prototype.or = function or (num) {
		    if (this.length > num.length) return this.clone().ior(num);
		    return num.clone().ior(this);
		  };

		  BN.prototype.uor = function uor (num) {
		    if (this.length > num.length) return this.clone().iuor(num);
		    return num.clone().iuor(this);
		  };

		  // And `num` with `this` in-place
		  BN.prototype.iuand = function iuand (num) {
		    // b = min-length(num, this)
		    var b;
		    if (this.length > num.length) {
		      b = num;
		    } else {
		      b = this;
		    }

		    for (var i = 0; i < b.length; i++) {
		      this.words[i] = this.words[i] & num.words[i];
		    }

		    this.length = b.length;

		    return this.strip();
		  };

		  BN.prototype.iand = function iand (num) {
		    assert((this.negative | num.negative) === 0);
		    return this.iuand(num);
		  };

		  // And `num` with `this`
		  BN.prototype.and = function and (num) {
		    if (this.length > num.length) return this.clone().iand(num);
		    return num.clone().iand(this);
		  };

		  BN.prototype.uand = function uand (num) {
		    if (this.length > num.length) return this.clone().iuand(num);
		    return num.clone().iuand(this);
		  };

		  // Xor `num` with `this` in-place
		  BN.prototype.iuxor = function iuxor (num) {
		    // a.length > b.length
		    var a;
		    var b;
		    if (this.length > num.length) {
		      a = this;
		      b = num;
		    } else {
		      a = num;
		      b = this;
		    }

		    for (var i = 0; i < b.length; i++) {
		      this.words[i] = a.words[i] ^ b.words[i];
		    }

		    if (this !== a) {
		      for (; i < a.length; i++) {
		        this.words[i] = a.words[i];
		      }
		    }

		    this.length = a.length;

		    return this.strip();
		  };

		  BN.prototype.ixor = function ixor (num) {
		    assert((this.negative | num.negative) === 0);
		    return this.iuxor(num);
		  };

		  // Xor `num` with `this`
		  BN.prototype.xor = function xor (num) {
		    if (this.length > num.length) return this.clone().ixor(num);
		    return num.clone().ixor(this);
		  };

		  BN.prototype.uxor = function uxor (num) {
		    if (this.length > num.length) return this.clone().iuxor(num);
		    return num.clone().iuxor(this);
		  };

		  // Not ``this`` with ``width`` bitwidth
		  BN.prototype.inotn = function inotn (width) {
		    assert(typeof width === 'number' && width >= 0);

		    var bytesNeeded = Math.ceil(width / 26) | 0;
		    var bitsLeft = width % 26;

		    // Extend the buffer with leading zeroes
		    this._expand(bytesNeeded);

		    if (bitsLeft > 0) {
		      bytesNeeded--;
		    }

		    // Handle complete words
		    for (var i = 0; i < bytesNeeded; i++) {
		      this.words[i] = ~this.words[i] & 0x3ffffff;
		    }

		    // Handle the residue
		    if (bitsLeft > 0) {
		      this.words[i] = ~this.words[i] & (0x3ffffff >> (26 - bitsLeft));
		    }

		    // And remove leading zeroes
		    return this.strip();
		  };

		  BN.prototype.notn = function notn (width) {
		    return this.clone().inotn(width);
		  };

		  // Set `bit` of `this`
		  BN.prototype.setn = function setn (bit, val) {
		    assert(typeof bit === 'number' && bit >= 0);

		    var off = (bit / 26) | 0;
		    var wbit = bit % 26;

		    this._expand(off + 1);

		    if (val) {
		      this.words[off] = this.words[off] | (1 << wbit);
		    } else {
		      this.words[off] = this.words[off] & ~(1 << wbit);
		    }

		    return this.strip();
		  };

		  // Add `num` to `this` in-place
		  BN.prototype.iadd = function iadd (num) {
		    var r;

		    // negative + positive
		    if (this.negative !== 0 && num.negative === 0) {
		      this.negative = 0;
		      r = this.isub(num);
		      this.negative ^= 1;
		      return this._normSign();

		    // positive + negative
		    } else if (this.negative === 0 && num.negative !== 0) {
		      num.negative = 0;
		      r = this.isub(num);
		      num.negative = 1;
		      return r._normSign();
		    }

		    // a.length > b.length
		    var a, b;
		    if (this.length > num.length) {
		      a = this;
		      b = num;
		    } else {
		      a = num;
		      b = this;
		    }

		    var carry = 0;
		    for (var i = 0; i < b.length; i++) {
		      r = (a.words[i] | 0) + (b.words[i] | 0) + carry;
		      this.words[i] = r & 0x3ffffff;
		      carry = r >>> 26;
		    }
		    for (; carry !== 0 && i < a.length; i++) {
		      r = (a.words[i] | 0) + carry;
		      this.words[i] = r & 0x3ffffff;
		      carry = r >>> 26;
		    }

		    this.length = a.length;
		    if (carry !== 0) {
		      this.words[this.length] = carry;
		      this.length++;
		    // Copy the rest of the words
		    } else if (a !== this) {
		      for (; i < a.length; i++) {
		        this.words[i] = a.words[i];
		      }
		    }

		    return this;
		  };

		  // Add `num` to `this`
		  BN.prototype.add = function add (num) {
		    var res;
		    if (num.negative !== 0 && this.negative === 0) {
		      num.negative = 0;
		      res = this.sub(num);
		      num.negative ^= 1;
		      return res;
		    } else if (num.negative === 0 && this.negative !== 0) {
		      this.negative = 0;
		      res = num.sub(this);
		      this.negative = 1;
		      return res;
		    }

		    if (this.length > num.length) return this.clone().iadd(num);

		    return num.clone().iadd(this);
		  };

		  // Subtract `num` from `this` in-place
		  BN.prototype.isub = function isub (num) {
		    // this - (-num) = this + num
		    if (num.negative !== 0) {
		      num.negative = 0;
		      var r = this.iadd(num);
		      num.negative = 1;
		      return r._normSign();

		    // -this - num = -(this + num)
		    } else if (this.negative !== 0) {
		      this.negative = 0;
		      this.iadd(num);
		      this.negative = 1;
		      return this._normSign();
		    }

		    // At this point both numbers are positive
		    var cmp = this.cmp(num);

		    // Optimization - zeroify
		    if (cmp === 0) {
		      this.negative = 0;
		      this.length = 1;
		      this.words[0] = 0;
		      return this;
		    }

		    // a > b
		    var a, b;
		    if (cmp > 0) {
		      a = this;
		      b = num;
		    } else {
		      a = num;
		      b = this;
		    }

		    var carry = 0;
		    for (var i = 0; i < b.length; i++) {
		      r = (a.words[i] | 0) - (b.words[i] | 0) + carry;
		      carry = r >> 26;
		      this.words[i] = r & 0x3ffffff;
		    }
		    for (; carry !== 0 && i < a.length; i++) {
		      r = (a.words[i] | 0) + carry;
		      carry = r >> 26;
		      this.words[i] = r & 0x3ffffff;
		    }

		    // Copy rest of the words
		    if (carry === 0 && i < a.length && a !== this) {
		      for (; i < a.length; i++) {
		        this.words[i] = a.words[i];
		      }
		    }

		    this.length = Math.max(this.length, i);

		    if (a !== this) {
		      this.negative = 1;
		    }

		    return this.strip();
		  };

		  // Subtract `num` from `this`
		  BN.prototype.sub = function sub (num) {
		    return this.clone().isub(num);
		  };

		  function smallMulTo (self, num, out) {
		    out.negative = num.negative ^ self.negative;
		    var len = (self.length + num.length) | 0;
		    out.length = len;
		    len = (len - 1) | 0;

		    // Peel one iteration (compiler can't do it, because of code complexity)
		    var a = self.words[0] | 0;
		    var b = num.words[0] | 0;
		    var r = a * b;

		    var lo = r & 0x3ffffff;
		    var carry = (r / 0x4000000) | 0;
		    out.words[0] = lo;

		    for (var k = 1; k < len; k++) {
		      // Sum all words with the same `i + j = k` and accumulate `ncarry`,
		      // note that ncarry could be >= 0x3ffffff
		      var ncarry = carry >>> 26;
		      var rword = carry & 0x3ffffff;
		      var maxJ = Math.min(k, num.length - 1);
		      for (var j = Math.max(0, k - self.length + 1); j <= maxJ; j++) {
		        var i = (k - j) | 0;
		        a = self.words[i] | 0;
		        b = num.words[j] | 0;
		        r = a * b + rword;
		        ncarry += (r / 0x4000000) | 0;
		        rword = r & 0x3ffffff;
		      }
		      out.words[k] = rword | 0;
		      carry = ncarry | 0;
		    }
		    if (carry !== 0) {
		      out.words[k] = carry | 0;
		    } else {
		      out.length--;
		    }

		    return out.strip();
		  }

		  // TODO(indutny): it may be reasonable to omit it for users who don't need
		  // to work with 256-bit numbers, otherwise it gives 20% improvement for 256-bit
		  // multiplication (like elliptic secp256k1).
		  var comb10MulTo = function comb10MulTo (self, num, out) {
		    var a = self.words;
		    var b = num.words;
		    var o = out.words;
		    var c = 0;
		    var lo;
		    var mid;
		    var hi;
		    var a0 = a[0] | 0;
		    var al0 = a0 & 0x1fff;
		    var ah0 = a0 >>> 13;
		    var a1 = a[1] | 0;
		    var al1 = a1 & 0x1fff;
		    var ah1 = a1 >>> 13;
		    var a2 = a[2] | 0;
		    var al2 = a2 & 0x1fff;
		    var ah2 = a2 >>> 13;
		    var a3 = a[3] | 0;
		    var al3 = a3 & 0x1fff;
		    var ah3 = a3 >>> 13;
		    var a4 = a[4] | 0;
		    var al4 = a4 & 0x1fff;
		    var ah4 = a4 >>> 13;
		    var a5 = a[5] | 0;
		    var al5 = a5 & 0x1fff;
		    var ah5 = a5 >>> 13;
		    var a6 = a[6] | 0;
		    var al6 = a6 & 0x1fff;
		    var ah6 = a6 >>> 13;
		    var a7 = a[7] | 0;
		    var al7 = a7 & 0x1fff;
		    var ah7 = a7 >>> 13;
		    var a8 = a[8] | 0;
		    var al8 = a8 & 0x1fff;
		    var ah8 = a8 >>> 13;
		    var a9 = a[9] | 0;
		    var al9 = a9 & 0x1fff;
		    var ah9 = a9 >>> 13;
		    var b0 = b[0] | 0;
		    var bl0 = b0 & 0x1fff;
		    var bh0 = b0 >>> 13;
		    var b1 = b[1] | 0;
		    var bl1 = b1 & 0x1fff;
		    var bh1 = b1 >>> 13;
		    var b2 = b[2] | 0;
		    var bl2 = b2 & 0x1fff;
		    var bh2 = b2 >>> 13;
		    var b3 = b[3] | 0;
		    var bl3 = b3 & 0x1fff;
		    var bh3 = b3 >>> 13;
		    var b4 = b[4] | 0;
		    var bl4 = b4 & 0x1fff;
		    var bh4 = b4 >>> 13;
		    var b5 = b[5] | 0;
		    var bl5 = b5 & 0x1fff;
		    var bh5 = b5 >>> 13;
		    var b6 = b[6] | 0;
		    var bl6 = b6 & 0x1fff;
		    var bh6 = b6 >>> 13;
		    var b7 = b[7] | 0;
		    var bl7 = b7 & 0x1fff;
		    var bh7 = b7 >>> 13;
		    var b8 = b[8] | 0;
		    var bl8 = b8 & 0x1fff;
		    var bh8 = b8 >>> 13;
		    var b9 = b[9] | 0;
		    var bl9 = b9 & 0x1fff;
		    var bh9 = b9 >>> 13;

		    out.negative = self.negative ^ num.negative;
		    out.length = 19;
		    /* k = 0 */
		    lo = Math.imul(al0, bl0);
		    mid = Math.imul(al0, bh0);
		    mid = (mid + Math.imul(ah0, bl0)) | 0;
		    hi = Math.imul(ah0, bh0);
		    var w0 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w0 >>> 26)) | 0;
		    w0 &= 0x3ffffff;
		    /* k = 1 */
		    lo = Math.imul(al1, bl0);
		    mid = Math.imul(al1, bh0);
		    mid = (mid + Math.imul(ah1, bl0)) | 0;
		    hi = Math.imul(ah1, bh0);
		    lo = (lo + Math.imul(al0, bl1)) | 0;
		    mid = (mid + Math.imul(al0, bh1)) | 0;
		    mid = (mid + Math.imul(ah0, bl1)) | 0;
		    hi = (hi + Math.imul(ah0, bh1)) | 0;
		    var w1 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w1 >>> 26)) | 0;
		    w1 &= 0x3ffffff;
		    /* k = 2 */
		    lo = Math.imul(al2, bl0);
		    mid = Math.imul(al2, bh0);
		    mid = (mid + Math.imul(ah2, bl0)) | 0;
		    hi = Math.imul(ah2, bh0);
		    lo = (lo + Math.imul(al1, bl1)) | 0;
		    mid = (mid + Math.imul(al1, bh1)) | 0;
		    mid = (mid + Math.imul(ah1, bl1)) | 0;
		    hi = (hi + Math.imul(ah1, bh1)) | 0;
		    lo = (lo + Math.imul(al0, bl2)) | 0;
		    mid = (mid + Math.imul(al0, bh2)) | 0;
		    mid = (mid + Math.imul(ah0, bl2)) | 0;
		    hi = (hi + Math.imul(ah0, bh2)) | 0;
		    var w2 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w2 >>> 26)) | 0;
		    w2 &= 0x3ffffff;
		    /* k = 3 */
		    lo = Math.imul(al3, bl0);
		    mid = Math.imul(al3, bh0);
		    mid = (mid + Math.imul(ah3, bl0)) | 0;
		    hi = Math.imul(ah3, bh0);
		    lo = (lo + Math.imul(al2, bl1)) | 0;
		    mid = (mid + Math.imul(al2, bh1)) | 0;
		    mid = (mid + Math.imul(ah2, bl1)) | 0;
		    hi = (hi + Math.imul(ah2, bh1)) | 0;
		    lo = (lo + Math.imul(al1, bl2)) | 0;
		    mid = (mid + Math.imul(al1, bh2)) | 0;
		    mid = (mid + Math.imul(ah1, bl2)) | 0;
		    hi = (hi + Math.imul(ah1, bh2)) | 0;
		    lo = (lo + Math.imul(al0, bl3)) | 0;
		    mid = (mid + Math.imul(al0, bh3)) | 0;
		    mid = (mid + Math.imul(ah0, bl3)) | 0;
		    hi = (hi + Math.imul(ah0, bh3)) | 0;
		    var w3 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w3 >>> 26)) | 0;
		    w3 &= 0x3ffffff;
		    /* k = 4 */
		    lo = Math.imul(al4, bl0);
		    mid = Math.imul(al4, bh0);
		    mid = (mid + Math.imul(ah4, bl0)) | 0;
		    hi = Math.imul(ah4, bh0);
		    lo = (lo + Math.imul(al3, bl1)) | 0;
		    mid = (mid + Math.imul(al3, bh1)) | 0;
		    mid = (mid + Math.imul(ah3, bl1)) | 0;
		    hi = (hi + Math.imul(ah3, bh1)) | 0;
		    lo = (lo + Math.imul(al2, bl2)) | 0;
		    mid = (mid + Math.imul(al2, bh2)) | 0;
		    mid = (mid + Math.imul(ah2, bl2)) | 0;
		    hi = (hi + Math.imul(ah2, bh2)) | 0;
		    lo = (lo + Math.imul(al1, bl3)) | 0;
		    mid = (mid + Math.imul(al1, bh3)) | 0;
		    mid = (mid + Math.imul(ah1, bl3)) | 0;
		    hi = (hi + Math.imul(ah1, bh3)) | 0;
		    lo = (lo + Math.imul(al0, bl4)) | 0;
		    mid = (mid + Math.imul(al0, bh4)) | 0;
		    mid = (mid + Math.imul(ah0, bl4)) | 0;
		    hi = (hi + Math.imul(ah0, bh4)) | 0;
		    var w4 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w4 >>> 26)) | 0;
		    w4 &= 0x3ffffff;
		    /* k = 5 */
		    lo = Math.imul(al5, bl0);
		    mid = Math.imul(al5, bh0);
		    mid = (mid + Math.imul(ah5, bl0)) | 0;
		    hi = Math.imul(ah5, bh0);
		    lo = (lo + Math.imul(al4, bl1)) | 0;
		    mid = (mid + Math.imul(al4, bh1)) | 0;
		    mid = (mid + Math.imul(ah4, bl1)) | 0;
		    hi = (hi + Math.imul(ah4, bh1)) | 0;
		    lo = (lo + Math.imul(al3, bl2)) | 0;
		    mid = (mid + Math.imul(al3, bh2)) | 0;
		    mid = (mid + Math.imul(ah3, bl2)) | 0;
		    hi = (hi + Math.imul(ah3, bh2)) | 0;
		    lo = (lo + Math.imul(al2, bl3)) | 0;
		    mid = (mid + Math.imul(al2, bh3)) | 0;
		    mid = (mid + Math.imul(ah2, bl3)) | 0;
		    hi = (hi + Math.imul(ah2, bh3)) | 0;
		    lo = (lo + Math.imul(al1, bl4)) | 0;
		    mid = (mid + Math.imul(al1, bh4)) | 0;
		    mid = (mid + Math.imul(ah1, bl4)) | 0;
		    hi = (hi + Math.imul(ah1, bh4)) | 0;
		    lo = (lo + Math.imul(al0, bl5)) | 0;
		    mid = (mid + Math.imul(al0, bh5)) | 0;
		    mid = (mid + Math.imul(ah0, bl5)) | 0;
		    hi = (hi + Math.imul(ah0, bh5)) | 0;
		    var w5 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w5 >>> 26)) | 0;
		    w5 &= 0x3ffffff;
		    /* k = 6 */
		    lo = Math.imul(al6, bl0);
		    mid = Math.imul(al6, bh0);
		    mid = (mid + Math.imul(ah6, bl0)) | 0;
		    hi = Math.imul(ah6, bh0);
		    lo = (lo + Math.imul(al5, bl1)) | 0;
		    mid = (mid + Math.imul(al5, bh1)) | 0;
		    mid = (mid + Math.imul(ah5, bl1)) | 0;
		    hi = (hi + Math.imul(ah5, bh1)) | 0;
		    lo = (lo + Math.imul(al4, bl2)) | 0;
		    mid = (mid + Math.imul(al4, bh2)) | 0;
		    mid = (mid + Math.imul(ah4, bl2)) | 0;
		    hi = (hi + Math.imul(ah4, bh2)) | 0;
		    lo = (lo + Math.imul(al3, bl3)) | 0;
		    mid = (mid + Math.imul(al3, bh3)) | 0;
		    mid = (mid + Math.imul(ah3, bl3)) | 0;
		    hi = (hi + Math.imul(ah3, bh3)) | 0;
		    lo = (lo + Math.imul(al2, bl4)) | 0;
		    mid = (mid + Math.imul(al2, bh4)) | 0;
		    mid = (mid + Math.imul(ah2, bl4)) | 0;
		    hi = (hi + Math.imul(ah2, bh4)) | 0;
		    lo = (lo + Math.imul(al1, bl5)) | 0;
		    mid = (mid + Math.imul(al1, bh5)) | 0;
		    mid = (mid + Math.imul(ah1, bl5)) | 0;
		    hi = (hi + Math.imul(ah1, bh5)) | 0;
		    lo = (lo + Math.imul(al0, bl6)) | 0;
		    mid = (mid + Math.imul(al0, bh6)) | 0;
		    mid = (mid + Math.imul(ah0, bl6)) | 0;
		    hi = (hi + Math.imul(ah0, bh6)) | 0;
		    var w6 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w6 >>> 26)) | 0;
		    w6 &= 0x3ffffff;
		    /* k = 7 */
		    lo = Math.imul(al7, bl0);
		    mid = Math.imul(al7, bh0);
		    mid = (mid + Math.imul(ah7, bl0)) | 0;
		    hi = Math.imul(ah7, bh0);
		    lo = (lo + Math.imul(al6, bl1)) | 0;
		    mid = (mid + Math.imul(al6, bh1)) | 0;
		    mid = (mid + Math.imul(ah6, bl1)) | 0;
		    hi = (hi + Math.imul(ah6, bh1)) | 0;
		    lo = (lo + Math.imul(al5, bl2)) | 0;
		    mid = (mid + Math.imul(al5, bh2)) | 0;
		    mid = (mid + Math.imul(ah5, bl2)) | 0;
		    hi = (hi + Math.imul(ah5, bh2)) | 0;
		    lo = (lo + Math.imul(al4, bl3)) | 0;
		    mid = (mid + Math.imul(al4, bh3)) | 0;
		    mid = (mid + Math.imul(ah4, bl3)) | 0;
		    hi = (hi + Math.imul(ah4, bh3)) | 0;
		    lo = (lo + Math.imul(al3, bl4)) | 0;
		    mid = (mid + Math.imul(al3, bh4)) | 0;
		    mid = (mid + Math.imul(ah3, bl4)) | 0;
		    hi = (hi + Math.imul(ah3, bh4)) | 0;
		    lo = (lo + Math.imul(al2, bl5)) | 0;
		    mid = (mid + Math.imul(al2, bh5)) | 0;
		    mid = (mid + Math.imul(ah2, bl5)) | 0;
		    hi = (hi + Math.imul(ah2, bh5)) | 0;
		    lo = (lo + Math.imul(al1, bl6)) | 0;
		    mid = (mid + Math.imul(al1, bh6)) | 0;
		    mid = (mid + Math.imul(ah1, bl6)) | 0;
		    hi = (hi + Math.imul(ah1, bh6)) | 0;
		    lo = (lo + Math.imul(al0, bl7)) | 0;
		    mid = (mid + Math.imul(al0, bh7)) | 0;
		    mid = (mid + Math.imul(ah0, bl7)) | 0;
		    hi = (hi + Math.imul(ah0, bh7)) | 0;
		    var w7 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w7 >>> 26)) | 0;
		    w7 &= 0x3ffffff;
		    /* k = 8 */
		    lo = Math.imul(al8, bl0);
		    mid = Math.imul(al8, bh0);
		    mid = (mid + Math.imul(ah8, bl0)) | 0;
		    hi = Math.imul(ah8, bh0);
		    lo = (lo + Math.imul(al7, bl1)) | 0;
		    mid = (mid + Math.imul(al7, bh1)) | 0;
		    mid = (mid + Math.imul(ah7, bl1)) | 0;
		    hi = (hi + Math.imul(ah7, bh1)) | 0;
		    lo = (lo + Math.imul(al6, bl2)) | 0;
		    mid = (mid + Math.imul(al6, bh2)) | 0;
		    mid = (mid + Math.imul(ah6, bl2)) | 0;
		    hi = (hi + Math.imul(ah6, bh2)) | 0;
		    lo = (lo + Math.imul(al5, bl3)) | 0;
		    mid = (mid + Math.imul(al5, bh3)) | 0;
		    mid = (mid + Math.imul(ah5, bl3)) | 0;
		    hi = (hi + Math.imul(ah5, bh3)) | 0;
		    lo = (lo + Math.imul(al4, bl4)) | 0;
		    mid = (mid + Math.imul(al4, bh4)) | 0;
		    mid = (mid + Math.imul(ah4, bl4)) | 0;
		    hi = (hi + Math.imul(ah4, bh4)) | 0;
		    lo = (lo + Math.imul(al3, bl5)) | 0;
		    mid = (mid + Math.imul(al3, bh5)) | 0;
		    mid = (mid + Math.imul(ah3, bl5)) | 0;
		    hi = (hi + Math.imul(ah3, bh5)) | 0;
		    lo = (lo + Math.imul(al2, bl6)) | 0;
		    mid = (mid + Math.imul(al2, bh6)) | 0;
		    mid = (mid + Math.imul(ah2, bl6)) | 0;
		    hi = (hi + Math.imul(ah2, bh6)) | 0;
		    lo = (lo + Math.imul(al1, bl7)) | 0;
		    mid = (mid + Math.imul(al1, bh7)) | 0;
		    mid = (mid + Math.imul(ah1, bl7)) | 0;
		    hi = (hi + Math.imul(ah1, bh7)) | 0;
		    lo = (lo + Math.imul(al0, bl8)) | 0;
		    mid = (mid + Math.imul(al0, bh8)) | 0;
		    mid = (mid + Math.imul(ah0, bl8)) | 0;
		    hi = (hi + Math.imul(ah0, bh8)) | 0;
		    var w8 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w8 >>> 26)) | 0;
		    w8 &= 0x3ffffff;
		    /* k = 9 */
		    lo = Math.imul(al9, bl0);
		    mid = Math.imul(al9, bh0);
		    mid = (mid + Math.imul(ah9, bl0)) | 0;
		    hi = Math.imul(ah9, bh0);
		    lo = (lo + Math.imul(al8, bl1)) | 0;
		    mid = (mid + Math.imul(al8, bh1)) | 0;
		    mid = (mid + Math.imul(ah8, bl1)) | 0;
		    hi = (hi + Math.imul(ah8, bh1)) | 0;
		    lo = (lo + Math.imul(al7, bl2)) | 0;
		    mid = (mid + Math.imul(al7, bh2)) | 0;
		    mid = (mid + Math.imul(ah7, bl2)) | 0;
		    hi = (hi + Math.imul(ah7, bh2)) | 0;
		    lo = (lo + Math.imul(al6, bl3)) | 0;
		    mid = (mid + Math.imul(al6, bh3)) | 0;
		    mid = (mid + Math.imul(ah6, bl3)) | 0;
		    hi = (hi + Math.imul(ah6, bh3)) | 0;
		    lo = (lo + Math.imul(al5, bl4)) | 0;
		    mid = (mid + Math.imul(al5, bh4)) | 0;
		    mid = (mid + Math.imul(ah5, bl4)) | 0;
		    hi = (hi + Math.imul(ah5, bh4)) | 0;
		    lo = (lo + Math.imul(al4, bl5)) | 0;
		    mid = (mid + Math.imul(al4, bh5)) | 0;
		    mid = (mid + Math.imul(ah4, bl5)) | 0;
		    hi = (hi + Math.imul(ah4, bh5)) | 0;
		    lo = (lo + Math.imul(al3, bl6)) | 0;
		    mid = (mid + Math.imul(al3, bh6)) | 0;
		    mid = (mid + Math.imul(ah3, bl6)) | 0;
		    hi = (hi + Math.imul(ah3, bh6)) | 0;
		    lo = (lo + Math.imul(al2, bl7)) | 0;
		    mid = (mid + Math.imul(al2, bh7)) | 0;
		    mid = (mid + Math.imul(ah2, bl7)) | 0;
		    hi = (hi + Math.imul(ah2, bh7)) | 0;
		    lo = (lo + Math.imul(al1, bl8)) | 0;
		    mid = (mid + Math.imul(al1, bh8)) | 0;
		    mid = (mid + Math.imul(ah1, bl8)) | 0;
		    hi = (hi + Math.imul(ah1, bh8)) | 0;
		    lo = (lo + Math.imul(al0, bl9)) | 0;
		    mid = (mid + Math.imul(al0, bh9)) | 0;
		    mid = (mid + Math.imul(ah0, bl9)) | 0;
		    hi = (hi + Math.imul(ah0, bh9)) | 0;
		    var w9 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w9 >>> 26)) | 0;
		    w9 &= 0x3ffffff;
		    /* k = 10 */
		    lo = Math.imul(al9, bl1);
		    mid = Math.imul(al9, bh1);
		    mid = (mid + Math.imul(ah9, bl1)) | 0;
		    hi = Math.imul(ah9, bh1);
		    lo = (lo + Math.imul(al8, bl2)) | 0;
		    mid = (mid + Math.imul(al8, bh2)) | 0;
		    mid = (mid + Math.imul(ah8, bl2)) | 0;
		    hi = (hi + Math.imul(ah8, bh2)) | 0;
		    lo = (lo + Math.imul(al7, bl3)) | 0;
		    mid = (mid + Math.imul(al7, bh3)) | 0;
		    mid = (mid + Math.imul(ah7, bl3)) | 0;
		    hi = (hi + Math.imul(ah7, bh3)) | 0;
		    lo = (lo + Math.imul(al6, bl4)) | 0;
		    mid = (mid + Math.imul(al6, bh4)) | 0;
		    mid = (mid + Math.imul(ah6, bl4)) | 0;
		    hi = (hi + Math.imul(ah6, bh4)) | 0;
		    lo = (lo + Math.imul(al5, bl5)) | 0;
		    mid = (mid + Math.imul(al5, bh5)) | 0;
		    mid = (mid + Math.imul(ah5, bl5)) | 0;
		    hi = (hi + Math.imul(ah5, bh5)) | 0;
		    lo = (lo + Math.imul(al4, bl6)) | 0;
		    mid = (mid + Math.imul(al4, bh6)) | 0;
		    mid = (mid + Math.imul(ah4, bl6)) | 0;
		    hi = (hi + Math.imul(ah4, bh6)) | 0;
		    lo = (lo + Math.imul(al3, bl7)) | 0;
		    mid = (mid + Math.imul(al3, bh7)) | 0;
		    mid = (mid + Math.imul(ah3, bl7)) | 0;
		    hi = (hi + Math.imul(ah3, bh7)) | 0;
		    lo = (lo + Math.imul(al2, bl8)) | 0;
		    mid = (mid + Math.imul(al2, bh8)) | 0;
		    mid = (mid + Math.imul(ah2, bl8)) | 0;
		    hi = (hi + Math.imul(ah2, bh8)) | 0;
		    lo = (lo + Math.imul(al1, bl9)) | 0;
		    mid = (mid + Math.imul(al1, bh9)) | 0;
		    mid = (mid + Math.imul(ah1, bl9)) | 0;
		    hi = (hi + Math.imul(ah1, bh9)) | 0;
		    var w10 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w10 >>> 26)) | 0;
		    w10 &= 0x3ffffff;
		    /* k = 11 */
		    lo = Math.imul(al9, bl2);
		    mid = Math.imul(al9, bh2);
		    mid = (mid + Math.imul(ah9, bl2)) | 0;
		    hi = Math.imul(ah9, bh2);
		    lo = (lo + Math.imul(al8, bl3)) | 0;
		    mid = (mid + Math.imul(al8, bh3)) | 0;
		    mid = (mid + Math.imul(ah8, bl3)) | 0;
		    hi = (hi + Math.imul(ah8, bh3)) | 0;
		    lo = (lo + Math.imul(al7, bl4)) | 0;
		    mid = (mid + Math.imul(al7, bh4)) | 0;
		    mid = (mid + Math.imul(ah7, bl4)) | 0;
		    hi = (hi + Math.imul(ah7, bh4)) | 0;
		    lo = (lo + Math.imul(al6, bl5)) | 0;
		    mid = (mid + Math.imul(al6, bh5)) | 0;
		    mid = (mid + Math.imul(ah6, bl5)) | 0;
		    hi = (hi + Math.imul(ah6, bh5)) | 0;
		    lo = (lo + Math.imul(al5, bl6)) | 0;
		    mid = (mid + Math.imul(al5, bh6)) | 0;
		    mid = (mid + Math.imul(ah5, bl6)) | 0;
		    hi = (hi + Math.imul(ah5, bh6)) | 0;
		    lo = (lo + Math.imul(al4, bl7)) | 0;
		    mid = (mid + Math.imul(al4, bh7)) | 0;
		    mid = (mid + Math.imul(ah4, bl7)) | 0;
		    hi = (hi + Math.imul(ah4, bh7)) | 0;
		    lo = (lo + Math.imul(al3, bl8)) | 0;
		    mid = (mid + Math.imul(al3, bh8)) | 0;
		    mid = (mid + Math.imul(ah3, bl8)) | 0;
		    hi = (hi + Math.imul(ah3, bh8)) | 0;
		    lo = (lo + Math.imul(al2, bl9)) | 0;
		    mid = (mid + Math.imul(al2, bh9)) | 0;
		    mid = (mid + Math.imul(ah2, bl9)) | 0;
		    hi = (hi + Math.imul(ah2, bh9)) | 0;
		    var w11 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w11 >>> 26)) | 0;
		    w11 &= 0x3ffffff;
		    /* k = 12 */
		    lo = Math.imul(al9, bl3);
		    mid = Math.imul(al9, bh3);
		    mid = (mid + Math.imul(ah9, bl3)) | 0;
		    hi = Math.imul(ah9, bh3);
		    lo = (lo + Math.imul(al8, bl4)) | 0;
		    mid = (mid + Math.imul(al8, bh4)) | 0;
		    mid = (mid + Math.imul(ah8, bl4)) | 0;
		    hi = (hi + Math.imul(ah8, bh4)) | 0;
		    lo = (lo + Math.imul(al7, bl5)) | 0;
		    mid = (mid + Math.imul(al7, bh5)) | 0;
		    mid = (mid + Math.imul(ah7, bl5)) | 0;
		    hi = (hi + Math.imul(ah7, bh5)) | 0;
		    lo = (lo + Math.imul(al6, bl6)) | 0;
		    mid = (mid + Math.imul(al6, bh6)) | 0;
		    mid = (mid + Math.imul(ah6, bl6)) | 0;
		    hi = (hi + Math.imul(ah6, bh6)) | 0;
		    lo = (lo + Math.imul(al5, bl7)) | 0;
		    mid = (mid + Math.imul(al5, bh7)) | 0;
		    mid = (mid + Math.imul(ah5, bl7)) | 0;
		    hi = (hi + Math.imul(ah5, bh7)) | 0;
		    lo = (lo + Math.imul(al4, bl8)) | 0;
		    mid = (mid + Math.imul(al4, bh8)) | 0;
		    mid = (mid + Math.imul(ah4, bl8)) | 0;
		    hi = (hi + Math.imul(ah4, bh8)) | 0;
		    lo = (lo + Math.imul(al3, bl9)) | 0;
		    mid = (mid + Math.imul(al3, bh9)) | 0;
		    mid = (mid + Math.imul(ah3, bl9)) | 0;
		    hi = (hi + Math.imul(ah3, bh9)) | 0;
		    var w12 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w12 >>> 26)) | 0;
		    w12 &= 0x3ffffff;
		    /* k = 13 */
		    lo = Math.imul(al9, bl4);
		    mid = Math.imul(al9, bh4);
		    mid = (mid + Math.imul(ah9, bl4)) | 0;
		    hi = Math.imul(ah9, bh4);
		    lo = (lo + Math.imul(al8, bl5)) | 0;
		    mid = (mid + Math.imul(al8, bh5)) | 0;
		    mid = (mid + Math.imul(ah8, bl5)) | 0;
		    hi = (hi + Math.imul(ah8, bh5)) | 0;
		    lo = (lo + Math.imul(al7, bl6)) | 0;
		    mid = (mid + Math.imul(al7, bh6)) | 0;
		    mid = (mid + Math.imul(ah7, bl6)) | 0;
		    hi = (hi + Math.imul(ah7, bh6)) | 0;
		    lo = (lo + Math.imul(al6, bl7)) | 0;
		    mid = (mid + Math.imul(al6, bh7)) | 0;
		    mid = (mid + Math.imul(ah6, bl7)) | 0;
		    hi = (hi + Math.imul(ah6, bh7)) | 0;
		    lo = (lo + Math.imul(al5, bl8)) | 0;
		    mid = (mid + Math.imul(al5, bh8)) | 0;
		    mid = (mid + Math.imul(ah5, bl8)) | 0;
		    hi = (hi + Math.imul(ah5, bh8)) | 0;
		    lo = (lo + Math.imul(al4, bl9)) | 0;
		    mid = (mid + Math.imul(al4, bh9)) | 0;
		    mid = (mid + Math.imul(ah4, bl9)) | 0;
		    hi = (hi + Math.imul(ah4, bh9)) | 0;
		    var w13 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w13 >>> 26)) | 0;
		    w13 &= 0x3ffffff;
		    /* k = 14 */
		    lo = Math.imul(al9, bl5);
		    mid = Math.imul(al9, bh5);
		    mid = (mid + Math.imul(ah9, bl5)) | 0;
		    hi = Math.imul(ah9, bh5);
		    lo = (lo + Math.imul(al8, bl6)) | 0;
		    mid = (mid + Math.imul(al8, bh6)) | 0;
		    mid = (mid + Math.imul(ah8, bl6)) | 0;
		    hi = (hi + Math.imul(ah8, bh6)) | 0;
		    lo = (lo + Math.imul(al7, bl7)) | 0;
		    mid = (mid + Math.imul(al7, bh7)) | 0;
		    mid = (mid + Math.imul(ah7, bl7)) | 0;
		    hi = (hi + Math.imul(ah7, bh7)) | 0;
		    lo = (lo + Math.imul(al6, bl8)) | 0;
		    mid = (mid + Math.imul(al6, bh8)) | 0;
		    mid = (mid + Math.imul(ah6, bl8)) | 0;
		    hi = (hi + Math.imul(ah6, bh8)) | 0;
		    lo = (lo + Math.imul(al5, bl9)) | 0;
		    mid = (mid + Math.imul(al5, bh9)) | 0;
		    mid = (mid + Math.imul(ah5, bl9)) | 0;
		    hi = (hi + Math.imul(ah5, bh9)) | 0;
		    var w14 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w14 >>> 26)) | 0;
		    w14 &= 0x3ffffff;
		    /* k = 15 */
		    lo = Math.imul(al9, bl6);
		    mid = Math.imul(al9, bh6);
		    mid = (mid + Math.imul(ah9, bl6)) | 0;
		    hi = Math.imul(ah9, bh6);
		    lo = (lo + Math.imul(al8, bl7)) | 0;
		    mid = (mid + Math.imul(al8, bh7)) | 0;
		    mid = (mid + Math.imul(ah8, bl7)) | 0;
		    hi = (hi + Math.imul(ah8, bh7)) | 0;
		    lo = (lo + Math.imul(al7, bl8)) | 0;
		    mid = (mid + Math.imul(al7, bh8)) | 0;
		    mid = (mid + Math.imul(ah7, bl8)) | 0;
		    hi = (hi + Math.imul(ah7, bh8)) | 0;
		    lo = (lo + Math.imul(al6, bl9)) | 0;
		    mid = (mid + Math.imul(al6, bh9)) | 0;
		    mid = (mid + Math.imul(ah6, bl9)) | 0;
		    hi = (hi + Math.imul(ah6, bh9)) | 0;
		    var w15 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w15 >>> 26)) | 0;
		    w15 &= 0x3ffffff;
		    /* k = 16 */
		    lo = Math.imul(al9, bl7);
		    mid = Math.imul(al9, bh7);
		    mid = (mid + Math.imul(ah9, bl7)) | 0;
		    hi = Math.imul(ah9, bh7);
		    lo = (lo + Math.imul(al8, bl8)) | 0;
		    mid = (mid + Math.imul(al8, bh8)) | 0;
		    mid = (mid + Math.imul(ah8, bl8)) | 0;
		    hi = (hi + Math.imul(ah8, bh8)) | 0;
		    lo = (lo + Math.imul(al7, bl9)) | 0;
		    mid = (mid + Math.imul(al7, bh9)) | 0;
		    mid = (mid + Math.imul(ah7, bl9)) | 0;
		    hi = (hi + Math.imul(ah7, bh9)) | 0;
		    var w16 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w16 >>> 26)) | 0;
		    w16 &= 0x3ffffff;
		    /* k = 17 */
		    lo = Math.imul(al9, bl8);
		    mid = Math.imul(al9, bh8);
		    mid = (mid + Math.imul(ah9, bl8)) | 0;
		    hi = Math.imul(ah9, bh8);
		    lo = (lo + Math.imul(al8, bl9)) | 0;
		    mid = (mid + Math.imul(al8, bh9)) | 0;
		    mid = (mid + Math.imul(ah8, bl9)) | 0;
		    hi = (hi + Math.imul(ah8, bh9)) | 0;
		    var w17 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w17 >>> 26)) | 0;
		    w17 &= 0x3ffffff;
		    /* k = 18 */
		    lo = Math.imul(al9, bl9);
		    mid = Math.imul(al9, bh9);
		    mid = (mid + Math.imul(ah9, bl9)) | 0;
		    hi = Math.imul(ah9, bh9);
		    var w18 = (((c + lo) | 0) + ((mid & 0x1fff) << 13)) | 0;
		    c = (((hi + (mid >>> 13)) | 0) + (w18 >>> 26)) | 0;
		    w18 &= 0x3ffffff;
		    o[0] = w0;
		    o[1] = w1;
		    o[2] = w2;
		    o[3] = w3;
		    o[4] = w4;
		    o[5] = w5;
		    o[6] = w6;
		    o[7] = w7;
		    o[8] = w8;
		    o[9] = w9;
		    o[10] = w10;
		    o[11] = w11;
		    o[12] = w12;
		    o[13] = w13;
		    o[14] = w14;
		    o[15] = w15;
		    o[16] = w16;
		    o[17] = w17;
		    o[18] = w18;
		    if (c !== 0) {
		      o[19] = c;
		      out.length++;
		    }
		    return out;
		  };

		  // Polyfill comb
		  if (!Math.imul) {
		    comb10MulTo = smallMulTo;
		  }

		  function bigMulTo (self, num, out) {
		    out.negative = num.negative ^ self.negative;
		    out.length = self.length + num.length;

		    var carry = 0;
		    var hncarry = 0;
		    for (var k = 0; k < out.length - 1; k++) {
		      // Sum all words with the same `i + j = k` and accumulate `ncarry`,
		      // note that ncarry could be >= 0x3ffffff
		      var ncarry = hncarry;
		      hncarry = 0;
		      var rword = carry & 0x3ffffff;
		      var maxJ = Math.min(k, num.length - 1);
		      for (var j = Math.max(0, k - self.length + 1); j <= maxJ; j++) {
		        var i = k - j;
		        var a = self.words[i] | 0;
		        var b = num.words[j] | 0;
		        var r = a * b;

		        var lo = r & 0x3ffffff;
		        ncarry = (ncarry + ((r / 0x4000000) | 0)) | 0;
		        lo = (lo + rword) | 0;
		        rword = lo & 0x3ffffff;
		        ncarry = (ncarry + (lo >>> 26)) | 0;

		        hncarry += ncarry >>> 26;
		        ncarry &= 0x3ffffff;
		      }
		      out.words[k] = rword;
		      carry = ncarry;
		      ncarry = hncarry;
		    }
		    if (carry !== 0) {
		      out.words[k] = carry;
		    } else {
		      out.length--;
		    }

		    return out.strip();
		  }

		  function jumboMulTo (self, num, out) {
		    var fftm = new FFTM();
		    return fftm.mulp(self, num, out);
		  }

		  BN.prototype.mulTo = function mulTo (num, out) {
		    var res;
		    var len = this.length + num.length;
		    if (this.length === 10 && num.length === 10) {
		      res = comb10MulTo(this, num, out);
		    } else if (len < 63) {
		      res = smallMulTo(this, num, out);
		    } else if (len < 1024) {
		      res = bigMulTo(this, num, out);
		    } else {
		      res = jumboMulTo(this, num, out);
		    }

		    return res;
		  };

		  // Cooley-Tukey algorithm for FFT
		  // slightly revisited to rely on looping instead of recursion

		  function FFTM (x, y) {
		    this.x = x;
		    this.y = y;
		  }

		  FFTM.prototype.makeRBT = function makeRBT (N) {
		    var t = new Array(N);
		    var l = BN.prototype._countBits(N) - 1;
		    for (var i = 0; i < N; i++) {
		      t[i] = this.revBin(i, l, N);
		    }

		    return t;
		  };

		  // Returns binary-reversed representation of `x`
		  FFTM.prototype.revBin = function revBin (x, l, N) {
		    if (x === 0 || x === N - 1) return x;

		    var rb = 0;
		    for (var i = 0; i < l; i++) {
		      rb |= (x & 1) << (l - i - 1);
		      x >>= 1;
		    }

		    return rb;
		  };

		  // Performs "tweedling" phase, therefore 'emulating'
		  // behaviour of the recursive algorithm
		  FFTM.prototype.permute = function permute (rbt, rws, iws, rtws, itws, N) {
		    for (var i = 0; i < N; i++) {
		      rtws[i] = rws[rbt[i]];
		      itws[i] = iws[rbt[i]];
		    }
		  };

		  FFTM.prototype.transform = function transform (rws, iws, rtws, itws, N, rbt) {
		    this.permute(rbt, rws, iws, rtws, itws, N);

		    for (var s = 1; s < N; s <<= 1) {
		      var l = s << 1;

		      var rtwdf = Math.cos(2 * Math.PI / l);
		      var itwdf = Math.sin(2 * Math.PI / l);

		      for (var p = 0; p < N; p += l) {
		        var rtwdf_ = rtwdf;
		        var itwdf_ = itwdf;

		        for (var j = 0; j < s; j++) {
		          var re = rtws[p + j];
		          var ie = itws[p + j];

		          var ro = rtws[p + j + s];
		          var io = itws[p + j + s];

		          var rx = rtwdf_ * ro - itwdf_ * io;

		          io = rtwdf_ * io + itwdf_ * ro;
		          ro = rx;

		          rtws[p + j] = re + ro;
		          itws[p + j] = ie + io;

		          rtws[p + j + s] = re - ro;
		          itws[p + j + s] = ie - io;

		          /* jshint maxdepth : false */
		          if (j !== l) {
		            rx = rtwdf * rtwdf_ - itwdf * itwdf_;

		            itwdf_ = rtwdf * itwdf_ + itwdf * rtwdf_;
		            rtwdf_ = rx;
		          }
		        }
		      }
		    }
		  };

		  FFTM.prototype.guessLen13b = function guessLen13b (n, m) {
		    var N = Math.max(m, n) | 1;
		    var odd = N & 1;
		    var i = 0;
		    for (N = N / 2 | 0; N; N = N >>> 1) {
		      i++;
		    }

		    return 1 << i + 1 + odd;
		  };

		  FFTM.prototype.conjugate = function conjugate (rws, iws, N) {
		    if (N <= 1) return;

		    for (var i = 0; i < N / 2; i++) {
		      var t = rws[i];

		      rws[i] = rws[N - i - 1];
		      rws[N - i - 1] = t;

		      t = iws[i];

		      iws[i] = -iws[N - i - 1];
		      iws[N - i - 1] = -t;
		    }
		  };

		  FFTM.prototype.normalize13b = function normalize13b (ws, N) {
		    var carry = 0;
		    for (var i = 0; i < N / 2; i++) {
		      var w = Math.round(ws[2 * i + 1] / N) * 0x2000 +
		        Math.round(ws[2 * i] / N) +
		        carry;

		      ws[i] = w & 0x3ffffff;

		      if (w < 0x4000000) {
		        carry = 0;
		      } else {
		        carry = w / 0x4000000 | 0;
		      }
		    }

		    return ws;
		  };

		  FFTM.prototype.convert13b = function convert13b (ws, len, rws, N) {
		    var carry = 0;
		    for (var i = 0; i < len; i++) {
		      carry = carry + (ws[i] | 0);

		      rws[2 * i] = carry & 0x1fff; carry = carry >>> 13;
		      rws[2 * i + 1] = carry & 0x1fff; carry = carry >>> 13;
		    }

		    // Pad with zeroes
		    for (i = 2 * len; i < N; ++i) {
		      rws[i] = 0;
		    }

		    assert(carry === 0);
		    assert((carry & -8192) === 0);
		  };

		  FFTM.prototype.stub = function stub (N) {
		    var ph = new Array(N);
		    for (var i = 0; i < N; i++) {
		      ph[i] = 0;
		    }

		    return ph;
		  };

		  FFTM.prototype.mulp = function mulp (x, y, out) {
		    var N = 2 * this.guessLen13b(x.length, y.length);

		    var rbt = this.makeRBT(N);

		    var _ = this.stub(N);

		    var rws = new Array(N);
		    var rwst = new Array(N);
		    var iwst = new Array(N);

		    var nrws = new Array(N);
		    var nrwst = new Array(N);
		    var niwst = new Array(N);

		    var rmws = out.words;
		    rmws.length = N;

		    this.convert13b(x.words, x.length, rws, N);
		    this.convert13b(y.words, y.length, nrws, N);

		    this.transform(rws, _, rwst, iwst, N, rbt);
		    this.transform(nrws, _, nrwst, niwst, N, rbt);

		    for (var i = 0; i < N; i++) {
		      var rx = rwst[i] * nrwst[i] - iwst[i] * niwst[i];
		      iwst[i] = rwst[i] * niwst[i] + iwst[i] * nrwst[i];
		      rwst[i] = rx;
		    }

		    this.conjugate(rwst, iwst, N);
		    this.transform(rwst, iwst, rmws, _, N, rbt);
		    this.conjugate(rmws, _, N);
		    this.normalize13b(rmws, N);

		    out.negative = x.negative ^ y.negative;
		    out.length = x.length + y.length;
		    return out.strip();
		  };

		  // Multiply `this` by `num`
		  BN.prototype.mul = function mul (num) {
		    var out = new BN(null);
		    out.words = new Array(this.length + num.length);
		    return this.mulTo(num, out);
		  };

		  // Multiply employing FFT
		  BN.prototype.mulf = function mulf (num) {
		    var out = new BN(null);
		    out.words = new Array(this.length + num.length);
		    return jumboMulTo(this, num, out);
		  };

		  // In-place Multiplication
		  BN.prototype.imul = function imul (num) {
		    return this.clone().mulTo(num, this);
		  };

		  BN.prototype.imuln = function imuln (num) {
		    assert(typeof num === 'number');
		    assert(num < 0x4000000);

		    // Carry
		    var carry = 0;
		    for (var i = 0; i < this.length; i++) {
		      var w = (this.words[i] | 0) * num;
		      var lo = (w & 0x3ffffff) + (carry & 0x3ffffff);
		      carry >>= 26;
		      carry += (w / 0x4000000) | 0;
		      // NOTE: lo is 27bit maximum
		      carry += lo >>> 26;
		      this.words[i] = lo & 0x3ffffff;
		    }

		    if (carry !== 0) {
		      this.words[i] = carry;
		      this.length++;
		    }

		    return this;
		  };

		  BN.prototype.muln = function muln (num) {
		    return this.clone().imuln(num);
		  };

		  // `this` * `this`
		  BN.prototype.sqr = function sqr () {
		    return this.mul(this);
		  };

		  // `this` * `this` in-place
		  BN.prototype.isqr = function isqr () {
		    return this.imul(this.clone());
		  };

		  // Math.pow(`this`, `num`)
		  BN.prototype.pow = function pow (num) {
		    var w = toBitArray(num);
		    if (w.length === 0) return new BN(1);

		    // Skip leading zeroes
		    var res = this;
		    for (var i = 0; i < w.length; i++, res = res.sqr()) {
		      if (w[i] !== 0) break;
		    }

		    if (++i < w.length) {
		      for (var q = res.sqr(); i < w.length; i++, q = q.sqr()) {
		        if (w[i] === 0) continue;

		        res = res.mul(q);
		      }
		    }

		    return res;
		  };

		  // Shift-left in-place
		  BN.prototype.iushln = function iushln (bits) {
		    assert(typeof bits === 'number' && bits >= 0);
		    var r = bits % 26;
		    var s = (bits - r) / 26;
		    var carryMask = (0x3ffffff >>> (26 - r)) << (26 - r);
		    var i;

		    if (r !== 0) {
		      var carry = 0;

		      for (i = 0; i < this.length; i++) {
		        var newCarry = this.words[i] & carryMask;
		        var c = ((this.words[i] | 0) - newCarry) << r;
		        this.words[i] = c | carry;
		        carry = newCarry >>> (26 - r);
		      }

		      if (carry) {
		        this.words[i] = carry;
		        this.length++;
		      }
		    }

		    if (s !== 0) {
		      for (i = this.length - 1; i >= 0; i--) {
		        this.words[i + s] = this.words[i];
		      }

		      for (i = 0; i < s; i++) {
		        this.words[i] = 0;
		      }

		      this.length += s;
		    }

		    return this.strip();
		  };

		  BN.prototype.ishln = function ishln (bits) {
		    // TODO(indutny): implement me
		    assert(this.negative === 0);
		    return this.iushln(bits);
		  };

		  // Shift-right in-place
		  // NOTE: `hint` is a lowest bit before trailing zeroes
		  // NOTE: if `extended` is present - it will be filled with destroyed bits
		  BN.prototype.iushrn = function iushrn (bits, hint, extended) {
		    assert(typeof bits === 'number' && bits >= 0);
		    var h;
		    if (hint) {
		      h = (hint - (hint % 26)) / 26;
		    } else {
		      h = 0;
		    }

		    var r = bits % 26;
		    var s = Math.min((bits - r) / 26, this.length);
		    var mask = 0x3ffffff ^ ((0x3ffffff >>> r) << r);
		    var maskedWords = extended;

		    h -= s;
		    h = Math.max(0, h);

		    // Extended mode, copy masked part
		    if (maskedWords) {
		      for (var i = 0; i < s; i++) {
		        maskedWords.words[i] = this.words[i];
		      }
		      maskedWords.length = s;
		    }

		    if (s === 0) ; else if (this.length > s) {
		      this.length -= s;
		      for (i = 0; i < this.length; i++) {
		        this.words[i] = this.words[i + s];
		      }
		    } else {
		      this.words[0] = 0;
		      this.length = 1;
		    }

		    var carry = 0;
		    for (i = this.length - 1; i >= 0 && (carry !== 0 || i >= h); i--) {
		      var word = this.words[i] | 0;
		      this.words[i] = (carry << (26 - r)) | (word >>> r);
		      carry = word & mask;
		    }

		    // Push carried bits as a mask
		    if (maskedWords && carry !== 0) {
		      maskedWords.words[maskedWords.length++] = carry;
		    }

		    if (this.length === 0) {
		      this.words[0] = 0;
		      this.length = 1;
		    }

		    return this.strip();
		  };

		  BN.prototype.ishrn = function ishrn (bits, hint, extended) {
		    // TODO(indutny): implement me
		    assert(this.negative === 0);
		    return this.iushrn(bits, hint, extended);
		  };

		  // Shift-left
		  BN.prototype.shln = function shln (bits) {
		    return this.clone().ishln(bits);
		  };

		  BN.prototype.ushln = function ushln (bits) {
		    return this.clone().iushln(bits);
		  };

		  // Shift-right
		  BN.prototype.shrn = function shrn (bits) {
		    return this.clone().ishrn(bits);
		  };

		  BN.prototype.ushrn = function ushrn (bits) {
		    return this.clone().iushrn(bits);
		  };

		  // Test if n bit is set
		  BN.prototype.testn = function testn (bit) {
		    assert(typeof bit === 'number' && bit >= 0);
		    var r = bit % 26;
		    var s = (bit - r) / 26;
		    var q = 1 << r;

		    // Fast case: bit is much higher than all existing words
		    if (this.length <= s) return false;

		    // Check bit and return
		    var w = this.words[s];

		    return !!(w & q);
		  };

		  // Return only lowers bits of number (in-place)
		  BN.prototype.imaskn = function imaskn (bits) {
		    assert(typeof bits === 'number' && bits >= 0);
		    var r = bits % 26;
		    var s = (bits - r) / 26;

		    assert(this.negative === 0, 'imaskn works only with positive numbers');

		    if (this.length <= s) {
		      return this;
		    }

		    if (r !== 0) {
		      s++;
		    }
		    this.length = Math.min(s, this.length);

		    if (r !== 0) {
		      var mask = 0x3ffffff ^ ((0x3ffffff >>> r) << r);
		      this.words[this.length - 1] &= mask;
		    }

		    return this.strip();
		  };

		  // Return only lowers bits of number
		  BN.prototype.maskn = function maskn (bits) {
		    return this.clone().imaskn(bits);
		  };

		  // Add plain number `num` to `this`
		  BN.prototype.iaddn = function iaddn (num) {
		    assert(typeof num === 'number');
		    assert(num < 0x4000000);
		    if (num < 0) return this.isubn(-num);

		    // Possible sign change
		    if (this.negative !== 0) {
		      if (this.length === 1 && (this.words[0] | 0) < num) {
		        this.words[0] = num - (this.words[0] | 0);
		        this.negative = 0;
		        return this;
		      }

		      this.negative = 0;
		      this.isubn(num);
		      this.negative = 1;
		      return this;
		    }

		    // Add without checks
		    return this._iaddn(num);
		  };

		  BN.prototype._iaddn = function _iaddn (num) {
		    this.words[0] += num;

		    // Carry
		    for (var i = 0; i < this.length && this.words[i] >= 0x4000000; i++) {
		      this.words[i] -= 0x4000000;
		      if (i === this.length - 1) {
		        this.words[i + 1] = 1;
		      } else {
		        this.words[i + 1]++;
		      }
		    }
		    this.length = Math.max(this.length, i + 1);

		    return this;
		  };

		  // Subtract plain number `num` from `this`
		  BN.prototype.isubn = function isubn (num) {
		    assert(typeof num === 'number');
		    assert(num < 0x4000000);
		    if (num < 0) return this.iaddn(-num);

		    if (this.negative !== 0) {
		      this.negative = 0;
		      this.iaddn(num);
		      this.negative = 1;
		      return this;
		    }

		    this.words[0] -= num;

		    if (this.length === 1 && this.words[0] < 0) {
		      this.words[0] = -this.words[0];
		      this.negative = 1;
		    } else {
		      // Carry
		      for (var i = 0; i < this.length && this.words[i] < 0; i++) {
		        this.words[i] += 0x4000000;
		        this.words[i + 1] -= 1;
		      }
		    }

		    return this.strip();
		  };

		  BN.prototype.addn = function addn (num) {
		    return this.clone().iaddn(num);
		  };

		  BN.prototype.subn = function subn (num) {
		    return this.clone().isubn(num);
		  };

		  BN.prototype.iabs = function iabs () {
		    this.negative = 0;

		    return this;
		  };

		  BN.prototype.abs = function abs () {
		    return this.clone().iabs();
		  };

		  BN.prototype._ishlnsubmul = function _ishlnsubmul (num, mul, shift) {
		    var len = num.length + shift;
		    var i;

		    this._expand(len);

		    var w;
		    var carry = 0;
		    for (i = 0; i < num.length; i++) {
		      w = (this.words[i + shift] | 0) + carry;
		      var right = (num.words[i] | 0) * mul;
		      w -= right & 0x3ffffff;
		      carry = (w >> 26) - ((right / 0x4000000) | 0);
		      this.words[i + shift] = w & 0x3ffffff;
		    }
		    for (; i < this.length - shift; i++) {
		      w = (this.words[i + shift] | 0) + carry;
		      carry = w >> 26;
		      this.words[i + shift] = w & 0x3ffffff;
		    }

		    if (carry === 0) return this.strip();

		    // Subtraction overflow
		    assert(carry === -1);
		    carry = 0;
		    for (i = 0; i < this.length; i++) {
		      w = -(this.words[i] | 0) + carry;
		      carry = w >> 26;
		      this.words[i] = w & 0x3ffffff;
		    }
		    this.negative = 1;

		    return this.strip();
		  };

		  BN.prototype._wordDiv = function _wordDiv (num, mode) {
		    var shift = this.length - num.length;

		    var a = this.clone();
		    var b = num;

		    // Normalize
		    var bhi = b.words[b.length - 1] | 0;
		    var bhiBits = this._countBits(bhi);
		    shift = 26 - bhiBits;
		    if (shift !== 0) {
		      b = b.ushln(shift);
		      a.iushln(shift);
		      bhi = b.words[b.length - 1] | 0;
		    }

		    // Initialize quotient
		    var m = a.length - b.length;
		    var q;

		    if (mode !== 'mod') {
		      q = new BN(null);
		      q.length = m + 1;
		      q.words = new Array(q.length);
		      for (var i = 0; i < q.length; i++) {
		        q.words[i] = 0;
		      }
		    }

		    var diff = a.clone()._ishlnsubmul(b, 1, m);
		    if (diff.negative === 0) {
		      a = diff;
		      if (q) {
		        q.words[m] = 1;
		      }
		    }

		    for (var j = m - 1; j >= 0; j--) {
		      var qj = (a.words[b.length + j] | 0) * 0x4000000 +
		        (a.words[b.length + j - 1] | 0);

		      // NOTE: (qj / bhi) is (0x3ffffff * 0x4000000 + 0x3ffffff) / 0x2000000 max
		      // (0x7ffffff)
		      qj = Math.min((qj / bhi) | 0, 0x3ffffff);

		      a._ishlnsubmul(b, qj, j);
		      while (a.negative !== 0) {
		        qj--;
		        a.negative = 0;
		        a._ishlnsubmul(b, 1, j);
		        if (!a.isZero()) {
		          a.negative ^= 1;
		        }
		      }
		      if (q) {
		        q.words[j] = qj;
		      }
		    }
		    if (q) {
		      q.strip();
		    }
		    a.strip();

		    // Denormalize
		    if (mode !== 'div' && shift !== 0) {
		      a.iushrn(shift);
		    }

		    return {
		      div: q || null,
		      mod: a
		    };
		  };

		  // NOTE: 1) `mode` can be set to `mod` to request mod only,
		  //       to `div` to request div only, or be absent to
		  //       request both div & mod
		  //       2) `positive` is true if unsigned mod is requested
		  BN.prototype.divmod = function divmod (num, mode, positive) {
		    assert(!num.isZero());

		    if (this.isZero()) {
		      return {
		        div: new BN(0),
		        mod: new BN(0)
		      };
		    }

		    var div, mod, res;
		    if (this.negative !== 0 && num.negative === 0) {
		      res = this.neg().divmod(num, mode);

		      if (mode !== 'mod') {
		        div = res.div.neg();
		      }

		      if (mode !== 'div') {
		        mod = res.mod.neg();
		        if (positive && mod.negative !== 0) {
		          mod.iadd(num);
		        }
		      }

		      return {
		        div: div,
		        mod: mod
		      };
		    }

		    if (this.negative === 0 && num.negative !== 0) {
		      res = this.divmod(num.neg(), mode);

		      if (mode !== 'mod') {
		        div = res.div.neg();
		      }

		      return {
		        div: div,
		        mod: res.mod
		      };
		    }

		    if ((this.negative & num.negative) !== 0) {
		      res = this.neg().divmod(num.neg(), mode);

		      if (mode !== 'div') {
		        mod = res.mod.neg();
		        if (positive && mod.negative !== 0) {
		          mod.isub(num);
		        }
		      }

		      return {
		        div: res.div,
		        mod: mod
		      };
		    }

		    // Both numbers are positive at this point

		    // Strip both numbers to approximate shift value
		    if (num.length > this.length || this.cmp(num) < 0) {
		      return {
		        div: new BN(0),
		        mod: this
		      };
		    }

		    // Very short reduction
		    if (num.length === 1) {
		      if (mode === 'div') {
		        return {
		          div: this.divn(num.words[0]),
		          mod: null
		        };
		      }

		      if (mode === 'mod') {
		        return {
		          div: null,
		          mod: new BN(this.modn(num.words[0]))
		        };
		      }

		      return {
		        div: this.divn(num.words[0]),
		        mod: new BN(this.modn(num.words[0]))
		      };
		    }

		    return this._wordDiv(num, mode);
		  };

		  // Find `this` / `num`
		  BN.prototype.div = function div (num) {
		    return this.divmod(num, 'div', false).div;
		  };

		  // Find `this` % `num`
		  BN.prototype.mod = function mod (num) {
		    return this.divmod(num, 'mod', false).mod;
		  };

		  BN.prototype.umod = function umod (num) {
		    return this.divmod(num, 'mod', true).mod;
		  };

		  // Find Round(`this` / `num`)
		  BN.prototype.divRound = function divRound (num) {
		    var dm = this.divmod(num);

		    // Fast case - exact division
		    if (dm.mod.isZero()) return dm.div;

		    var mod = dm.div.negative !== 0 ? dm.mod.isub(num) : dm.mod;

		    var half = num.ushrn(1);
		    var r2 = num.andln(1);
		    var cmp = mod.cmp(half);

		    // Round down
		    if (cmp < 0 || r2 === 1 && cmp === 0) return dm.div;

		    // Round up
		    return dm.div.negative !== 0 ? dm.div.isubn(1) : dm.div.iaddn(1);
		  };

		  BN.prototype.modn = function modn (num) {
		    assert(num <= 0x3ffffff);
		    var p = (1 << 26) % num;

		    var acc = 0;
		    for (var i = this.length - 1; i >= 0; i--) {
		      acc = (p * acc + (this.words[i] | 0)) % num;
		    }

		    return acc;
		  };

		  // In-place division by number
		  BN.prototype.idivn = function idivn (num) {
		    assert(num <= 0x3ffffff);

		    var carry = 0;
		    for (var i = this.length - 1; i >= 0; i--) {
		      var w = (this.words[i] | 0) + carry * 0x4000000;
		      this.words[i] = (w / num) | 0;
		      carry = w % num;
		    }

		    return this.strip();
		  };

		  BN.prototype.divn = function divn (num) {
		    return this.clone().idivn(num);
		  };

		  BN.prototype.egcd = function egcd (p) {
		    assert(p.negative === 0);
		    assert(!p.isZero());

		    var x = this;
		    var y = p.clone();

		    if (x.negative !== 0) {
		      x = x.umod(p);
		    } else {
		      x = x.clone();
		    }

		    // A * x + B * y = x
		    var A = new BN(1);
		    var B = new BN(0);

		    // C * x + D * y = y
		    var C = new BN(0);
		    var D = new BN(1);

		    var g = 0;

		    while (x.isEven() && y.isEven()) {
		      x.iushrn(1);
		      y.iushrn(1);
		      ++g;
		    }

		    var yp = y.clone();
		    var xp = x.clone();

		    while (!x.isZero()) {
		      for (var i = 0, im = 1; (x.words[0] & im) === 0 && i < 26; ++i, im <<= 1);
		      if (i > 0) {
		        x.iushrn(i);
		        while (i-- > 0) {
		          if (A.isOdd() || B.isOdd()) {
		            A.iadd(yp);
		            B.isub(xp);
		          }

		          A.iushrn(1);
		          B.iushrn(1);
		        }
		      }

		      for (var j = 0, jm = 1; (y.words[0] & jm) === 0 && j < 26; ++j, jm <<= 1);
		      if (j > 0) {
		        y.iushrn(j);
		        while (j-- > 0) {
		          if (C.isOdd() || D.isOdd()) {
		            C.iadd(yp);
		            D.isub(xp);
		          }

		          C.iushrn(1);
		          D.iushrn(1);
		        }
		      }

		      if (x.cmp(y) >= 0) {
		        x.isub(y);
		        A.isub(C);
		        B.isub(D);
		      } else {
		        y.isub(x);
		        C.isub(A);
		        D.isub(B);
		      }
		    }

		    return {
		      a: C,
		      b: D,
		      gcd: y.iushln(g)
		    };
		  };

		  // This is reduced incarnation of the binary EEA
		  // above, designated to invert members of the
		  // _prime_ fields F(p) at a maximal speed
		  BN.prototype._invmp = function _invmp (p) {
		    assert(p.negative === 0);
		    assert(!p.isZero());

		    var a = this;
		    var b = p.clone();

		    if (a.negative !== 0) {
		      a = a.umod(p);
		    } else {
		      a = a.clone();
		    }

		    var x1 = new BN(1);
		    var x2 = new BN(0);

		    var delta = b.clone();

		    while (a.cmpn(1) > 0 && b.cmpn(1) > 0) {
		      for (var i = 0, im = 1; (a.words[0] & im) === 0 && i < 26; ++i, im <<= 1);
		      if (i > 0) {
		        a.iushrn(i);
		        while (i-- > 0) {
		          if (x1.isOdd()) {
		            x1.iadd(delta);
		          }

		          x1.iushrn(1);
		        }
		      }

		      for (var j = 0, jm = 1; (b.words[0] & jm) === 0 && j < 26; ++j, jm <<= 1);
		      if (j > 0) {
		        b.iushrn(j);
		        while (j-- > 0) {
		          if (x2.isOdd()) {
		            x2.iadd(delta);
		          }

		          x2.iushrn(1);
		        }
		      }

		      if (a.cmp(b) >= 0) {
		        a.isub(b);
		        x1.isub(x2);
		      } else {
		        b.isub(a);
		        x2.isub(x1);
		      }
		    }

		    var res;
		    if (a.cmpn(1) === 0) {
		      res = x1;
		    } else {
		      res = x2;
		    }

		    if (res.cmpn(0) < 0) {
		      res.iadd(p);
		    }

		    return res;
		  };

		  BN.prototype.gcd = function gcd (num) {
		    if (this.isZero()) return num.abs();
		    if (num.isZero()) return this.abs();

		    var a = this.clone();
		    var b = num.clone();
		    a.negative = 0;
		    b.negative = 0;

		    // Remove common factor of two
		    for (var shift = 0; a.isEven() && b.isEven(); shift++) {
		      a.iushrn(1);
		      b.iushrn(1);
		    }

		    do {
		      while (a.isEven()) {
		        a.iushrn(1);
		      }
		      while (b.isEven()) {
		        b.iushrn(1);
		      }

		      var r = a.cmp(b);
		      if (r < 0) {
		        // Swap `a` and `b` to make `a` always bigger than `b`
		        var t = a;
		        a = b;
		        b = t;
		      } else if (r === 0 || b.cmpn(1) === 0) {
		        break;
		      }

		      a.isub(b);
		    } while (true);

		    return b.iushln(shift);
		  };

		  // Invert number in the field F(num)
		  BN.prototype.invm = function invm (num) {
		    return this.egcd(num).a.umod(num);
		  };

		  BN.prototype.isEven = function isEven () {
		    return (this.words[0] & 1) === 0;
		  };

		  BN.prototype.isOdd = function isOdd () {
		    return (this.words[0] & 1) === 1;
		  };

		  // And first word and num
		  BN.prototype.andln = function andln (num) {
		    return this.words[0] & num;
		  };

		  // Increment at the bit position in-line
		  BN.prototype.bincn = function bincn (bit) {
		    assert(typeof bit === 'number');
		    var r = bit % 26;
		    var s = (bit - r) / 26;
		    var q = 1 << r;

		    // Fast case: bit is much higher than all existing words
		    if (this.length <= s) {
		      this._expand(s + 1);
		      this.words[s] |= q;
		      return this;
		    }

		    // Add bit and propagate, if needed
		    var carry = q;
		    for (var i = s; carry !== 0 && i < this.length; i++) {
		      var w = this.words[i] | 0;
		      w += carry;
		      carry = w >>> 26;
		      w &= 0x3ffffff;
		      this.words[i] = w;
		    }
		    if (carry !== 0) {
		      this.words[i] = carry;
		      this.length++;
		    }
		    return this;
		  };

		  BN.prototype.isZero = function isZero () {
		    return this.length === 1 && this.words[0] === 0;
		  };

		  BN.prototype.cmpn = function cmpn (num) {
		    var negative = num < 0;

		    if (this.negative !== 0 && !negative) return -1;
		    if (this.negative === 0 && negative) return 1;

		    this.strip();

		    var res;
		    if (this.length > 1) {
		      res = 1;
		    } else {
		      if (negative) {
		        num = -num;
		      }

		      assert(num <= 0x3ffffff, 'Number is too big');

		      var w = this.words[0] | 0;
		      res = w === num ? 0 : w < num ? -1 : 1;
		    }
		    if (this.negative !== 0) return -res | 0;
		    return res;
		  };

		  // Compare two numbers and return:
		  // 1 - if `this` > `num`
		  // 0 - if `this` == `num`
		  // -1 - if `this` < `num`
		  BN.prototype.cmp = function cmp (num) {
		    if (this.negative !== 0 && num.negative === 0) return -1;
		    if (this.negative === 0 && num.negative !== 0) return 1;

		    var res = this.ucmp(num);
		    if (this.negative !== 0) return -res | 0;
		    return res;
		  };

		  // Unsigned comparison
		  BN.prototype.ucmp = function ucmp (num) {
		    // At this point both numbers have the same sign
		    if (this.length > num.length) return 1;
		    if (this.length < num.length) return -1;

		    var res = 0;
		    for (var i = this.length - 1; i >= 0; i--) {
		      var a = this.words[i] | 0;
		      var b = num.words[i] | 0;

		      if (a === b) continue;
		      if (a < b) {
		        res = -1;
		      } else if (a > b) {
		        res = 1;
		      }
		      break;
		    }
		    return res;
		  };

		  BN.prototype.gtn = function gtn (num) {
		    return this.cmpn(num) === 1;
		  };

		  BN.prototype.gt = function gt (num) {
		    return this.cmp(num) === 1;
		  };

		  BN.prototype.gten = function gten (num) {
		    return this.cmpn(num) >= 0;
		  };

		  BN.prototype.gte = function gte (num) {
		    return this.cmp(num) >= 0;
		  };

		  BN.prototype.ltn = function ltn (num) {
		    return this.cmpn(num) === -1;
		  };

		  BN.prototype.lt = function lt (num) {
		    return this.cmp(num) === -1;
		  };

		  BN.prototype.lten = function lten (num) {
		    return this.cmpn(num) <= 0;
		  };

		  BN.prototype.lte = function lte (num) {
		    return this.cmp(num) <= 0;
		  };

		  BN.prototype.eqn = function eqn (num) {
		    return this.cmpn(num) === 0;
		  };

		  BN.prototype.eq = function eq (num) {
		    return this.cmp(num) === 0;
		  };

		  //
		  // A reduce context, could be using montgomery or something better, depending
		  // on the `m` itself.
		  //
		  BN.red = function red (num) {
		    return new Red(num);
		  };

		  BN.prototype.toRed = function toRed (ctx) {
		    assert(!this.red, 'Already a number in reduction context');
		    assert(this.negative === 0, 'red works only with positives');
		    return ctx.convertTo(this)._forceRed(ctx);
		  };

		  BN.prototype.fromRed = function fromRed () {
		    assert(this.red, 'fromRed works only with numbers in reduction context');
		    return this.red.convertFrom(this);
		  };

		  BN.prototype._forceRed = function _forceRed (ctx) {
		    this.red = ctx;
		    return this;
		  };

		  BN.prototype.forceRed = function forceRed (ctx) {
		    assert(!this.red, 'Already a number in reduction context');
		    return this._forceRed(ctx);
		  };

		  BN.prototype.redAdd = function redAdd (num) {
		    assert(this.red, 'redAdd works only with red numbers');
		    return this.red.add(this, num);
		  };

		  BN.prototype.redIAdd = function redIAdd (num) {
		    assert(this.red, 'redIAdd works only with red numbers');
		    return this.red.iadd(this, num);
		  };

		  BN.prototype.redSub = function redSub (num) {
		    assert(this.red, 'redSub works only with red numbers');
		    return this.red.sub(this, num);
		  };

		  BN.prototype.redISub = function redISub (num) {
		    assert(this.red, 'redISub works only with red numbers');
		    return this.red.isub(this, num);
		  };

		  BN.prototype.redShl = function redShl (num) {
		    assert(this.red, 'redShl works only with red numbers');
		    return this.red.shl(this, num);
		  };

		  BN.prototype.redMul = function redMul (num) {
		    assert(this.red, 'redMul works only with red numbers');
		    this.red._verify2(this, num);
		    return this.red.mul(this, num);
		  };

		  BN.prototype.redIMul = function redIMul (num) {
		    assert(this.red, 'redMul works only with red numbers');
		    this.red._verify2(this, num);
		    return this.red.imul(this, num);
		  };

		  BN.prototype.redSqr = function redSqr () {
		    assert(this.red, 'redSqr works only with red numbers');
		    this.red._verify1(this);
		    return this.red.sqr(this);
		  };

		  BN.prototype.redISqr = function redISqr () {
		    assert(this.red, 'redISqr works only with red numbers');
		    this.red._verify1(this);
		    return this.red.isqr(this);
		  };

		  // Square root over p
		  BN.prototype.redSqrt = function redSqrt () {
		    assert(this.red, 'redSqrt works only with red numbers');
		    this.red._verify1(this);
		    return this.red.sqrt(this);
		  };

		  BN.prototype.redInvm = function redInvm () {
		    assert(this.red, 'redInvm works only with red numbers');
		    this.red._verify1(this);
		    return this.red.invm(this);
		  };

		  // Return negative clone of `this` % `red modulo`
		  BN.prototype.redNeg = function redNeg () {
		    assert(this.red, 'redNeg works only with red numbers');
		    this.red._verify1(this);
		    return this.red.neg(this);
		  };

		  BN.prototype.redPow = function redPow (num) {
		    assert(this.red && !num.red, 'redPow(normalNum)');
		    this.red._verify1(this);
		    return this.red.pow(this, num);
		  };

		  // Prime numbers with efficient reduction
		  var primes = {
		    k256: null,
		    p224: null,
		    p192: null,
		    p25519: null
		  };

		  // Pseudo-Mersenne prime
		  function MPrime (name, p) {
		    // P = 2 ^ N - K
		    this.name = name;
		    this.p = new BN(p, 16);
		    this.n = this.p.bitLength();
		    this.k = new BN(1).iushln(this.n).isub(this.p);

		    this.tmp = this._tmp();
		  }

		  MPrime.prototype._tmp = function _tmp () {
		    var tmp = new BN(null);
		    tmp.words = new Array(Math.ceil(this.n / 13));
		    return tmp;
		  };

		  MPrime.prototype.ireduce = function ireduce (num) {
		    // Assumes that `num` is less than `P^2`
		    // num = HI * (2 ^ N - K) + HI * K + LO = HI * K + LO (mod P)
		    var r = num;
		    var rlen;

		    do {
		      this.split(r, this.tmp);
		      r = this.imulK(r);
		      r = r.iadd(this.tmp);
		      rlen = r.bitLength();
		    } while (rlen > this.n);

		    var cmp = rlen < this.n ? -1 : r.ucmp(this.p);
		    if (cmp === 0) {
		      r.words[0] = 0;
		      r.length = 1;
		    } else if (cmp > 0) {
		      r.isub(this.p);
		    } else {
		      if (r.strip !== undefined) {
		        // r is BN v4 instance
		        r.strip();
		      } else {
		        // r is BN v5 instance
		        r._strip();
		      }
		    }

		    return r;
		  };

		  MPrime.prototype.split = function split (input, out) {
		    input.iushrn(this.n, 0, out);
		  };

		  MPrime.prototype.imulK = function imulK (num) {
		    return num.imul(this.k);
		  };

		  function K256 () {
		    MPrime.call(
		      this,
		      'k256',
		      'ffffffff ffffffff ffffffff ffffffff ffffffff ffffffff fffffffe fffffc2f');
		  }
		  inherits(K256, MPrime);

		  K256.prototype.split = function split (input, output) {
		    // 256 = 9 * 26 + 22
		    var mask = 0x3fffff;

		    var outLen = Math.min(input.length, 9);
		    for (var i = 0; i < outLen; i++) {
		      output.words[i] = input.words[i];
		    }
		    output.length = outLen;

		    if (input.length <= 9) {
		      input.words[0] = 0;
		      input.length = 1;
		      return;
		    }

		    // Shift by 9 limbs
		    var prev = input.words[9];
		    output.words[output.length++] = prev & mask;

		    for (i = 10; i < input.length; i++) {
		      var next = input.words[i] | 0;
		      input.words[i - 10] = ((next & mask) << 4) | (prev >>> 22);
		      prev = next;
		    }
		    prev >>>= 22;
		    input.words[i - 10] = prev;
		    if (prev === 0 && input.length > 10) {
		      input.length -= 10;
		    } else {
		      input.length -= 9;
		    }
		  };

		  K256.prototype.imulK = function imulK (num) {
		    // K = 0x1000003d1 = [ 0x40, 0x3d1 ]
		    num.words[num.length] = 0;
		    num.words[num.length + 1] = 0;
		    num.length += 2;

		    // bounded at: 0x40 * 0x3ffffff + 0x3d0 = 0x100000390
		    var lo = 0;
		    for (var i = 0; i < num.length; i++) {
		      var w = num.words[i] | 0;
		      lo += w * 0x3d1;
		      num.words[i] = lo & 0x3ffffff;
		      lo = w * 0x40 + ((lo / 0x4000000) | 0);
		    }

		    // Fast length reduction
		    if (num.words[num.length - 1] === 0) {
		      num.length--;
		      if (num.words[num.length - 1] === 0) {
		        num.length--;
		      }
		    }
		    return num;
		  };

		  function P224 () {
		    MPrime.call(
		      this,
		      'p224',
		      'ffffffff ffffffff ffffffff ffffffff 00000000 00000000 00000001');
		  }
		  inherits(P224, MPrime);

		  function P192 () {
		    MPrime.call(
		      this,
		      'p192',
		      'ffffffff ffffffff ffffffff fffffffe ffffffff ffffffff');
		  }
		  inherits(P192, MPrime);

		  function P25519 () {
		    // 2 ^ 255 - 19
		    MPrime.call(
		      this,
		      '25519',
		      '7fffffffffffffff ffffffffffffffff ffffffffffffffff ffffffffffffffed');
		  }
		  inherits(P25519, MPrime);

		  P25519.prototype.imulK = function imulK (num) {
		    // K = 0x13
		    var carry = 0;
		    for (var i = 0; i < num.length; i++) {
		      var hi = (num.words[i] | 0) * 0x13 + carry;
		      var lo = hi & 0x3ffffff;
		      hi >>>= 26;

		      num.words[i] = lo;
		      carry = hi;
		    }
		    if (carry !== 0) {
		      num.words[num.length++] = carry;
		    }
		    return num;
		  };

		  // Exported mostly for testing purposes, use plain name instead
		  BN._prime = function prime (name) {
		    // Cached version of prime
		    if (primes[name]) return primes[name];

		    var prime;
		    if (name === 'k256') {
		      prime = new K256();
		    } else if (name === 'p224') {
		      prime = new P224();
		    } else if (name === 'p192') {
		      prime = new P192();
		    } else if (name === 'p25519') {
		      prime = new P25519();
		    } else {
		      throw new Error('Unknown prime ' + name);
		    }
		    primes[name] = prime;

		    return prime;
		  };

		  //
		  // Base reduction engine
		  //
		  function Red (m) {
		    if (typeof m === 'string') {
		      var prime = BN._prime(m);
		      this.m = prime.p;
		      this.prime = prime;
		    } else {
		      assert(m.gtn(1), 'modulus must be greater than 1');
		      this.m = m;
		      this.prime = null;
		    }
		  }

		  Red.prototype._verify1 = function _verify1 (a) {
		    assert(a.negative === 0, 'red works only with positives');
		    assert(a.red, 'red works only with red numbers');
		  };

		  Red.prototype._verify2 = function _verify2 (a, b) {
		    assert((a.negative | b.negative) === 0, 'red works only with positives');
		    assert(a.red && a.red === b.red,
		      'red works only with red numbers');
		  };

		  Red.prototype.imod = function imod (a) {
		    if (this.prime) return this.prime.ireduce(a)._forceRed(this);
		    return a.umod(this.m)._forceRed(this);
		  };

		  Red.prototype.neg = function neg (a) {
		    if (a.isZero()) {
		      return a.clone();
		    }

		    return this.m.sub(a)._forceRed(this);
		  };

		  Red.prototype.add = function add (a, b) {
		    this._verify2(a, b);

		    var res = a.add(b);
		    if (res.cmp(this.m) >= 0) {
		      res.isub(this.m);
		    }
		    return res._forceRed(this);
		  };

		  Red.prototype.iadd = function iadd (a, b) {
		    this._verify2(a, b);

		    var res = a.iadd(b);
		    if (res.cmp(this.m) >= 0) {
		      res.isub(this.m);
		    }
		    return res;
		  };

		  Red.prototype.sub = function sub (a, b) {
		    this._verify2(a, b);

		    var res = a.sub(b);
		    if (res.cmpn(0) < 0) {
		      res.iadd(this.m);
		    }
		    return res._forceRed(this);
		  };

		  Red.prototype.isub = function isub (a, b) {
		    this._verify2(a, b);

		    var res = a.isub(b);
		    if (res.cmpn(0) < 0) {
		      res.iadd(this.m);
		    }
		    return res;
		  };

		  Red.prototype.shl = function shl (a, num) {
		    this._verify1(a);
		    return this.imod(a.ushln(num));
		  };

		  Red.prototype.imul = function imul (a, b) {
		    this._verify2(a, b);
		    return this.imod(a.imul(b));
		  };

		  Red.prototype.mul = function mul (a, b) {
		    this._verify2(a, b);
		    return this.imod(a.mul(b));
		  };

		  Red.prototype.isqr = function isqr (a) {
		    return this.imul(a, a.clone());
		  };

		  Red.prototype.sqr = function sqr (a) {
		    return this.mul(a, a);
		  };

		  Red.prototype.sqrt = function sqrt (a) {
		    if (a.isZero()) return a.clone();

		    var mod3 = this.m.andln(3);
		    assert(mod3 % 2 === 1);

		    // Fast case
		    if (mod3 === 3) {
		      var pow = this.m.add(new BN(1)).iushrn(2);
		      return this.pow(a, pow);
		    }

		    // Tonelli-Shanks algorithm (Totally unoptimized and slow)
		    //
		    // Find Q and S, that Q * 2 ^ S = (P - 1)
		    var q = this.m.subn(1);
		    var s = 0;
		    while (!q.isZero() && q.andln(1) === 0) {
		      s++;
		      q.iushrn(1);
		    }
		    assert(!q.isZero());

		    var one = new BN(1).toRed(this);
		    var nOne = one.redNeg();

		    // Find quadratic non-residue
		    // NOTE: Max is such because of generalized Riemann hypothesis.
		    var lpow = this.m.subn(1).iushrn(1);
		    var z = this.m.bitLength();
		    z = new BN(2 * z * z).toRed(this);

		    while (this.pow(z, lpow).cmp(nOne) !== 0) {
		      z.redIAdd(nOne);
		    }

		    var c = this.pow(z, q);
		    var r = this.pow(a, q.addn(1).iushrn(1));
		    var t = this.pow(a, q);
		    var m = s;
		    while (t.cmp(one) !== 0) {
		      var tmp = t;
		      for (var i = 0; tmp.cmp(one) !== 0; i++) {
		        tmp = tmp.redSqr();
		      }
		      assert(i < m);
		      var b = this.pow(c, new BN(1).iushln(m - i - 1));

		      r = r.redMul(b);
		      c = b.redSqr();
		      t = t.redMul(c);
		      m = i;
		    }

		    return r;
		  };

		  Red.prototype.invm = function invm (a) {
		    var inv = a._invmp(this.m);
		    if (inv.negative !== 0) {
		      inv.negative = 0;
		      return this.imod(inv).redNeg();
		    } else {
		      return this.imod(inv);
		    }
		  };

		  Red.prototype.pow = function pow (a, num) {
		    if (num.isZero()) return new BN(1).toRed(this);
		    if (num.cmpn(1) === 0) return a.clone();

		    var windowSize = 4;
		    var wnd = new Array(1 << windowSize);
		    wnd[0] = new BN(1).toRed(this);
		    wnd[1] = a;
		    for (var i = 2; i < wnd.length; i++) {
		      wnd[i] = this.mul(wnd[i - 1], a);
		    }

		    var res = wnd[0];
		    var current = 0;
		    var currentLen = 0;
		    var start = num.bitLength() % 26;
		    if (start === 0) {
		      start = 26;
		    }

		    for (i = num.length - 1; i >= 0; i--) {
		      var word = num.words[i];
		      for (var j = start - 1; j >= 0; j--) {
		        var bit = (word >> j) & 1;
		        if (res !== wnd[0]) {
		          res = this.sqr(res);
		        }

		        if (bit === 0 && current === 0) {
		          currentLen = 0;
		          continue;
		        }

		        current <<= 1;
		        current |= bit;
		        currentLen++;
		        if (currentLen !== windowSize && (i !== 0 || j !== 0)) continue;

		        res = this.mul(res, wnd[current]);
		        currentLen = 0;
		        current = 0;
		      }
		      start = 26;
		    }

		    return res;
		  };

		  Red.prototype.convertTo = function convertTo (num) {
		    var r = num.umod(this.m);

		    return r === num ? r.clone() : r;
		  };

		  Red.prototype.convertFrom = function convertFrom (num) {
		    var res = num.clone();
		    res.red = null;
		    return res;
		  };

		  //
		  // Montgomery method engine
		  //

		  BN.mont = function mont (num) {
		    return new Mont(num);
		  };

		  function Mont (m) {
		    Red.call(this, m);

		    this.shift = this.m.bitLength();
		    if (this.shift % 26 !== 0) {
		      this.shift += 26 - (this.shift % 26);
		    }

		    this.r = new BN(1).iushln(this.shift);
		    this.r2 = this.imod(this.r.sqr());
		    this.rinv = this.r._invmp(this.m);

		    this.minv = this.rinv.mul(this.r).isubn(1).div(this.m);
		    this.minv = this.minv.umod(this.r);
		    this.minv = this.r.sub(this.minv);
		  }
		  inherits(Mont, Red);

		  Mont.prototype.convertTo = function convertTo (num) {
		    return this.imod(num.ushln(this.shift));
		  };

		  Mont.prototype.convertFrom = function convertFrom (num) {
		    var r = this.imod(num.mul(this.rinv));
		    r.red = null;
		    return r;
		  };

		  Mont.prototype.imul = function imul (a, b) {
		    if (a.isZero() || b.isZero()) {
		      a.words[0] = 0;
		      a.length = 1;
		      return a;
		    }

		    var t = a.imul(b);
		    var c = t.maskn(this.shift).mul(this.minv).imaskn(this.shift).mul(this.m);
		    var u = t.isub(c).iushrn(this.shift);
		    var res = u;

		    if (u.cmp(this.m) >= 0) {
		      res = u.isub(this.m);
		    } else if (u.cmpn(0) < 0) {
		      res = u.iadd(this.m);
		    }

		    return res._forceRed(this);
		  };

		  Mont.prototype.mul = function mul (a, b) {
		    if (a.isZero() || b.isZero()) return new BN(0)._forceRed(this);

		    var t = a.mul(b);
		    var c = t.maskn(this.shift).mul(this.minv).imaskn(this.shift).mul(this.m);
		    var u = t.isub(c).iushrn(this.shift);
		    var res = u;
		    if (u.cmp(this.m) >= 0) {
		      res = u.isub(this.m);
		    } else if (u.cmpn(0) < 0) {
		      res = u.iadd(this.m);
		    }

		    return res._forceRed(this);
		  };

		  Mont.prototype.invm = function invm (a) {
		    // (AR)^-1 * R^2 = (A^-1 * R^-1) * R^2 = A^-1 * R
		    var res = this.imod(a._invmp(this.m).mul(this.r2));
		    return res._forceRed(this);
		  };
		})(module, bn); 
	} (bn$1));
	return bn$1.exports;
}

var withPublic_1;
var hasRequiredWithPublic;

function requireWithPublic () {
	if (hasRequiredWithPublic) return withPublic_1;
	hasRequiredWithPublic = 1;
	var BN = requireBn();
	var Buffer = require$$0$2.Buffer;

	function withPublic (paddedMsg, key) {
	  return Buffer.from(paddedMsg
	    .toRed(BN.mont(key.modulus))
	    .redPow(new BN(key.publicExponent))
	    .fromRed()
	    .toArray())
	}

	withPublic_1 = withPublic;
	return withPublic_1;
}

var browserifyRsa;
var hasRequiredBrowserifyRsa;

function requireBrowserifyRsa () {
	if (hasRequiredBrowserifyRsa) return browserifyRsa;
	hasRequiredBrowserifyRsa = 1;

	var BN = require$$0$5;
	var randomBytes = require$$1;
	var Buffer = require$$0$2.Buffer;

	function getr(priv) {
		var len = priv.modulus.byteLength();
		var r;
		do {
			r = new BN(randomBytes(len));
		} while (r.cmp(priv.modulus) >= 0 || !r.umod(priv.prime1) || !r.umod(priv.prime2));
		return r;
	}

	function blind(priv) {
		var r = getr(priv);
		var blinder = r.toRed(BN.mont(priv.modulus)).redPow(new BN(priv.publicExponent)).fromRed();
		return { blinder: blinder, unblinder: r.invm(priv.modulus) };
	}

	function crt(msg, priv) {
		var blinds = blind(priv);
		var len = priv.modulus.byteLength();
		var blinded = new BN(msg).mul(blinds.blinder).umod(priv.modulus);
		var c1 = blinded.toRed(BN.mont(priv.prime1));
		var c2 = blinded.toRed(BN.mont(priv.prime2));
		var qinv = priv.coefficient;
		var p = priv.prime1;
		var q = priv.prime2;
		var m1 = c1.redPow(priv.exponent1).fromRed();
		var m2 = c2.redPow(priv.exponent2).fromRed();
		var h = m1.isub(m2).imul(qinv).umod(p).imul(q);
		return m2.iadd(h).imul(blinds.unblinder).umod(priv.modulus).toArrayLike(Buffer, 'be', len);
	}
	crt.getr = getr;

	browserifyRsa = crt;
	return browserifyRsa;
}

var publicEncrypt$1;
var hasRequiredPublicEncrypt;

function requirePublicEncrypt () {
	if (hasRequiredPublicEncrypt) return publicEncrypt$1;
	hasRequiredPublicEncrypt = 1;
	var parseKeys = requireParseAsn1();
	var randomBytes = require$$1;
	var createHash = requireBrowser$1();
	var mgf = requireMgf();
	var xor = requireXor();
	var BN = requireBn();
	var withPublic = requireWithPublic();
	var crt = /*@__PURE__*/ requireBrowserifyRsa();
	var Buffer = require$$0$2.Buffer;

	publicEncrypt$1 = function publicEncrypt (publicKey, msg, reverse) {
	  var padding;
	  if (publicKey.padding) {
	    padding = publicKey.padding;
	  } else if (reverse) {
	    padding = 1;
	  } else {
	    padding = 4;
	  }
	  var key = parseKeys(publicKey);
	  var paddedMsg;
	  if (padding === 4) {
	    paddedMsg = oaep(key, msg);
	  } else if (padding === 1) {
	    paddedMsg = pkcs1(key, msg, reverse);
	  } else if (padding === 3) {
	    paddedMsg = new BN(msg);
	    if (paddedMsg.cmp(key.modulus) >= 0) {
	      throw new Error('data too long for modulus')
	    }
	  } else {
	    throw new Error('unknown padding')
	  }
	  if (reverse) {
	    return crt(paddedMsg, key)
	  } else {
	    return withPublic(paddedMsg, key)
	  }
	};

	function oaep (key, msg) {
	  var k = key.modulus.byteLength();
	  var mLen = msg.length;
	  var iHash = createHash('sha1').update(Buffer.alloc(0)).digest();
	  var hLen = iHash.length;
	  var hLen2 = 2 * hLen;
	  if (mLen > k - hLen2 - 2) {
	    throw new Error('message too long')
	  }
	  var ps = Buffer.alloc(k - mLen - hLen2 - 2);
	  var dblen = k - hLen - 1;
	  var seed = randomBytes(hLen);
	  var maskedDb = xor(Buffer.concat([iHash, ps, Buffer.alloc(1, 1), msg], dblen), mgf(seed, dblen));
	  var maskedSeed = xor(seed, mgf(maskedDb, hLen));
	  return new BN(Buffer.concat([Buffer.alloc(1), maskedSeed, maskedDb], k))
	}
	function pkcs1 (key, msg, reverse) {
	  var mLen = msg.length;
	  var k = key.modulus.byteLength();
	  if (mLen > k - 11) {
	    throw new Error('message too long')
	  }
	  var ps;
	  if (reverse) {
	    ps = Buffer.alloc(k - mLen - 3, 0xff);
	  } else {
	    ps = nonZero(k - mLen - 3);
	  }
	  return new BN(Buffer.concat([Buffer.from([0, reverse ? 1 : 2]), ps, Buffer.alloc(1), msg], k))
	}
	function nonZero (len) {
	  var out = Buffer.allocUnsafe(len);
	  var i = 0;
	  var cache = randomBytes(len * 2);
	  var cur = 0;
	  var num;
	  while (i < len) {
	    if (cur === cache.length) {
	      cache = randomBytes(len * 2);
	      cur = 0;
	    }
	    num = cache[cur++];
	    if (num) {
	      out[i++] = num;
	    }
	  }
	  return out
	}
	return publicEncrypt$1;
}

var privateDecrypt$1;
var hasRequiredPrivateDecrypt;

function requirePrivateDecrypt () {
	if (hasRequiredPrivateDecrypt) return privateDecrypt$1;
	hasRequiredPrivateDecrypt = 1;
	var parseKeys = requireParseAsn1();
	var mgf = requireMgf();
	var xor = requireXor();
	var BN = requireBn();
	var crt = /*@__PURE__*/ requireBrowserifyRsa();
	var createHash = requireBrowser$1();
	var withPublic = requireWithPublic();
	var Buffer = require$$0$2.Buffer;

	privateDecrypt$1 = function privateDecrypt (privateKey, enc, reverse) {
	  var padding;
	  if (privateKey.padding) {
	    padding = privateKey.padding;
	  } else if (reverse) {
	    padding = 1;
	  } else {
	    padding = 4;
	  }

	  var key = parseKeys(privateKey);
	  var k = key.modulus.byteLength();
	  if (enc.length > k || new BN(enc).cmp(key.modulus) >= 0) {
	    throw new Error('decryption error')
	  }
	  var msg;
	  if (reverse) {
	    msg = withPublic(new BN(enc), key);
	  } else {
	    msg = crt(enc, key);
	  }
	  var zBuffer = Buffer.alloc(k - msg.length);
	  msg = Buffer.concat([zBuffer, msg], k);
	  if (padding === 4) {
	    return oaep(key, msg)
	  } else if (padding === 1) {
	    return pkcs1(key, msg, reverse)
	  } else if (padding === 3) {
	    return msg
	  } else {
	    throw new Error('unknown padding')
	  }
	};

	function oaep (key, msg) {
	  var k = key.modulus.byteLength();
	  var iHash = createHash('sha1').update(Buffer.alloc(0)).digest();
	  var hLen = iHash.length;
	  if (msg[0] !== 0) {
	    throw new Error('decryption error')
	  }
	  var maskedSeed = msg.slice(1, hLen + 1);
	  var maskedDb = msg.slice(hLen + 1);
	  var seed = xor(maskedSeed, mgf(maskedDb, hLen));
	  var db = xor(maskedDb, mgf(seed, k - hLen - 1));
	  if (compare(iHash, db.slice(0, hLen))) {
	    throw new Error('decryption error')
	  }
	  var i = hLen;
	  while (db[i] === 0) {
	    i++;
	  }
	  if (db[i++] !== 1) {
	    throw new Error('decryption error')
	  }
	  return db.slice(i)
	}

	function pkcs1 (key, msg, reverse) {
	  var p1 = msg.slice(0, 2);
	  var i = 2;
	  var status = 0;
	  while (msg[i++] !== 0) {
	    if (i >= msg.length) {
	      status++;
	      break
	    }
	  }
	  var ps = msg.slice(2, i - 1);

	  if ((p1.toString('hex') !== '0002' && !reverse) || (p1.toString('hex') !== '0001' && reverse)) {
	    status++;
	  }
	  if (ps.length < 8) {
	    status++;
	  }
	  if (status) {
	    throw new Error('decryption error')
	  }
	  return msg.slice(i)
	}
	function compare (a, b) {
	  a = Buffer.from(a);
	  b = Buffer.from(b);
	  var dif = 0;
	  var len = a.length;
	  if (a.length !== b.length) {
	    dif++;
	    len = Math.min(a.length, b.length);
	  }
	  var i = -1;
	  while (++i < len) {
	    dif += (a[i] ^ b[i]);
	  }
	  return dif
	}
	return privateDecrypt$1;
}

var hasRequiredBrowser;

function requireBrowser () {
	if (hasRequiredBrowser) return browser$2;
	hasRequiredBrowser = 1;
	(function (exports$1) {
		exports$1.publicEncrypt = requirePublicEncrypt();
		exports$1.privateDecrypt = requirePrivateDecrypt();

		exports$1.privateEncrypt = function privateEncrypt (key, buf) {
		  return exports$1.publicEncrypt(key, buf, true)
		};

		exports$1.publicDecrypt = function publicDecrypt (key, buf) {
		  return exports$1.privateDecrypt(key, buf, true)
		}; 
	} (browser$2));
	return browser$2;
}

var browserExports = requireBrowser();

function getSubtle() {
    const { crypto } = globalThis;
    if (!crypto?.subtle) {
        const url = 'https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto';
        throw new Error(`SubtleCrypto not supported. This feature is available only in secure contexts (HTTPS). ${url}`);
    }
    return crypto.subtle;
}
function computeMd5(input) {
    return Buffer.from(md5$1(utf8ToBinary(input)));
}
function computeSha1(input) {
    return Buffer.from(sha1(utf8ToBinary(input)));
}
function createCipheriv(algorithm, key, iv) {
    const suite = algorithm.toLowerCase();
    if (aesModes[suite]) {
        return browserExports$1.createCipheriv(suite, key, iv);
    }
    throw new TypeError(`Invalid suite type.  In browser only AES is supported but got ${algorithm}.`);
}
function createDecipheriv(algorithm, key, iv) {
    const suite = algorithm.toLowerCase();
    if (aesModes[suite]) {
        return browserExports$1.createDecipheriv(suite, key, iv);
    }
    throw new TypeError(`Invalid suite type.  In browser only AES is supported but got ${algorithm}.`);
}
function publicEncrypt(publicKey, buffer) {
    return browserExports.publicEncrypt(publicKey, buffer);
}
function privateDecrypt(privateKey, buffer) {
    return browserExports.privateDecrypt(privateKey, buffer);
}

/**
 * Computes a deterministic index for a given string or number key.
 * Used for deterministically selecting an entry from an ordered list
 * for various load balancing and partitioning purposes.
 *
 * @param lengthOfArray Number of items to select from
 * @param key Input string or number
 * @returns Array index between [0..lengthOfArray-1]
 */
function keyToArrayIndex(lengthOfArray, key) {
    if (!(Number.isSafeInteger(lengthOfArray) && lengthOfArray > 0)) {
        throw new Error(`lengthOfArray is not a safe positive integer! ${lengthOfArray}`);
    }
    if (lengthOfArray === 1) {
        // Fast common case
        return 0;
    }
    // Number key handling
    if (typeof key === 'number') {
        return Math.abs(key) % lengthOfArray;
    }
    // String key handling
    const intHash = computeMd5(key).readInt32LE(0);
    return Math.abs(intHash) % lengthOfArray;
}

/**
 * Browser-safe environment object.
 *
 * `process.env` is NOT available in browsers unless explicitly polyfilled
 * by the bundler or runtime. This guard prevents runtime errors and falls
 * back to an empty object when no polyfill exists.
 */
const defaultEnv = typeof process !== 'undefined' && process?.env ? process.env : {};
/**
 * Application environment values.
 *
 * Values here are browser-safe defaults and override any polyfilled
 * `process.env` values if present.
 */
const env = Object.assign(defaultEnv, {
    DISABLE_PRETTY_LOG: 'true',
});

const parseBoolean = (value) => {
    switch (value) {
        case 'true':
            return true;
        case 'false':
            return false;
        case undefined:
            return undefined;
        default:
            throw new Error('Invalid boolean value: ${value}');
    }
};
/**
 * Disabled when environment variable DISABLE_PRETTY_LOG is set to true.
 */
function isPrettyPrintDisabled() {
    return parseBoolean(env.DISABLE_PRETTY_LOG) ?? false;
}
function isJestRunning() {
    return env.JEST_WORKER_ID !== undefined;
}
/**
 * This whole monstrosity exists only because pino in browser environment will not print a log message
 * when invoking `logger.info(undefined, 'msg') instead you need to call `logger.info(msg)`.
 */
function wrappedMethodCall(wrappedPinoMethod) {
    return (msg, metadata) => {
        if (metadata !== undefined) {
            wrappedPinoMethod(metadata, msg);
        }
        else {
            wrappedPinoMethod(msg);
        }
    };
}
class Logger {
    static NAME_LENGTH = 25;
    static rootLogger;
    static getRootLogger() {
        Logger.rootLogger ??= pino({
            name: 'rootLogger',
            enabled: !env.NOLOG,
            level: env.LOG_LEVEL ?? 'info',
            formatters: {
                level: (label) => {
                    return { level: label }; // log level as string instead of number
                },
            },
            transport: isPrettyPrintDisabled()
                ? undefined
                : {
                    target: 'pino-pretty',
                    options: {
                        colorize: parseBoolean(env.LOG_COLORS) ?? true,
                        singleLine: true,
                        translateTime: 'yyyy-mm-dd"T"HH:MM:ss.l',
                        ignore: 'pid,hostname',
                        levelFirst: true,
                        sync: isJestRunning(),
                    },
                },
            browser: {
                asObject: true,
            },
        });
        return Logger.rootLogger;
    }
    logger;
    fatal;
    error;
    warn;
    info;
    debug;
    trace;
    constructor(loggerModule, contextBindings, defaultLogLevel = 'info', parentLogger = Logger.getRootLogger()) {
        this.logger = parentLogger.child({
            name: Logger.createName(loggerModule),
            ...contextBindings
        }, {
            level: env.LOG_LEVEL ?? defaultLogLevel
        });
        this.fatal = wrappedMethodCall(this.logger.fatal.bind(this.logger));
        this.error = wrappedMethodCall(this.logger.error.bind(this.logger));
        this.warn = wrappedMethodCall(this.logger.warn.bind(this.logger));
        this.info = wrappedMethodCall(this.logger.info.bind(this.logger));
        this.debug = wrappedMethodCall(this.logger.debug.bind(this.logger));
        this.trace = wrappedMethodCall(this.logger.trace.bind(this.logger));
    }
    static createName(loggerModule) {
        const loggerModuleId = typeof loggerModule === 'string' ? loggerModule : String(loggerModule.id);
        const parsedPath = path.parse(loggerModuleId);
        let fileId = parsedPath.name;
        if (fileId === 'index') {
            // file with name "foobar/index.ts" -> "foobar"
            const parts = parsedPath.dir.split(path.sep);
            fileId = parts[parts.length - 1];
        }
        const longName = without([env.STREAMR_APPLICATION_ID, fileId], undefined).join(':');
        return isPrettyPrintDisabled() ?
            longName : padEnd(longName.substring(0, this.NAME_LENGTH), this.NAME_LENGTH, ' ');
    }
}

/*
 * @param {number} interval - execute task when UTC timestamp is divisible by the given interval
 *                            e.g. scheduleAtFixedRate(() => {},  24 * 60 * 60 * 1000) triggers the
 *                            task once a day at 00:00 UTC
 *                            (but no tasks will be executed concurrently: if a previous task is
 *                            still ongoing when the next task should, the new task is silently skipped)
 */
const scheduleAtFixedRate = (task, interval, abortSignal) => {
    const initTime = Date.now();
    let invocationTime = initTime - (initTime % interval);
    repeatScheduleTask((doneCb) => {
        const now = Date.now();
        invocationTime += interval;
        if (now < invocationTime) {
            setAbortableTimeout(async () => {
                await task(invocationTime);
                doneCb();
            }, (invocationTime - now), abortSignal);
        }
        else {
            doneCb();
        }
    }, abortSignal);
};
/** @internal */
const repeatScheduleTask = (scheduleNextTask, abortSignal) => {
    const scheduleNext = () => {
        if (!abortSignal?.aborted) {
            scheduleNextTask(scheduleNext);
        }
    };
    scheduleNext();
};

class Sampler {
    metric;
    listener;
    constructor(metric) {
        this.metric = metric;
        this.listener = (value) => this.onRecord(value);
    }
    start(_now) {
        this.metric.on('record', this.listener);
    }
    stop(_now) {
        this.metric.off('record', this.listener);
    }
}
class Metric {
    latestValue;
    eventEmitter = new EventEmitter();
    samplerFactory;
    /** @internal */
    constructor(samplerFactory, initialValue) {
        this.samplerFactory = samplerFactory;
        this.latestValue = initialValue;
    }
    record(value) {
        this.latestValue = value;
        this.eventEmitter.emit('record', value);
    }
    getLatestValue() {
        return this.latestValue;
    }
    /** @internal */
    on(eventName, listener) {
        this.eventEmitter.on(eventName, listener);
    }
    /** @internal */
    off(eventName, listener) {
        this.eventEmitter.off(eventName, listener);
    }
    /** @internal */
    createSampler() {
        return this.samplerFactory(this);
    }
}
/*
 * Sum of all records within a sampling period.
 *
 * E.g. count of failed connections
 */
class CountSampler extends Sampler {
    sum = 0;
    onRecord(value) {
        this.sum += value;
    }
    getAggregatedValue() {
        return this.sum;
    }
}
class CountMetric extends Metric {
    constructor() {
        super((m) => new CountSampler(m));
    }
}
/*
 * Average of all records within a sampling period. If the sampling data is
 * continuous, you may want to use LevelSampler instead.
 *
 * E.g. average latency
 */
class AverageSampler extends Sampler {
    sum = 0;
    count = 0;
    onRecord(value) {
        this.sum += value;
        this.count++;
    }
    getAggregatedValue() {
        if (this.count > 0) {
            return this.sum / this.count;
        }
        else {
            return undefined;
        }
    }
}
/*
 * Average level of the records during a sampling period. Takes the average of the
 * recorded values, but also includes the current level as a first sample when
 * the sampling starts.
 *
 * E.g. average count of currently active connections
 */
class LevelSampler extends AverageSampler {
    start(now) {
        super.start(now);
        const latest = this.metric.getLatestValue();
        if (latest !== undefined) {
            this.onRecord(latest);
        }
    }
}
class LevelMetric extends Metric {
    constructor(initialValue) {
        super((m) => new LevelSampler(m), initialValue);
    }
}
/*
 * Sum of records divided by seconds.
 *
 * E.g. download speed (bytes per second)
 */
class RateSampler extends Sampler {
    sum = 0;
    startTimestamp = undefined;
    stopTimestamp = undefined;
    start(now) {
        super.start(now);
        this.startTimestamp = now;
    }
    stop(now) {
        super.stop(now);
        this.stopTimestamp = now;
    }
    onRecord(value) {
        this.sum += value;
    }
    getAggregatedValue() {
        if ((this.startTimestamp !== undefined) && (this.stopTimestamp !== undefined) && (this.startTimestamp !== this.stopTimestamp)) {
            const elapsedSeconds = (this.stopTimestamp - this.startTimestamp) / 1000;
            return this.sum / elapsedSeconds;
        }
        else {
            return undefined;
        }
    }
}
class RateMetric extends Metric {
    constructor() {
        super((m) => new RateSampler(m));
    }
}
class MetricsContext {
    metrics = new Map();
    addMetrics(namespace, definitions) {
        Object.keys(definitions).forEach((key) => {
            const id = `${namespace}.${key}`;
            if (this.metrics.has(id)) {
                throw new Error(`Metrics "${id}" already created`);
            }
            this.metrics.set(id, definitions[key]);
        });
    }
    createReportProducer(onReport, interval, abortSignal, formatNumber) {
        const ongoingSamples = new Map();
        scheduleAtFixedRate(async (now) => {
            if (ongoingSamples.size > 0) {
                const report = {
                    period: {
                        start: now - interval,
                        end: now
                    }
                };
                ongoingSamples.forEach((sample, metricId) => {
                    sample.stop(now);
                    const value = sample.getAggregatedValue();
                    if (value !== undefined) {
                        set(report, metricId, (formatNumber !== undefined) ? formatNumber(value) : value);
                    }
                });
                onReport(report);
                ongoingSamples.clear();
            }
            this.metrics.forEach((metric, id) => {
                const sample = metric.createSampler();
                sample.start(now);
                ongoingSamples.set(id, sample);
            });
        }, interval, abortSignal);
    }
    getMetric(id) {
        return this.metrics.get(id);
    }
}

/**
 * Generic multimap: a key which maps to multiple valuess.
 * The values is an array
 * -> when we query the data, we get it back in the same order
 * -> an array may contain duplicates, if same value is added multiple times
 *    (we could implement a Multiset class if we need a different kind of duplication handling)
 **/
class Multimap {
    delegatee = new Map();
    get(key) {
        return this.delegatee.get(key) ?? [];
    }
    has(key, value) {
        const items = this.delegatee.get(key);
        if (items !== undefined) {
            return items.includes(value);
        }
        else {
            return false;
        }
    }
    add(key, value) {
        this.delegatee.set(key, this.get(key).concat(value));
    }
    addAll(key, values) {
        this.delegatee.set(key, this.get(key).concat(values));
    }
    remove(key, value) {
        const items = this.delegatee.get(key);
        if (items !== undefined) {
            const newItems = items.filter((i) => i !== value);
            if (newItems.length > 0) {
                this.delegatee.set(key, newItems);
            }
            else {
                this.delegatee.delete(key);
            }
        }
    }
    removeAll(key, values) {
        values.forEach((value) => this.remove(key, value));
    }
    *keys() {
        yield* this.delegatee.keys();
    }
    *values() {
        for (const k of this.keys()) {
            yield* this.get(k);
        }
    }
    getKeyCount() {
        return this.delegatee.size;
    }
    isEmpty() {
        return this.getKeyCount() === 0;
    }
}

const DEFAULT_CHARSET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
// From: https://stackoverflow.com/questions/10726909/random-alpha-numeric-string-in-javascript
function randomString(length, charset = DEFAULT_CHARSET) {
    let result = '';
    for (let i = 0; i < length; ++i) {
        result += charset[Math.floor(Math.random() * charset.length)];
    }
    return result;
}

/*
 * @param {number} interval - number of milliseconds to wait after a task is completed
 */
const scheduleAtInterval = async (task, interval, executeAtStart, abortSignal) => {
    if (abortSignal.aborted) {
        return;
    }
    if (executeAtStart) {
        await task();
    }
    repeatScheduleTask((doneCb) => {
        setAbortableTimeout(async () => {
            await task();
            doneCb();
        }, interval, abortSignal);
    }, abortSignal);
};

/**
 * Wait for a specific time
 * @param ms time to wait for in milliseconds
 * @param abortSignal to control abortion of any wait
 * @returns {Promise<void>} resolves when time has passed
 */
function wait(ms, abortSignal) {
    let timeoutRef;
    return asAbortable(new Promise((resolve) => {
        timeoutRef = setTimeout(resolve, ms);
    }), abortSignal).finally(() => {
        clearTimeout(timeoutRef);
    });
}

/**
 * @param {number} approximateIntervalInMs - approximate time (in milliseconds) to wait after a task is completed
 * @param {number} driftMultiplier how much the wait time can vary: e.g. if the interval is 60 minutes and the drift is 0.1,
 * the delay between invocations will range from 54 to 66 minutes
 */
const scheduleAtApproximateInterval = async (task, approximateIntervalInMs, driftMultiplier, executeAtStart, abortSignal) => {
    if (abortSignal.aborted) {
        return;
    }
    if (executeAtStart) {
        await task();
    }
    return scheduleAtInterval(async () => {
        try {
            await wait(Math.round(Math.random() * approximateIntervalInMs * 2 * driftMultiplier), abortSignal);
        }
        catch {
            // the abort signal timeouted, ignore
        }
        if (!abortSignal.aborted) {
            await task();
        }
    }, approximateIntervalInMs * (1 - driftMultiplier), false, abortSignal);
};

function toEthereumAddressOrENSName(str) {
    return isENSNameFormatIgnoreCase(str) ? toENSName(str) : toEthereumAddress(str);
}

class TimeoutError extends Error {
    code = 'TimeoutError';
    constructor(timeoutInMs, customErrorContext) {
        super(customErrorContext === undefined
            ? `timed out after ${timeoutInMs} ms`
            : `${customErrorContext} (timed out after ${timeoutInMs} ms)`);
    }
}
const withTimeout = (task, timeoutInMs, customErrorContext, abortSignal) => {
    let timeoutRef;
    return asAbortable(Promise.race([
        task,
        new Promise((_resolve, reject) => {
            timeoutRef = setTimeout(() => {
                reject(new TimeoutError(timeoutInMs, customErrorContext));
            }, timeoutInMs);
        })
    ]), abortSignal, customErrorContext).finally(() => {
        clearTimeout(timeoutRef);
    });
};

/**
 * Wait for an event to be emitted on emitter within timeout.
 *
 * @param emitter emitter of event
 * @param eventName event to wait for
 * @param timeout amount of time in milliseconds to wait for
 * @param predicate function that gets passed the event arguments, should return true if event accepted
 * @param abortSignal
 * @returns {Promise<Parameters<TEvents[TEventName]>>} resolves with event arguments if event occurred within timeout else rejects
 */
async function waitForEvent(emitter, eventName, timeout = 5000, predicate = () => true, abortSignal) {
    let listener;
    const task = new Promise((resolve) => {
        listener = ((...eventArgs) => {
            if (predicate(...eventArgs)) {
                resolve(eventArgs);
            }
        });
        emitter.on(eventName, listener);
    });
    return withTimeout(task, timeout, 'waitForEvent', abortSignal).finally(() => {
        emitter.off(eventName, listener);
    });
}

async function raceForEvent(emitter, eventNames, timeout = 5000) {
    const raceAbortController = new AbortController();
    const promises = eventNames.map(async (eventName) => {
        const eventArgs = await waitForEvent(emitter, eventName, timeout, () => true, raceAbortController.signal);
        return {
            winnerName: eventName,
            winnerArgs: eventArgs
        };
    });
    let result;
    try {
        result = await Promise.race(promises);
    }
    finally {
        // Call raceAbortController.abort() to remove the event listeners. Note that this not strictly needed when Promise.race(promises) rejects.
        // The race can reject only if withTimeout() timeouts, and as all timeouts happen at the same time, the event listeners are cleaned
        // up immediately. In that sense this could be moved out from the finally block. But it makes sense to keep it here so that the function
        // can be seen as an atomic operation: all cleanup happens _before_ the function returns, not immediately _after_ it returns.
        // The Promise.allSettled() call implements that atomicity by waiting the cleanups initiated by raceAbortController.abort() to complete.
        raceAbortController.abort();
        await Promise.allSettled(promises);
    }
    return result;
}

/**
 * Compose a single AbortSignal from multiple AbortSignals with "OR" logic.
 *
 * WARNING: be aware of a potential memory leak that can occur if the composed
 * AbortSignal is never destroyed. This can happen if an instance of AbortSignal
 * is composed over and over with this utility but the composed AbortSignal (or
 * the other passed AbortSignal(s)) never abort. In this situation the
 * aforementioned instance of AbortSignal will have more and more listeners added
 * but never cleaned.
 */
function composeAbortSignals(...signals) {
    const abortController = new AbortController();
    for (const signal of signals) {
        if (signal?.aborted) {
            abortController.abort();
            return Object.assign(abortController.signal, { destroy: () => { } });
        }
    }
    function destroy() {
        for (const signal of signals) {
            signal?.removeEventListener('abort', onAbort);
        }
    }
    let aborted = false;
    function onAbort() {
        if (aborted) {
            return;
        }
        aborted = true;
        abortController.abort();
        destroy();
    }
    for (const signal of signals) {
        signal?.addEventListener('abort', onAbort, { once: true });
    }
    return Object.assign(abortController.signal, { destroy });
}

function throwError(userAborted, conditionFn, onTimeoutContext) {
    const action = userAborted ? 'aborted' : 'timed out';
    let msg = `until: ${action} before "${conditionFn.toString()}" became true`;
    if (onTimeoutContext) {
        msg += `\n${onTimeoutContext()}`;
    }
    throw new Error(msg);
}
/**
 * Wait for a condition to become true by re-evaluating `conditionFn` every `retryInterval` milliseconds.
 *
 * @param conditionFn condition to be evaluated; should return boolean or Promise<boolean> and have
 * no side effects.
 * @param timeout amount of time in milliseconds to wait for
 * @param retryInterval how often, in milliseconds, to re-evaluate condition
 * @param abortSignal pass an abort signal to cancel prematurely
 * @param onTimeoutContext evaluated only on timeout. Used to associate human-friendly textual context to error.
 * @returns {Promise<void>} resolves immediately if
 * conditionFn evaluates to true on a retry attempt within timeout. If timeout
 * is reached with conditionFn never evaluating to true, rejects.
 */
const until = async (conditionFn, timeout = 5000, retryInterval = 100, abortSignal, onTimeoutContext) => {
    let userAborted = abortSignal?.aborted ?? false;
    if (userAborted) {
        throwError(userAborted, conditionFn, onTimeoutContext);
    }
    abortSignal?.addEventListener('abort', () => { userAborted = true; }, { once: true });
    const timeoutAbortSignal = AbortSignal.timeout(timeout);
    const composedSignal = composeAbortSignals(timeoutAbortSignal, abortSignal);
    try {
        while (true) {
            const result = await asAbortable(Promise.resolve(conditionFn()), composedSignal);
            if (result) {
                return;
            }
            await wait(retryInterval, composedSignal);
        }
    }
    catch (e) {
        if (e instanceof AbortError) {
            throwError(userAborted, conditionFn, onTimeoutContext);
        }
        throw e;
    }
    finally {
        composedSignal.destroy();
    }
};

/**
 * Wraps a rate limiter around a function that ensures the function is invoked max once per `intervalInMs`.
 */
function withRateLimit(fn, intervalInMs) {
    let lastInvocationTimestamp = 0;
    return async () => {
        const now = Date.now();
        if (now - lastInvocationTimestamp >= intervalInMs) {
            lastInvocationTimestamp = now;
            await fn();
        }
    };
}

/*
 * Emits an addEventListener/removeEventListener event to a separate EventEmitter
 * whenever a listener is added or removed
 */
class ObservableEventEmitter {
    delegate = new EventEmitter();
    observer = new EventEmitter();
    on(eventName, listener) {
        this.delegate.on(eventName, listener);
        this.observer.emit('addEventListener', eventName);
    }
    once(eventName, listener) {
        const wrappedFn = (payload) => {
            listener(payload);
            this.observer.emit('removeEventListener', eventName);
        };
        this.delegate.once(eventName, wrappedFn);
        this.observer.emit('addEventListener', eventName);
    }
    off(eventName, listener) {
        this.delegate.off(eventName, listener);
        this.observer.emit('removeEventListener', eventName);
    }
    removeAllListeners() {
        const eventNames = this.delegate.eventNames();
        this.delegate.removeAllListeners();
        for (const eventName of eventNames) {
            this.observer.emit('removeEventListener', eventName);
        }
    }
    emit(eventName, payload) {
        this.delegate.emit(eventName, payload);
    }
    getListenerCount(eventName) {
        return this.delegate.listenerCount(eventName);
    }
    getObserver() {
        return this.observer;
    }
}

/*
 * Initializes a gateway which can produce events to the given emitter. The gateway is running
 * when there are any listeners for the given eventName: the start() callback is called
 * when a first event listener for the event name is added, and the stop() callback is called
 * when the last event listener is removed.
 */
const initEventGateway = (eventName, start, stop, emitter) => {
    const observer = emitter.getObserver();
    const emit = (payload) => emitter.emit(eventName, payload);
    let producer;
    observer.on('addEventListener', (sourceEvent) => {
        if ((sourceEvent === eventName) && (producer === undefined)) {
            producer = start(emit);
        }
    });
    observer.on('removeEventListener', (sourceEvent) => {
        if ((sourceEvent === eventName) && (producer !== undefined) && (emitter.getListenerCount(eventName) === 0)) {
            stop(producer);
            producer = undefined;
        }
    });
    if (emitter.getListenerCount(eventName) > 0) {
        producer = start(emit);
    }
};

const addManagedEventListener = (emitter, eventName, listener, abortSignal) => {
    if (!abortSignal.aborted) {
        emitter.on(eventName, listener);
        abortSignal.addEventListener('abort', () => {
            emitter.off(eventName, listener);
        }, {
            once: true
        });
    }
};

/*
 * Does deep merge. This is similar to `lodash` merge, but handles arrays differently:
 * `lodash` merges elements of arrays by their indices, this overwrites the existing
 * value with the array
 */
const merge = (...sources) => {
    const result = {};
    mergeWith(result, ...sources, (_, srcValue) => {
        if (isArray(srcValue)) {
            return [...srcValue];
        }
        else {
            return undefined; // no customization: does the default merging for this field
        }
    });
    return result;
};

const collect = async (source, maxCount) => {
    if ((maxCount !== undefined) && (maxCount <= 0)) {
        return [];
    }
    const items = [];
    for await (const item of source) {
        items.push(item);
        if ((maxCount !== undefined) && (items.length >= maxCount)) {
            break;
        }
    }
    return items;
};

/*
 * Gate to lock access to some resource.
 */
class Gate {
    pending;
    constructor(isOpen) {
        if (!isOpen) {
            this.close();
        }
    }
    open() {
        this.clearPending();
    }
    close() {
        this.pending ??= new Defer();
    }
    isOpen() {
        return !this.pending;
    }
    clearPending() {
        const { pending } = this;
        if (pending === undefined) {
            return;
        }
        this.pending = undefined;
        pending.resolve(undefined);
    }
    async waitUntilOpen() {
        if (this.pending) {
            await this.pending;
        }
    }
}

/**
 * Query one entity with queryEntity method, and a list of entitities with queryEntities method.
 * The queryEntities method does pagination automatically.
 *
 * If we want to ensure that The Graph index is up-to-date, we can call the updateRequiredBlockNumber()
 * method. In that case a queryEntity()/queryEntities() waits until The Graph has been indexed at least
 * to that block number. If the indexing takes longer than opts.indexTimeout, the query call rejects
 * with a TimeoutError.
 */
class TheGraphClient {
    requiredBlockNumber = 0;
    indexingState;
    serverUrl;
    fetch;
    logger;
    constructor(opts) {
        this.serverUrl = opts.serverUrl;
        this.fetch = opts.fetch;
        this.logger = opts.logger ?? new Logger('TheGraphClient');
        this.indexingState = new IndexingState(() => this.getIndexBlockNumber(), opts.indexTimeout ?? 60000, opts.indexPollInterval ?? 1000, this.logger);
    }
    async queryEntity(query) {
        await this.indexingState.waitUntilIndexed(this.requiredBlockNumber);
        return this.sendQuery(query);
    }
    async *queryEntities(createQuery, 
    /*
     * For simple queries there is one root level property, e.g. "streams" or "permissions"
     * which contain array of items. If the query contains more than one root level property
     * or we want to return non-root elements as items, the caller must pass a custom
     * function to parse the items.
     */
    parseItems = (response) => {
        const rootKey = Object.keys(response)[0];
        return response[rootKey];
    }, pageSize = 1000) {
        await this.indexingState.waitUntilIndexed(this.requiredBlockNumber);
        let lastResultSet;
        do {
            const lastId = (lastResultSet !== undefined) ? lastResultSet[lastResultSet.length - 1].id : '';
            const query = createQuery(lastId, pageSize, this.requiredBlockNumber);
            const response = await this.sendQuery(query);
            const items = parseItems(response);
            yield* items;
            lastResultSet = items;
        } while (lastResultSet.length === pageSize);
    }
    updateRequiredBlockNumber(blockNumber) {
        this.requiredBlockNumber = Math.max(blockNumber, this.requiredBlockNumber);
    }
    async sendQuery(query) {
        this.logger.trace('Send GraphQL query', { query });
        const res = await this.fetch(this.serverUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                accept: '*/*',
            },
            body: JSON.stringify(query)
        });
        const resText = await res.text();
        let resJson;
        try {
            resJson = JSON.parse(resText);
        }
        catch {
            throw new Error(`GraphQL query failed with "${resText}"`);
        }
        this.logger.trace('Received GraphQL response', { resJson });
        if (!resJson.data) {
            if (resJson.errors && resJson.errors.length > 0) {
                throw new Error('GraphQL query failed: ' + JSON.stringify(resJson.errors.map((e) => e.message)));
            }
            else {
                throw new Error('GraphQL query failed');
            }
        }
        return resJson.data;
    }
    async getIndexBlockNumber() {
        const response = await this.sendQuery({ query: '{ _meta { block { number } } }' });
        // eslint-disable-next-line no-underscore-dangle
        return response._meta.block.number;
    }
}
class BlockNumberGate extends Gate {
    blockNumber;
    constructor(blockNumber) {
        super(true);
        this.blockNumber = blockNumber;
    }
}
class IndexingState {
    blockNumber = 0;
    gates = new Set();
    getCurrentBlockNumber;
    timeout;
    pollInterval;
    logger;
    constructor(getCurrentBlockNumber, timeout, pollInterval, logger) {
        this.getCurrentBlockNumber = async () => {
            try {
                return await getCurrentBlockNumber();
            }
            catch (err) {
                logger.warn('Failed to get current block number', { reason: err instanceof Error ? err.message : String(err) });
                return undefined;
            }
        };
        this.timeout = timeout;
        this.pollInterval = pollInterval;
        this.logger = logger;
    }
    async waitUntilIndexed(blockNumber) {
        if (blockNumber <= this.blockNumber) {
            return;
        }
        this.logger.debug('Wait until The Graph is synchronized', { blockNumber: this.blockNumber, blockTarget: blockNumber });
        const gate = this.getOrCreateGate(blockNumber);
        try {
            await withTimeout(gate.waitUntilOpen(), this.timeout, `The Graph did not synchronize to block ${blockNumber}`);
        }
        catch (e) {
            if (e instanceof TimeoutError) {
                this.gates.delete(gate);
            }
            throw e;
        }
    }
    getOrCreateGate(blockNumber) {
        const gate = new BlockNumberGate(blockNumber);
        if (blockNumber > this.blockNumber) {
            const isPolling = this.gates.size > 0;
            gate.close();
            this.gates.add(gate);
            if (!isPolling) {
                this.startPolling();
            }
        }
        return gate;
    }
    async startPolling() {
        this.logger.trace('Start polling');
        while (this.gates.size > 0) {
            const newBlockNumber = await this.getCurrentBlockNumber();
            if (newBlockNumber !== undefined && newBlockNumber !== this.blockNumber) {
                this.blockNumber = newBlockNumber;
                this.logger.trace('Polled', { blockNumber: this.blockNumber });
                this.gates.forEach((gate) => {
                    if (gate.blockNumber <= this.blockNumber) {
                        gate.open();
                        this.gates.delete(gate);
                    }
                });
            }
            if (this.gates.size > 0) {
                await wait(this.pollInterval);
            }
        }
        this.logger.trace('Stop polling');
    }
}

class Heap {
    items;
    compare;
    constructor(compare) {
        this.items = [];
        this.compare = compare;
    }
    push(item) {
        const index = this.getItemIndex(item);
        this.items.splice(index, 0, item);
    }
    pop() {
        return this.items.shift();
    }
    peek() {
        if (!this.isEmpty()) {
            return this.items[0];
        }
        else {
            return undefined;
        }
    }
    contains(item) {
        if (!this.isEmpty()) {
            const index = this.getItemIndex(item);
            if (index !== this.items.length) {
                return this.compare(this.items[index], item) === 0;
            }
        }
        return false;
    }
    isEmpty() {
        return this.items.length === 0;
    }
    values() {
        return this.items;
    }
    /*
     * Return the index at which the item can be inserted in order to maintain
     * the sort order of the array. If the item is in the array, it can be found
     * at the returned index.
     */
    getItemIndex(item) {
        if (!this.isEmpty()) {
            let first = 0;
            let last = this.items.length;
            while (first < last) {
                const middle = Math.floor((first + last) / 2);
                const comparison = this.compare(this.items[middle], item);
                if (comparison < 0) {
                    first = middle + 1;
                }
                else {
                    last = middle;
                }
            }
            return last;
        }
        else {
            return 0;
        }
    }
}

/**
 * Execute a promise that should never reject. If it does, log the error and exit the process
 * (in Node/Electron) or throw an unhandled error (in browsers).
 * To be used in places where we want to "annotate" that the intention of a promise is never
 * to reject (unless something is really wrong).
 */
const executeSafePromise = async (createPromise) => {
    try {
        return await createPromise();
    }
    catch (err) {
        const logger = new Logger('executeSafePromise');
        logger.fatal('Assertion failure!', { message: err?.message, err });
        // Check if we're in a Node/Electron environment
        if (typeof process !== 'undefined' && process.exit !== undefined) {
            process.exit(1);
        }
        else {
            // Browser environment - throw with proper error chaining
            throw new Error('executeSafePromise: Assertion failure!', { cause: err });
        }
    }
};

const os = {
    homedir: () => '/'
};

// TODO: use untildify instead?
function filePathToNodeFormat(filePath) {
    if (filePath.startsWith('~/')) {
        return filePath.replace('~', os.homedir());
    }
    else {
        return filePath;
    }
}

const retry = async (task, onRetryableFailure, description, maxCount, delay) => {
    for (let i = 0; i < maxCount; i++) {
        try {
            const result = await task();
            return result;
        }
        catch (err) {
            if (i < (maxCount - 1)) {
                const message = `${description} failed, retrying in ${delay} ms`;
                onRetryableFailure(message, err);
                await wait(delay);
            }
        }
    }
    throw new Error(`${description} failed after ${maxCount} attempts`);
};

// If you change this you also need to change `writeUint32BE` and `readUInt32BE` in the code below
const HEADER_LENGTH = 4;
/**
 * Utilities to deal with length-prefixed frames, i.e. | length [4 bytes] | payload [variable bytes] |
 */
const toLengthPrefixedFrame = (payload) => {
    const length = Buffer.alloc(HEADER_LENGTH);
    length.writeUint32BE(payload.length);
    return Buffer.concat([length, payload]);
};
class LengthPrefixedFrameDecoder extends Transform {
    buffer = Buffer.alloc(0);
    constructor() {
        super({ objectMode: true });
    }
    _transform(chunk, _encoding, callback) {
        this.buffer = Buffer.concat([this.buffer, chunk]);
        this.processStreamData(callback);
    }
    _flush(callback) {
        // Handle any remaining data when the stream ends
        this.processStreamData(callback);
    }
    processStreamData(callback) {
        while (this.buffer.length >= HEADER_LENGTH) {
            const payloadSize = this.buffer.readUInt32BE(0);
            if (this.buffer.length >= payloadSize + HEADER_LENGTH) {
                const payload = this.buffer.subarray(HEADER_LENGTH, payloadSize + HEADER_LENGTH);
                this.push(payload);
                this.buffer = this.buffer.subarray(payloadSize + HEADER_LENGTH);
            }
            else {
                break;
            }
        }
        callback();
    }
}

const ipv4ToNumber = (ip) => {
    const octets = ip.split('.').map(Number);
    return ((octets[0] << 24) | (octets[1] << 16) | (octets[2] << 8) | octets[3]) >>> 0;
};
const numberToIpv4 = (value) => {
    const octets = [24, 16, 8, 0].map((shift) => (value >> shift) & 255);
    return octets.join('.');
};

class MapWithTtl {
    delegate = new Map();
    getTtl;
    constructor(getTtl) {
        this.getTtl = getTtl;
    }
    set(key, value) {
        const existing = this.delegate.get(key);
        if (existing !== undefined) {
            clearTimeout(existing.timeout);
        }
        this.delegate.set(key, {
            value,
            timeout: this.createTimeout(key, value)
        });
    }
    get(key) {
        const wrapper = this.delegate.get(key);
        return wrapper?.value;
    }
    has(key) {
        return this.delegate.has(key);
    }
    delete(key) {
        const existing = this.delegate.get(key);
        if (existing !== undefined) {
            clearTimeout(existing.timeout);
            this.delegate.delete(key);
        }
    }
    clear() {
        this.delegate.forEach((value) => {
            clearTimeout(value.timeout);
        });
        this.delegate.clear();
    }
    size() {
        return this.delegate.size;
    }
    *values() {
        for (const v of this.delegate.values()) {
            yield v.value;
        }
    }
    forEach(cb) {
        this.delegate.forEach((valueWrapper, key) => {
            cb(valueWrapper.value, key);
        });
    }
    createTimeout(key, value) {
        return setTimeout(() => {
            this.delete(key);
        }, this.getTtl(value));
    }
}

/**
 * Create an instance of `StreamID` from a given string stream id or path.
 *
 * Supported formats:
 *  - full stream id format, e.g., '0x0000000000000000000000000000000000000000/foo/bar' or 'name.eth/foo/bar'
 *  - path-only format, e.g. , '/foo/bar'
 *  - legacy format, e.g., '7wa7APtlTq6EC5iTCBy6dw'
 *
 *  If `streamIdOrPath` is not in path-only format, `domain` can be left undefined.
 */
function toStreamID(streamIdOrPath, domain) {
    if (streamIdOrPath.length === 0) {
        throw new Error('stream id may not be empty');
    }
    const firstSlashIdx = streamIdOrPath.indexOf('/');
    if (firstSlashIdx === -1) { // legacy format
        return streamIdOrPath;
    }
    else if (firstSlashIdx === 0) { // path-only format
        if (domain === undefined) {
            throw new Error(`path-only format "${streamIdOrPath}" provided without domain`);
        }
        return (domain + streamIdOrPath);
    }
    else {
        const domain = toEthereumAddressOrENSName(streamIdOrPath.substring(0, firstSlashIdx));
        const path = streamIdOrPath.substring(firstSlashIdx);
        return (domain + path);
    }
}
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class StreamIDUtils {
    static isPathOnlyFormat(streamIdOrPath) {
        return streamIdOrPath.startsWith('/');
    }
    static getDomain(streamId) {
        const domainAndPath = StreamIDUtils.getDomainAndPath(streamId);
        return domainAndPath?.[0];
    }
    static getPath(streamId) {
        const domainAndPath = StreamIDUtils.getDomainAndPath(streamId);
        return domainAndPath?.[1];
    }
    static getDomainAndPath(streamId) {
        const firstSlashIdx = streamId.indexOf('/');
        if (firstSlashIdx !== -1) {
            const domain = streamId.substring(0, firstSlashIdx);
            return [domain, streamId.substring(firstSlashIdx)];
        }
        else {
            return undefined;
        }
    }
}

const DEFAULT_PARTITION_COUNT = 1;
const MAX_PARTITION_COUNT = 100;
function ensureValidStreamPartitionIndex(streamPartition) {
    if (!Number.isSafeInteger(streamPartition) || streamPartition < 0 || streamPartition >= MAX_PARTITION_COUNT) {
        throw new Error(`invalid streamPartition value: ${streamPartition}`);
    }
}
function ensureValidStreamPartitionCount(streamPartition) {
    if (!Number.isSafeInteger(streamPartition) || streamPartition < 0 || streamPartition > MAX_PARTITION_COUNT) {
        throw new Error(`invalid streamPartition value: ${streamPartition}`);
    }
}

const DELIMITER = '#';
function toStreamPartID(streamId, streamPartition) {
    ensureValidStreamPartitionIndex(streamPartition);
    return `${streamId}${DELIMITER}${streamPartition}`;
}
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
class StreamPartIDUtils {
    static parse(streamPartIdAsStr) {
        const [streamId, streamPartition] = StreamPartIDUtils.parseRawElements(streamPartIdAsStr);
        if (streamPartition === undefined) {
            throw new Error(`invalid streamPartId string: ${streamPartIdAsStr}`);
        }
        toStreamID(streamId); // throws if not valid
        ensureValidStreamPartitionIndex(streamPartition);
        return streamPartIdAsStr;
    }
    static getStreamID(streamPartId) {
        return this.getStreamIDAndPartition(streamPartId)[0];
    }
    static getStreamPartition(streamPartId) {
        return this.getStreamIDAndPartition(streamPartId)[1];
    }
    static getStreamIDAndPartition(streamPartId) {
        return StreamPartIDUtils.parseRawElements(streamPartId);
    }
    static parseRawElements(str) {
        const lastIdx = str.lastIndexOf(DELIMITER);
        if (lastIdx === -1 || lastIdx === str.length - 1) {
            return [str, undefined];
        }
        return [str.substring(0, lastIdx), Number(str.substring(lastIdx + 1))];
    }
}

const REGEX = /^0x[a-fA-F0-9]+$/;
const toUserId = (input) => {
    if (input instanceof Uint8Array) {
        return binaryToHex(input, true);
    }
    else {
        if (isValidUserId(input)) {
            return input.toLowerCase();
        }
        throw new Error(`not a valid UserID: "${input}"`);
    }
};
const toUserIdRaw = (userId) => {
    return hexToBinary(userId);
};
const isValidUserId = (input) => {
    return REGEX.test(input);
};
const isEthereumAddressUserId = (userId) => {
    return userId.length === PREFIXED_STRING_LENGTH;
};

const PRECISION = 1e18;
const multiplyWeiAmount = (val1, val2) => {
    return val1 * BigInt(PRECISION * val2) / BigInt(PRECISION);
};

/* eslint-disable class-methods-use-this */
const KEY_TYPES = [
    'ECDSA_SECP256K1_EVM',
    'ECDSA_SECP256R1',
    'ML_DSA_87'
];
const ECDSA_SECP256K1_EVM_SIGN_MAGIC = '\u0019Ethereum Signed Message:\n';
class SigningUtil {
    static getInstance(type) {
        switch (type) {
            case 'ECDSA_SECP256K1_EVM':
                return new EcdsaSecp256k1Evm();
            case 'ECDSA_SECP256R1':
                return new EcdsaSecp256r1();
            case 'ML_DSA_87':
                return new MlDsa87();
            default:
                throw new Error(`Unknown key pair type: ${type}`);
        }
    }
}
/**
 * EVM compatible ECDSA signing scheme using keccak hash, magic bytes, and secp256k1 curve.
 */
class EcdsaSecp256k1Evm extends SigningUtil {
    generateKeyPair() {
        const privateKey = randomBytes(32);
        const publicKey = secp256k1.publicKeyCreate(privateKey, false);
        return {
            // Return address as 'publicKey'
            publicKey: this.publicKeyToAddress(publicKey),
            privateKey,
        };
    }
    keccakHash(message, useEthereumMagic = true) {
        const keccak = new Keccak(256);
        keccak.update(useEthereumMagic ? Buffer.concat([
            Buffer.from(ECDSA_SECP256K1_EVM_SIGN_MAGIC + message.length),
            message
        ]) : Buffer.from(message));
        return keccak.digest('binary');
    }
    recoverPublicKey(signature, payload) {
        const signatureBuffer = Buffer.from(signature);
        const recoveryId = signatureBuffer.readUInt8(signatureBuffer.length - 1) - 27;
        return secp256k1.ecdsaRecover(signatureBuffer.subarray(0, signatureBuffer.length - 1), recoveryId, this.keccakHash(payload), false, Buffer.alloc);
    }
    async createSignature(payload, privateKey) {
        const msgHash = this.keccakHash(payload);
        const sigObj = secp256k1.ecdsaSign(msgHash, privateKey);
        const result = Buffer.alloc(sigObj.signature.length + 1, Buffer.from(sigObj.signature));
        result.writeInt8(27 + sigObj.recid, result.length - 1);
        return result;
    }
    publicKeyToAddress(publicKey) {
        if (publicKey.length !== 65) {
            throw new Error(`Expected 65 bytes (an ECDSA uncompressed public key with header byte). Got length: ${publicKey.length}`);
        }
        const pubKeyWithoutFirstByte = publicKey.subarray(1, publicKey.length);
        const keccak = new Keccak(256);
        keccak.update(Buffer.from(pubKeyWithoutFirstByte));
        const hashOfPubKey = keccak.digest('binary');
        return hashOfPubKey.subarray(12, hashOfPubKey.length);
    }
    recoverSignerUserId(signature, payload) {
        const publicKey = this.recoverPublicKey(signature, payload);
        return this.publicKeyToAddress(publicKey);
    }
    async verifySignature(expectedUserId, payload, signature) {
        try {
            const recoveredAddress = this.recoverSignerUserId(signature, payload);
            return areEqualBinaries(recoveredAddress, expectedUserId);
        }
        catch {
            return false;
        }
    }
    assertValidKeyPair(address, privateKey) {
        const computedPublicKey = secp256k1.publicKeyCreate(privateKey, false);
        const computedAddress = this.publicKeyToAddress(computedPublicKey);
        if (!areEqualBinaries(address, computedAddress)) {
            throw new Error(`Given private key is for a different address! Given: ${binaryToHex(address)}, Computed: ${binaryToHex(computedAddress)}`);
        }
    }
}
/**
 * Signing scheme using ECDSA with secp256r1 curve and SHA-256, natively supported by browsers
 */
class EcdsaSecp256r1 extends SigningUtil {
    generateKeyPair(compressPublicKey = true) {
        const privateKey = randomBytes(32);
        const publicKey = this.getPublicKeyFromPrivateKey(privateKey, compressPublicKey);
        return {
            publicKey,
            privateKey,
        };
    }
    isCompressedPublicKey(publicKey) {
        return publicKey.length === 33;
    }
    isUncompressedPublicKey(publicKey) {
        return publicKey.length === 65;
    }
    toBase64Url(base64) {
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    getPublicKeyFromPrivateKey(privateKey, compressed = true) {
        return p256.getPublicKey(privateKey, compressed);
    }
    getUncompressedPublicKey(publicKey) {
        if (this.isCompressedPublicKey(publicKey)) {
            // Decode compressed public key to an elliptic curve point
            const point = p256.ProjectivePoint.fromHex(publicKey);
            // Convert the point to an uncompressed public key
            return point.toRawBytes(false);
        }
        // No-op if called with already uncompressed key
        if (this.isUncompressedPublicKey(publicKey)) {
            return publicKey;
        }
        throw new Error(`Unexpected public key length: ${publicKey.length}`);
    }
    privateKeyToJWK(privateKey) {
        const publicKey = this.getPublicKeyFromPrivateKey(privateKey, false);
        // uncompressed publicKey = [header (1 byte), x (32 bytes), y (32 bytes)
        const x = publicKey.subarray(1, 33);
        const y = publicKey.subarray(33);
        /**
         * Warning, there are some platform-specific differences. Logging observations here:
         * - buffer.toString('base64url') works on Mac but NOT in Linux/CI
         * - importKey accepts base64 encoded variables on Mac but NOT in Linux/CI
         * For this reason, they must be base64url encoded AND we need to use our own
         * toBase64Url converter.
         */
        const xBase64 = Buffer.from(x).toString('base64');
        const yBase64 = Buffer.from(y).toString('base64');
        const privateKeyBase64 = Buffer.from(privateKey).toString('base64');
        return {
            key_ops: ['sign'],
            ext: true,
            kty: 'EC',
            x: this.toBase64Url(xBase64),
            y: this.toBase64Url(yBase64),
            crv: 'P-256',
            d: this.toBase64Url(privateKeyBase64)
        };
    }
    /**
     * Pass the privateKey in JsonWebKey format for a slight performance gain.
     * You can convert raw keys to JWK using the privateKeyToJWK function.
     */
    async createSignature(payload, privateKey) {
        const subtleCrypto = getSubtle();
        const jwk = privateKey instanceof Uint8Array ? this.privateKeyToJWK(privateKey) : privateKey;
        /**
         * Stupidly, importKey does not support the 'raw' format. This means we need to
         * first compute the JWK from the raw key, and only then we can import and use it.
         */
        const key = await subtleCrypto.importKey('jwk', jwk, {
            name: 'ECDSA',
            namedCurve: 'P-256'
        }, false, ['sign']);
        const signature = await subtleCrypto.sign({
            name: 'ECDSA',
            hash: { name: 'SHA-256' }
        }, key, payload);
        return new Uint8Array(signature);
    }
    async publicKeyToCryptoKey(publicKey) {
        return getSubtle().importKey('raw', publicKey, {
            name: 'ECDSA',
            namedCurve: 'P-256'
        }, false, ['verify']);
    }
    async verifySignature(publicKey, payload, signature) {
        let key;
        try {
            key = await this.publicKeyToCryptoKey(publicKey);
        }
        catch (err) {
            // On some browsers (Safari), compressed keys are not supported for some reason!
            // If that might be the case, retry with an uncompressed key
            if (this.isCompressedPublicKey(publicKey)) {
                key = await this.publicKeyToCryptoKey(this.getUncompressedPublicKey(publicKey));
            }
            else {
                throw err;
            }
        }
        const isValid = await getSubtle().verify({
            name: 'ECDSA',
            hash: { name: 'SHA-256' }
        }, key, signature, payload);
        return isValid;
    }
    assertValidKeyPair(publicKey, privateKey) {
        if (privateKey.length !== 32) {
            throw new Error(`Expected a raw private key of 32 bytes. Maybe your key is in some encapsulating format?`);
        }
        if (!this.isCompressedPublicKey(publicKey) && !this.isUncompressedPublicKey(publicKey)) {
            throw new Error(`Expected a public key of either 33 bytes (compressed) or 65 bytes (uncompressed)!`);
        }
        const computedPublicKey = this.getPublicKeyFromPrivateKey(privateKey, publicKey.length === 33);
        if (!areEqualBinaries(computedPublicKey, publicKey)) {
            throw new Error(`Given private key is for a different public key! Given: ${binaryToHex(publicKey)}, Computed: ${binaryToHex(computedPublicKey)}`);
        }
    }
}
/**
 * Signing scheme using ML-DSA-87
 */
class MlDsa87 extends SigningUtil {
    generateKeyPair() {
        const seed = randomBytes(32);
        const keys = ml_dsa87.keygen(seed);
        return {
            privateKey: keys.secretKey,
            publicKey: keys.publicKey,
        };
    }
    async createSignature(payload, privateKey, seed) {
        return ml_dsa87.sign(privateKey, payload, seed);
    }
    async verifySignature(publicKey, payload, signature) {
        return ml_dsa87.verify(publicKey, payload, signature);
    }
    assertValidKeyPair(publicKey, privateKey) {
        // Validity of key pair is tested by signing and validating something
        const payload = Buffer.from('data-to-sign');
        const signature = ml_dsa87.sign(privateKey, payload);
        if (!ml_dsa87.verify(publicKey, payload, signature)) {
            throw new Error(`The given ML-DSA public key and private key don't match!`);
        }
    }
}

export { AbortError, Cache, CountMetric, DEFAULT_PARTITION_COUNT, Defer, EcdsaSecp256k1Evm, EcdsaSecp256r1, Gate, Heap, KEY_TYPES, LengthPrefixedFrameDecoder, LevelMetric, Logger, MAX_PARTITION_COUNT, MapWithTtl, Metric, MetricsContext, MlDsa87, Multimap, ObservableEventEmitter, RateMetric, SigningUtil, StreamIDUtils, StreamPartIDUtils, TheGraphClient, TimeoutError, addManagedEventListener, areEqualBinaries, asAbortable, binaryToHex, binaryToUtf8, collect, composeAbortSignals, computeMd5, computeSha1, createCipheriv, createDecipheriv, ensureValidStreamPartitionCount, ensureValidStreamPartitionIndex, executeSafePromise, filePathToNodeFormat, getSubtle, hexToBinary, initEventGateway, ipv4ToNumber, isENSName, isEthereumAddressUserId, isValidUserId, keyToArrayIndex, merge, multiplyWeiAmount, numberToIpv4, pTransaction, privateDecrypt, publicEncrypt, raceForEvent, randomString, retry, scheduleAtApproximateInterval, scheduleAtFixedRate, scheduleAtInterval, setAbortableInterval, setAbortableTimeout, toENSName, toEthereumAddress, toEthereumAddressOrENSName, toLengthPrefixedFrame, toStreamID, toStreamPartID, toUserId, toUserIdRaw, until, utf8ToBinary, wait, waitForEvent, withRateLimit, withTimeout };
//# sourceMappingURL=exports-browser.js.map
